import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, ChevronDown, ChevronUp, Copy, Download, ExternalLink, Eye, FileText, Filter, LogOut, Mail, MapPin, MessageCircle, Pencil, Phone, Play, Plus, RefreshCw, Save, Send, Settings, Sparkles, Star, TableProperties, Trash2, Upload, User, X } from 'lucide-react';
import SheetAnalyticsResult from './SheetAnalyticsResult';
import { ref } from 'firebase/storage';
import toast from 'react-hot-toast';
import sheetLogo from '../../assets/images/sheetlogo1.png';
import { storage } from '../../config/firebase';
import { SheetCalendarEventChip, SheetCalendarPresetCard, SheetKanbanPresetCard, SheetListCardPreset, SheetProfilePresetCard, SheetSidebarActionFormCard } from './SheetPresetRenderers';
import LoginModal from '../Auth/LoginModal';
import Profile from '../Profile/Profile';
import { useAuth } from '../../contexts/AuthContext';
import { sheetFormService } from '../../services/sheetFormService';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import {
  evaluateHeaderConditionalState,
  sanitizeHeaderConditionalLogic
} from '../../utils/sheetConditionalLogic';
import { getColumnLetter, hasHeaderFormula, parseFormulaToConfig, resolveRowFormulas } from '../../utils/sheetFormula';
import { buildSheetCsvTemplate, getSheetCsvColumns, parseSheetCsvImport } from '../../utils/sheetCsv';
import { getSheetPath, getWorkspacePath, isLikelyWorkspaceCustomHost } from '../../utils/sheetDomains';
import { normalizeSheetOptionSource, resolveHeaderOptions, sanitizeHeaderConditionalOptions } from '../../utils/sheetOptions';
import { applySheetPageMetadata, buildSheetShareMetadata } from '../../utils/sheetShare';
import { scheduleSheetAnalyticsCalculation, setBoundedSheetAnalyticsCacheEntry } from '../../utils/sheetAnalyticsRuntime';
import { uploadFileAndGetUrl } from '../../utils/storageUpload';
import {
  DEFAULT_WORKSPACE_PRIMARY_COLOR,
  normalizeWorkspacePrimaryColor,
  workspacePrimaryColorWithAlpha
} from '../../utils/sheetWorkspaceTheme';

const DEFAULT_COLUMN_WIDTH = 180;
const META_COLUMN_WIDTH = 88;
const RESIZE_HANDLE_WIDTH = 10;
const SELECT_COLUMN_WIDTH = 56;
const thematicBreakPattern = /^([-*_])(?:\s*\1){2,}\s*$/;
const ACTION_CONFIG_MODE_VALUES = new Set(['linked_form', 'update_rows']);
const ACTION_UPDATE_TARGET_CURRENT_SHEET = '__current_sheet__';
const ACTION_UPDATE_CONDITION_OPERATOR_VALUES = new Set(['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty']);
const ACTION_UPDATE_VALUE_MODE_VALUES = new Set(['static', 'sourceHeader']);
const LOG_SYSTEM_FIELDS = new Set([
  'logScope',
  'sourceSheetId',
  'sourceSheetTitle',
  'sourceRowId',
  'sourceColumnId',
  'sourceRowLabel',
  'sourceColumnLabel',
  'loggedAt',
  'loggedBy',
  'loggedByEmail'
]);
const ACTIVE_ACTIONS_HEADER_ID = 'system_active_actions';
const ACTION_FORM_NOTIFICATION_COPY = {
  booking: { single: 'Booked', plural: 'Bookings' },
  checkout: { single: 'Checked Out', plural: 'Checkouts' },
  inquiry: { single: 'Has Enquiry', plural: 'Enquiries' },
  custom: { single: 'Has Action', plural: 'Actions' }
};
const SHEET_RENDER_PRESET_VALUES = new Set(['default', 'profiles', 'list-card', 'kanban', 'calendar']);
const ACTION_FORM_TYPES = new Set(['booking', 'checkout', 'inquiry', 'custom']);

const normalizeSheetRenderPreset = (value) => (
  SHEET_RENDER_PRESET_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'default'
);
const normalizeActionFormType = (value) => (
  ACTION_FORM_TYPES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'custom'
);
const normalizeActionConfigMode = (value) => (
  ACTION_CONFIG_MODE_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'linked_form'
);
const ACTION_LINKED_FORM_BEHAVIOR_VALUES = new Set(['open_form', 'instant_save']);
const normalizeActionLinkedFormBehavior = (value) => (
  ACTION_LINKED_FORM_BEHAVIOR_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'open_form'
);
const ACTION_INSTANT_SAVE_PROMPT_FIELD_TYPES = new Set(['select', 'buttons', 'yesno', 'rog', 'multiselect', 'list']);
const SHEET_RENDER_FONT_FAMILIES = {
  Inter: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  Roboto: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  'Open Sans': '"Open Sans", "Helvetica Neue", Arial, sans-serif',
  Montserrat: '"Montserrat", "Avenir Next", "Segoe UI", sans-serif',
  Poppins: '"Poppins", "Avenir Next", "Segoe UI", sans-serif',
  Lato: '"Lato", "Helvetica Neue", Arial, sans-serif',
  Nunito: '"Nunito", "Trebuchet MS", "Segoe UI", sans-serif',
  'Source Sans Pro': '"Source Sans Pro", "Helvetica Neue", Arial, sans-serif',
  Raleway: '"Raleway", "Helvetica Neue", Arial, sans-serif',
  Ubuntu: '"Ubuntu", "Segoe UI", sans-serif',
  'Playfair Display': '"Playfair Display", Georgia, "Times New Roman", serif',
  Merriweather: '"Merriweather", Georgia, serif',
  Georgia: 'Georgia, "Times New Roman", serif',
  'Times New Roman': '"Times New Roman", Times, serif',
  'Crimson Text': '"Crimson Text", Georgia, serif'
};
const getSheetRenderFontFamily = (value) => SHEET_RENDER_FONT_FAMILIES[`${value || ''}`.trim()] || SHEET_RENDER_FONT_FAMILIES.Inter;
const getSheetFormPath = (sheetId) => `/sheet-form/${sheetId}`;
const getSheetFormUrl = (sheetId) => `${window.location.origin}${getSheetFormPath(sheetId)}`;
const getActionSheetFormUrl = (sheetId, context = {}) => {
  const url = new URL(getSheetFormUrl(sheetId));
  const actionId = `${context?.actionId || ''}`.trim();
  const sourceSheetId = `${context?.sourceSheetId || ''}`.trim();
  if (actionId) url.searchParams.set('actionId', actionId);
  if (sourceSheetId) url.searchParams.set('sourceSheetId', sourceSheetId);
  return url.toString();
};
const normalizeActionSheetFormFieldWidth = (value) => {
  const normalized = `${value || ''}`.trim();
  return ['full', 'two-thirds', 'half', 'third'].includes(normalized) ? normalized : 'full';
};
const normalizeActionSheetFormSectionDividerLabel = (item = {}) => {
  const label = item.label ?? item.content ?? 'Section';
  const normalizedLabel = `${label}`;
  return normalizedLabel.trim() ? normalizedLabel : 'Section';
};
const getActionSheetFormFieldWidthClass = (width) => {
  switch (normalizeActionSheetFormFieldWidth(width)) {
    case 'two-thirds':
      return 'md:col-span-4';
    case 'half':
      return 'md:col-span-3';
    case 'third':
      return 'md:col-span-2';
    default:
      return 'md:col-span-6';
  }
};
const normalizeActionSheetFormSubmissionMode = (value) => (
  `${value || ''}`.trim() === 'modular' ? 'modular' : 'singleRow'
);
const normalizeActionSheetFormTextAlignment = (value) => (
  ['left', 'center', 'right'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'left'
);
const normalizeActionSheetFormSlideMode = (value) => (
  ['manual', 'auto'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'manual'
);
const normalizeActionSheetFormSlideIntervalSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 5;
  return Math.max(2, Math.min(30, Math.round(numeric)));
};
const sanitizeActionFormRichTextHtml = (html) => {
  const source = `${html || ''}`.trim();
  if (!source) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return source;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;
  const allowedTags = new Set(['p', 'div', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a']);

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return doc.createTextNode(node.textContent || '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return doc.createDocumentFragment();
    }

    const tag = node.tagName.toLowerCase();
    const fragment = doc.createDocumentFragment();
    const children = Array.from(node.childNodes).map((child) => sanitizeNode(child));

    if (!allowedTags.has(tag)) {
      children.forEach((child) => fragment.appendChild(child));
      return fragment;
    }

    const element = doc.createElement(tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag);
    if (tag === 'a') {
      const href = `${node.getAttribute('href') || ''}`.trim();
      if (/^https?:\/\//i.test(href)) {
        element.setAttribute('href', href);
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noreferrer');
      }
    }
    if (['p', 'div'].includes(tag)) {
      const textAlign = `${node.style?.textAlign || node.getAttribute('align') || ''}`.trim().toLowerCase();
      if (['left', 'center', 'right'].includes(textAlign)) {
        element.style.textAlign = textAlign;
      }
    }
    children.forEach((child) => element.appendChild(child));
    return element;
  };

  const output = doc.createElement('div');
  Array.from(root.childNodes).forEach((child) => output.appendChild(sanitizeNode(child)));
  return output.innerHTML.trim();
};
const normalizeActionSheetFormSlides = (slides = []) => (
  (Array.isArray(slides) ? slides : [])
    .map((slide, index) => {
      const url = `${slide?.url || slide?.src || ''}`.trim();
      if (!url) return null;
      return {
        id: `${slide?.id || `slide_${index + 1}`}`.trim() || `slide_${index + 1}`,
        url,
        alt: `${slide?.alt || slide?.caption || ''}`.trim()
      };
    })
    .filter(Boolean)
);
const buildActionSheetFormLayoutItems = (form) => {
  const fields = Array.isArray(form?.fields) ? form.fields : [];
  const fieldMap = new Map(fields.map((field) => [`${field?.id || ''}`.trim(), field]));
  const rawLayout = Array.isArray(form?.formLayout) && form.formLayout.length
    ? form.formLayout
    : fields.map((field) => ({
        id: `field_${field.id}`,
        type: 'field',
        headerId: field.id,
        width: field.formWidth || 'full'
      }));

  const items = [];
  const seenFieldIds = new Set();

  rawLayout.forEach((item, index) => {
    const type = `${item?.type || ''}`.trim();

    if (type === 'field') {
      const headerId = `${item?.headerId || item?.fieldId || ''}`.trim();
      const field = fieldMap.get(headerId);
      if (!field || seenFieldIds.has(headerId)) return;
      seenFieldIds.add(headerId);
      items.push({
        id: `${item?.id || `field_${headerId}`}`.trim() || `field_${headerId}`,
        type: 'field',
        width: normalizeActionSheetFormFieldWidth(item?.width || field.formWidth),
        field
      });
      return;
    }

    if (type === 'sectionDivider') {
      items.push({
        id: `${item?.id || `section_${index + 1}`}`.trim() || `section_${index + 1}`,
        type: 'sectionDivider',
        label: normalizeActionSheetFormSectionDividerLabel(item)
      });
      return;
    }

    if (type === 'textBlock') {
      items.push({
        id: `${item?.id || `text_block_${index + 1}`}`.trim() || `text_block_${index + 1}`,
        type: 'textBlock',
        contentHtml: `${item?.contentHtml || item?.content || ''}`,
        alignment: normalizeActionSheetFormTextAlignment(item?.alignment)
      });
      return;
    }

    if (type === 'imageSlide') {
      items.push({
        id: `${item?.id || `image_slide_${index + 1}`}`.trim() || `image_slide_${index + 1}`,
        type: 'imageSlide',
        slides: normalizeActionSheetFormSlides(item?.slides),
        slideMode: normalizeActionSheetFormSlideMode(item?.slideMode),
        autoAdvanceSeconds: normalizeActionSheetFormSlideIntervalSeconds(item?.autoAdvanceSeconds)
      });
    }
  });

  if (!items.some((item) => item.type === 'field')) {
    return fields.map((field) => ({
      id: `field_${field.id}`,
      type: 'field',
      width: normalizeActionSheetFormFieldWidth(field.formWidth),
      field
    }));
  }

  return items;
};

const ActionFormRichTextBlock = ({ item }) => {
  const html = useMemo(() => sanitizeActionFormRichTextHtml(item?.contentHtml || item?.content || ''), [item]);
  if (!html) return null;
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[normalizeActionSheetFormTextAlignment(item?.alignment)];

  return (
    <div
      className={`prose prose-slate max-w-none text-sm leading-6 text-slate-700 [&_a]:text-[#008080] [&_a]:underline [&_ol]:pl-5 [&_p]:mb-3 [&_ul]:pl-5 ${alignClass}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const ActionFormImageSlide = ({ item }) => {
  const slides = useMemo(() => normalizeActionSheetFormSlides(item?.slides), [item]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const slideMode = normalizeActionSheetFormSlideMode(item?.slideMode);
  const autoAdvanceSeconds = normalizeActionSheetFormSlideIntervalSeconds(item?.autoAdvanceSeconds);

  useEffect(() => {
    setActiveSlideIndex(0);
  }, [item?.id, slides.length]);

  useEffect(() => {
    if (slideMode !== 'auto' || slides.length <= 1) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveSlideIndex((current) => (current + 1) % slides.length);
    }, autoAdvanceSeconds * 1000);
    return () => window.clearInterval(intervalId);
  }, [autoAdvanceSeconds, slideMode, slides.length]);

  if (!slides.length) return null;
  const activeSlide = slides[Math.min(activeSlideIndex, slides.length - 1)] || slides[0];

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-slate-50 shadow-sm">
      <div className="relative">
        <img
          src={activeSlide.url}
          alt={activeSlide.alt || 'Slide'}
          className="max-h-64 w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent px-4 pb-4 pt-12">
          {activeSlide.alt ? (
            <p className="text-sm font-medium text-white">{activeSlide.alt}</p>
          ) : null}
        </div>
      </div>
      {slides.length > 1 && (
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            {slides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActiveSlideIndex(slideIndex)}
                className={`h-2.5 rounded-full transition ${slideIndex === activeSlideIndex ? 'w-8 bg-[#008080]' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
                aria-label={`Show slide ${slideIndex + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const normalizeActionFormFieldMappings = (fieldMappings = []) => (
  (Array.isArray(fieldMappings) ? fieldMappings : [])
    .map((fieldMapping, index) => ({
      id: `${fieldMapping?.id || `action_form_mapping_${index + 1}`}`.trim() || `action_form_mapping_${index + 1}`,
      valueMode: ACTION_UPDATE_VALUE_MODE_VALUES.has(`${fieldMapping?.valueMode || ''}`.trim())
        ? `${fieldMapping?.valueMode}`.trim()
        : (`${fieldMapping?.sourceHeaderId || ''}`.trim() ? 'sourceHeader' : 'static'),
      value: `${fieldMapping?.value ?? ''}`,
      sourceHeaderId: `${fieldMapping?.sourceHeaderId || ''}`.trim(),
      targetHeaderId: `${fieldMapping?.targetHeaderId || ''}`.trim()
    }))
    .filter((fieldMapping) => (
      fieldMapping.targetHeaderId
      && (
        fieldMapping.valueMode === 'static'
          ? true
          : fieldMapping.sourceHeaderId
      )
    ))
);
const normalizeActionUpdateConditions = (conditions = []) => (
  (Array.isArray(conditions) ? conditions : [])
    .map((condition, index) => ({
      id: `${condition?.id || `action_update_condition_${index + 1}`}`.trim() || `action_update_condition_${index + 1}`,
      targetHeaderId: `${condition?.targetHeaderId || ''}`.trim(),
      operator: ACTION_UPDATE_CONDITION_OPERATOR_VALUES.has(`${condition?.operator || ''}`.trim()) ? `${condition?.operator}`.trim() : 'equals',
      valueMode: ACTION_UPDATE_VALUE_MODE_VALUES.has(`${condition?.valueMode || ''}`.trim()) ? `${condition?.valueMode}`.trim() : 'static',
      value: `${condition?.value ?? ''}`,
      sourceHeaderId: `${condition?.sourceHeaderId || ''}`.trim()
    }))
    .filter((condition) => condition.targetHeaderId)
);
const normalizeActionUpdateValues = (updates = []) => (
  (Array.isArray(updates) ? updates : [])
    .map((update, index) => ({
      id: `${update?.id || `action_update_value_${index + 1}`}`.trim() || `action_update_value_${index + 1}`,
      targetHeaderId: `${update?.targetHeaderId || ''}`.trim(),
      valueMode: ACTION_UPDATE_VALUE_MODE_VALUES.has(`${update?.valueMode || ''}`.trim()) ? `${update?.valueMode}`.trim() : 'static',
      value: `${update?.value ?? ''}`,
      sourceHeaderId: `${update?.sourceHeaderId || ''}`.trim()
    }))
    .filter((update) => update.targetHeaderId)
);
const normalizeSheetActionForms = (actionForms = []) => (
  (Array.isArray(actionForms) ? actionForms : [])
    .map((actionForm, index) => {
      const id = `${actionForm?.id || `action_form_${index + 1}`}`.trim();
      const targetSheetId = `${actionForm?.targetSheetId || ''}`.trim();
      if (!id || !targetSheetId) return null;

      return {
        id,
        mode: normalizeActionConfigMode(actionForm?.mode),
        type: normalizeActionFormType(actionForm?.type),
        name: `${actionForm?.name || actionForm?.label || ''}`.trim(),
        description: `${actionForm?.description || ''}`.trim(),
        buttonLabel: `${actionForm?.buttonLabel || ''}`.trim(),
        submitLabel: `${actionForm?.submitLabel || ''}`.trim(),
        linkedFormBehavior: normalizeActionLinkedFormBehavior(actionForm?.linkedFormBehavior),
        allowTargetRowSelection: normalizeActionLinkedFormBehavior(actionForm?.linkedFormBehavior) === 'open_form' && actionForm?.allowTargetRowSelection === true,
        targetSheetId,
        fieldMappings: normalizeActionFormFieldMappings(actionForm?.fieldMappings),
        conditions: normalizeActionUpdateConditions(actionForm?.conditions),
        updates: normalizeActionUpdateValues(actionForm?.updates)
      };
    })
    .filter(Boolean)
);
const getSheetRenderPresetConfig = (config) => ({
  profileHeaderId: `${config?.profileHeaderId || ''}`.trim(),
  imageHeaderId: `${config?.imageHeaderId || ''}`.trim(),
  categoryHeaderId: `${config?.categoryHeaderId || ''}`.trim(),
  titleHeaderId: `${config?.titleHeaderId || ''}`.trim(),
  bodyHeaderId: `${config?.bodyHeaderId || ''}`.trim(),
  actionHeaderId: `${config?.actionHeaderId || ''}`.trim(),
  tagHeaderId: `${config?.tagHeaderId || ''}`.trim(),
  imageLayout: `${config?.imageLayout || ''}`.trim() === 'full-width' ? 'full-width' : 'inline',
  statusHeaderId: `${config?.statusHeaderId || ''}`.trim(),
  metaHeaderId: `${config?.metaHeaderId || ''}`.trim(),
  dateHeaderId: `${config?.dateHeaderId || ''}`.trim(),
  uiColumns: Math.max(1, Math.min(4, Math.round(Number(config?.uiColumns) || 3)))
});

const parseRowDateValue = (value) => {
  if (!value) return null;
  const candidate = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

const addDays = (date, count) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1);
const getDateKey = (date) => `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
const getWeekStart = (date) => addDays(date, -date.getDay());
const getMonthGridStart = (date) => {
  const monthStart = getMonthStart(date);
  return new Date(monthStart.getFullYear(), monthStart.getMonth(), monthStart.getDate() - monthStart.getDay());
};

const REPORT_SCOPE_VALUES = new Set(['active-row', 'sheet-overview']);
const REPORT_TONE_VALUES = new Set(['professional', 'executive', 'analytical', 'operational']);
const REPORT_FORMAT_VALUES = new Set(['markdown', 'summary']);
const REPORT_TARGET_TYPES = new Set(['active-row', 'selected-rows', 'column', 'sheet']);

const normalizeReportSkillForViewer = (sheet = {}, headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
  const reportSkill = sheet?.aiSkills?.report || {};
  return {
    enabled: Boolean(reportSkill?.enabled),
    title: `${reportSkill?.title || ''}`.trim(),
    objective: `${reportSkill?.objective || ''}`.trim(),
    scope: REPORT_SCOPE_VALUES.has(`${reportSkill?.scope || ''}`.trim()) ? `${reportSkill.scope}`.trim() : 'active-row',
    tone: REPORT_TONE_VALUES.has(`${reportSkill?.tone || ''}`.trim()) ? `${reportSkill.tone}`.trim() : 'professional',
    format: REPORT_FORMAT_VALUES.has(`${reportSkill?.format || ''}`.trim()) ? `${reportSkill.format}`.trim() : 'markdown',
    focusHeaderIds: Array.from(new Set(
      (Array.isArray(reportSkill?.focusHeaderIds) ? reportSkill.focusHeaderIds : [])
        .map((headerId) => `${headerId || ''}`.trim())
        .filter((headerId) => validHeaderIds.has(headerId))
    )),
    includeVisibleRows: reportSkill?.includeVisibleRows !== false,
    includeRelatedLogs: reportSkill?.includeRelatedLogs !== false,
    includeSheetLogs: Boolean(reportSkill?.includeSheetLogs),
    maxVisibleRows: Math.max(1, Math.min(50, Number(reportSkill?.maxVisibleRows) || 15)),
    maxLogs: Math.max(1, Math.min(50, Number(reportSkill?.maxLogs) || 12)),
    instructions: `${reportSkill?.instructions || ''}`.trim()
  };
};

const normalizeSheetAutomationsForViewer = (sheet = {}, headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
  return (Array.isArray(sheet?.aiAutomations) ? sheet.aiAutomations : [])
    .filter((automation) => automation && typeof automation === 'object')
    .map((automation, index) => ({
      id: `${automation?.id || `automation_${index + 1}`}`.trim(),
      enabled: automation?.enabled !== false,
      name: `${automation?.name || ''}`.trim(),
      type: ['report', 'email_digest'].includes(`${automation?.type || ''}`.trim()) ? `${automation.type}`.trim() : 'report',
      triggerType: ['manual', 'schedule'].includes(`${automation?.triggerType || ''}`.trim()) ? `${automation.triggerType}`.trim() : 'manual',
      scheduleCadence: ['daily', 'weekly'].includes(`${automation?.scheduleCadence || ''}`.trim()) ? `${automation.scheduleCadence}`.trim() : 'daily',
      scheduleDay: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].includes(`${automation?.scheduleDay || ''}`.trim()) ? `${automation.scheduleDay}`.trim() : 'fri',
      scheduleTime: `${automation?.scheduleTime || ''}`.trim() || '07:00',
      timezone: `${automation?.timezone || ''}`.trim() || 'Africa/Johannesburg',
      scope: REPORT_SCOPE_VALUES.has(`${automation?.scope || ''}`.trim()) ? `${automation.scope}`.trim() : 'active-row',
      tone: REPORT_TONE_VALUES.has(`${automation?.tone || ''}`.trim()) ? `${automation.tone}`.trim() : 'professional',
      format: REPORT_FORMAT_VALUES.has(`${automation?.format || ''}`.trim()) ? `${automation.format}`.trim() : 'markdown',
      focusHeaderIds: Array.from(new Set(
        (Array.isArray(automation?.focusHeaderIds) ? automation.focusHeaderIds : [])
          .map((headerId) => `${headerId || ''}`.trim())
          .filter((headerId) => validHeaderIds.has(headerId))
      )),
      includeVisibleRows: automation?.includeVisibleRows !== false,
      includeRelatedLogs: automation?.includeRelatedLogs !== false,
      includeSheetLogs: Boolean(automation?.includeSheetLogs),
      maxVisibleRows: Math.max(1, Math.min(50, Number(automation?.maxVisibleRows) || 15)),
      maxLogs: Math.max(1, Math.min(50, Number(automation?.maxLogs) || 12)),
      instructions: `${automation?.instructions || ''}`.trim(),
      recipients: Array.isArray(automation?.recipients) ? automation.recipients : []
    }))
    .filter((automation) => automation.id && automation.name);
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const sanitizeFilterHeaderIds = (filterHeaderIds = [], headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => header.id));
  return Array.from(new Set(
    (Array.isArray(filterHeaderIds) ? filterHeaderIds : [])
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && validHeaderIds.has(headerId))
  ));
};

const escapeHtml = (value) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInlineMarkdown = (text) => {
  let html = escapeHtml(text);
  html = html.replace(/`([^`]+)`/g, '<code class="rounded bg-slate-200/80 px-1 py-0.5 text-[0.92em]">$1</code>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="underline">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  return html;
};

const markdownToHtml = (source) => {
  const lines = `${source ?? ''}`.replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;

  const closeLists = () => {
    if (inUl) {
      html.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      html.push('</ol>');
      inOl = false;
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();

    if (line.startsWith('```')) {
      closeLists();
      if (!inCode) {
        inCode = true;
        html.push('<pre class="mb-3 overflow-x-auto rounded-xl bg-slate-950/95 p-3 text-slate-100"><code>');
      } else {
        inCode = false;
        html.push('</code></pre>');
      }
      return;
    }

    if (inCode) {
      html.push(`${escapeHtml(rawLine)}\n`);
      return;
    }

    if (!line.trim()) {
      closeLists();
      return;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      closeLists();
      const level = heading[1].length;
      const sizeClass = level === 1 ? 'text-lg' : level === 2 ? 'text-base' : 'text-sm';
      html.push(`<h${level} class="mb-2 font-bold ${sizeClass}">${renderInlineMarkdown(heading[2])}</h${level}>`);
      return;
    }

    const quote = line.match(/^>\s+(.+)$/);
    if (quote) {
      closeLists();
      html.push(`<blockquote class="mb-3 border-l-4 border-slate-300 pl-3 italic text-slate-600">${renderInlineMarkdown(quote[1])}</blockquote>`);
      return;
    }

    if (thematicBreakPattern.test(line.trim())) {
      closeLists();
      html.push('<hr class="my-3 border-0 border-t border-slate-300" />');
      return;
    }

    const ordered = line.match(/^(\d+)\.\s+(.+)$/);
    if (ordered) {
      if (!inOl) {
        if (inUl) {
          html.push('</ul>');
          inUl = false;
        }
        html.push('<ol class="mb-3 list-decimal space-y-1 pl-5">');
        inOl = true;
      }
      html.push(`<li>${renderInlineMarkdown(ordered[2])}</li>`);
      return;
    }

    const unordered = line.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      if (!inUl) {
        if (inOl) {
          html.push('</ol>');
          inOl = false;
        }
        html.push('<ul class="mb-3 list-disc space-y-1 pl-5">');
        inUl = true;
      }
      html.push(`<li>${renderInlineMarkdown(unordered[1])}</li>`);
      return;
    }

    closeLists();
    html.push(`<p class="mb-3 leading-6">${renderInlineMarkdown(line)}</p>`);
  });

  closeLists();
  if (inCode) {
    html.push('</code></pre>');
  }
  return html.join('');
};

const formatValueForFilter = (header, value) => {
  if (header?.type === 'checkbox') return value ? 'true' : 'false';
  if (header?.type === 'multiselect' || header?.type === 'list') return Array.isArray(value) ? value.map((item) => `${item || ''}`.trim()).filter(Boolean).join(' ') : '';
  return `${value ?? ''}`.trim();
};

const YES_NO_OPTIONS = ['Yes', 'No'];
const ROG_OPTIONS = ['Red', 'Orange', 'Green'];
const FIXED_HEADER_OPTIONS = {
  yesno: YES_NO_OPTIONS,
  rog: ROG_OPTIONS
};

const normalizeListItems = (rawValue) => (
  (Array.isArray(rawValue) ? rawValue : `${rawValue || ''}`.split(','))
    .map((item) => `${item || ''}`.trim())
    .filter(Boolean)
);

const getFixedOptionsForHeaderType = (headerType) => FIXED_HEADER_OPTIONS[`${headerType || ''}`.trim()] || [];

const getResolvedChoiceOptions = (header, optionSourceRowsBySheetId = {}, rowValues = {}, headers = []) => {
  const fixedOptions = getFixedOptionsForHeaderType(header?.type);
  if (fixedOptions.length) return fixedOptions;
  return resolveHeaderOptions(header, optionSourceRowsBySheetId, rowValues, headers);
};

const getRogTone = (value) => {
  switch (`${value || ''}`.trim().toLowerCase()) {
    case 'red':
      return {
        solid: 'border-rose-500 bg-rose-500 text-white',
        soft: 'border-rose-200 bg-rose-50 text-rose-700',
        overlay: 'bg-rose-50/80'
      };
    case 'orange':
      return {
        solid: 'border-orange-500 bg-orange-500 text-white',
        soft: 'border-orange-200 bg-orange-50 text-orange-700',
        overlay: 'bg-orange-50/80'
      };
    case 'green':
      return {
        solid: 'border-emerald-500 bg-emerald-500 text-white',
        soft: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        overlay: 'bg-emerald-50/80'
      };
    default:
      return null;
  }
};

const getChoiceButtonClasses = ({ headerType, option, selectedValue, disabled = false, readOnlyGhost = false }) => {
  const isSelected = `${selectedValue ?? ''}` === option;
  if (`${headerType || ''}` === 'rog') {
    const tone = getRogTone(option);
    return `rounded-xl border px-3 py-1 text-xs font-semibold transition ${
      disabled
        ? (readOnlyGhost ? 'cursor-not-allowed border-transparent bg-transparent text-slate-500' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400')
        : isSelected
          ? tone?.solid || 'border-[#008080] bg-[#008080]/10 text-[#008080]'
          : tone?.soft || 'border-slate-200 bg-slate-100 text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
    }`;
  }
  return `rounded-xl border px-3 py-1 text-xs font-semibold transition ${
    disabled
      ? (readOnlyGhost ? 'cursor-not-allowed border-transparent bg-transparent text-slate-500' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400')
      : isSelected
        ? 'border-[#008080] bg-[#008080]/10 text-[#008080]'
        : 'border-slate-200 bg-slate-100 text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
  }`;
};

const getRogOverlayClass = (header, value) => (
  header?.type === 'rog' && header?.rogOverlay ? (getRogTone(value)?.overlay || '') : ''
);

const getMapNavigationUrl = (value) => {
  const query = `${value || ''}`.trim();
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

const getFilterOptionsForHeader = (header, rows = [], optionSourceRowsBySheetId = {}, headers = []) => {
  if (!header) return [];

  if (header.type === 'checkbox') {
    return [
      { value: 'true', label: 'Checked' },
      { value: 'false', label: 'Unchecked' }
    ];
  }

  const rowValues = rows.flatMap((row) => {
    const rawValue = row?.resolvedValues?.[header.id] ?? row?.values?.[header.id];
    if (header.type === 'multiselect' || header.type === 'list') {
      return normalizeListItems(rawValue);
    }
    const formattedValue = formatValueForFilter(header, rawValue);
    return formattedValue ? [formattedValue] : [];
  });

  const supportsConfiguredOptions = ['select', 'buttons', 'yesno', 'rog', 'multiselect'].includes(header.type);
  const configuredOptions = supportsConfiguredOptions
    ? rows.flatMap((row) => getResolvedChoiceOptions(
        header,
        optionSourceRowsBySheetId,
        row?.resolvedValues || row?.values || {},
        headers
      ))
    : [];

  return Array.from(new Set(
    [
      ...configuredOptions,
      ...rowValues
    ]
      .map((item) => `${item ?? ''}`.trim())
      .filter(Boolean)
  )).map((item) => {
    const compactLabel = item.replace(/\s+/g, ' ');
    return {
      value: item,
      label: compactLabel.length > 90 ? `${compactLabel.slice(0, 87)}...` : compactLabel
    };
  });
};

const doesRowMatchFilter = (row, header, filterValue) => {
  const normalizedFilter = `${filterValue ?? ''}`.trim().toLowerCase();
  if (!normalizedFilter || !header) return true;

  const rawValue = row?.resolvedValues?.[header.id] ?? row?.values?.[header.id];

  if (header.type === 'checkbox') {
    return normalizedFilter === 'true' ? Boolean(rawValue) : !Boolean(rawValue);
  }

  if (header.type === 'multiselect' || header.type === 'list') {
    return normalizeListItems(rawValue).some((item) => `${item ?? ''}`.trim().toLowerCase() === normalizedFilter);
  }

  return formatValueForFilter(header, rawValue).toLowerCase() === normalizedFilter;
};

const normalizeRows = (rows = []) => rows.map((row) => ({
  id: row.id,
  values: { ...(row.values || {}) },
  createdBy: row.createdBy || null,
  createdByEmail: row.createdByEmail || null,
  contributorId: row.contributorId || row.createdBy || null,
  storageMode: row.storageMode || 'sheet-items',
  createdAt: row.createdAt || null,
  updatedAt: row.updatedAt || null,
  isDirty: false,
  isNew: false
}));

const formatDefaultDateTokenValue = (headerType, date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  switch (headerType) {
    case 'date':
      return `${year}-${month}-${day}`;
    case 'time':
      return `${hours}:${minutes}`;
    case 'datetime':
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    default:
      return '';
  }
};

const getDynamicDefaultTokenForHeader = (headerType, rawValue) => {
  const normalizedValue = `${rawValue ?? ''}`.trim().toUpperCase();
  if (headerType === 'date' && normalizedValue === 'TODAY()') return 'TODAY()';
  if (headerType === 'time' && normalizedValue === 'TIMENOW()') return 'TIMENOW()';
  if (headerType === 'datetime' && normalizedValue === 'NOW()') return 'NOW()';
  return '';
};

const getHeaderInitialValue = (header) => {
  if (!header || header.defaultValueMode === 'none') {
    return header?.type === 'multiselect' || header?.type === 'list' ? [] : header?.type === 'checkbox' ? false : '';
  }
  if (header.type === 'checkbox') return Boolean(header.defaultValue);
  if (header.type === 'multiselect' || header.type === 'list') return Array.isArray(header.defaultValue) ? header.defaultValue : [];
  const dynamicToken = getDynamicDefaultTokenForHeader(header.type, header.defaultValue);
  if (dynamicToken) return formatDefaultDateTokenValue(header.type);
  return header.defaultValue ?? '';
};

const createDraftRow = (headers = []) => ({
  id: `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  values: headers.reduce((acc, header) => {
    acc[header.id] = getHeaderInitialValue(header);
    return acc;
  }, {}),
  createdAt: null,
  updatedAt: null,
  isDirty: true,
  isNew: true
});

const shouldAutoCreateDraftRow = (sheetData, user, existingRows = []) => {
  if (!user?.uid || !sheetData) return false;

  const isOwner = sheetData.userId === user.uid;
  const isOwnRowsMode = isLegacyOwnRowsMode(sheetData);
  const canCreateSharedRows = isViewAllEditOwnMode(sheetData) || isSharedSheetEditMode(sheetData);
  const hasSavedRows = Array.isArray(existingRows)
    && existingRows.some((row) => row && !row.isNew);

  if (hasSavedRows) return false;
  if (isOwnRowsMode && !isOwner) return false;
  return isOwner || isOwnRowsMode || canCreateSharedRows;
};

const createDraftRows = (sheetData, user, headers = [], existingRows = []) => (
  shouldAutoCreateDraftRow(sheetData, user, existingRows) ? [createDraftRow(headers)] : []
);

const getProfileDisplayData = (header, rowValues = {}) => {
  const imageHeaderId = `${header?.profileConfig?.imageHeaderId || ''}`.trim();
  const labelHeaderId = `${header?.profileConfig?.labelHeaderId || ''}`.trim();
  return {
    imageUrl: `${rowValues?.[imageHeaderId] ?? ''}`.trim(),
    label: `${rowValues?.[labelHeaderId] ?? ''}`.trim()
  };
};

const mergeRealtimeRows = (incomingRows = [], previousRows = [], sheetData, user, headers = []) => {
  const normalizedIncomingRows = normalizeRows(incomingRows);
  const dirtySavedRows = new Map(
    previousRows
      .filter((row) => row.isDirty && !row.isNew)
      .map((row) => [row.id, row])
  );
  const localDraftRows = previousRows.filter((row) => row.isNew);

  const mergedSavedRows = normalizedIncomingRows.map((row) => {
    const localDirtyRow = dirtySavedRows.get(row.id);
    return localDirtyRow
      ? {
          ...row,
          ...localDirtyRow,
          createdAt: row.createdAt,
          updatedAt: row.updatedAt,
          createdBy: row.createdBy,
          createdByEmail: row.createdByEmail,
          contributorId: row.contributorId,
          storageMode: row.storageMode
        }
      : row;
  });

  const orphanedDirtyRows = previousRows.filter(
    (row) => row.isDirty && !row.isNew && !normalizedIncomingRows.some((incomingRow) => incomingRow.id === row.id)
  );

  const nextRows = [...mergedSavedRows, ...orphanedDirtyRows, ...localDraftRows];
  return nextRows.some((row) => row.isNew)
    ? nextRows
    : [...createDraftRows(sheetData, user, headers, normalizedIncomingRows), ...nextRows];
};

const renderRatingStars = (value, onChange, disabled = false) => {
  const currentValue = Number(value) || 0;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((ratingValue) => (
        <button
          key={ratingValue}
          type="button"
          onClick={() => !disabled && onChange(ratingValue)}
          className={`rounded p-0.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <Star
            className={`h-4 w-4 ${ratingValue <= currentValue ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
          />
        </button>
      ))}
    </div>
  );
};

const getInputTypeForHeader = (headerType) => {
  switch (headerType) {
    case 'email':
      return 'email';
    case 'telephone':
      return 'tel';
    case 'address':
      return 'text';
    case 'hyperlink':
    case 'youtube':
    case 'document':
    case 'image':
      return 'url';
    case 'date':
      return 'date';
    case 'time':
      return 'time';
    case 'datetime':
      return 'datetime-local';
    case 'number':
    case 'currency':
    case 'percent':
      return 'number';
    default:
      return 'text';
  }
};

const getInputModeForHeader = (headerType) => {
  switch (headerType) {
    case 'number':
    case 'currency':
    case 'percent':
      return 'decimal';
    case 'telephone':
      return 'tel';
    case 'email':
      return 'email';
    case 'address':
      return 'text';
    case 'hyperlink':
    case 'youtube':
    case 'document':
      return 'url';
    default:
      return undefined;
  }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(`${value || ''}`.trim());
const normalizePhoneValue = (value) => `${value || ''}`.replace(/[^\d+]/g, '');
const normalizeWhatsAppPhoneValue = (value) => `${value || ''}`.replace(/\D/g, '');
const isValidPhone = (value) => {
  const normalized = normalizeWhatsAppPhoneValue(value);
  return normalized.length >= 7;
};

const formatHeaderValueForDraft = (header, value) => {
  if (header?.type === 'checkbox') return value ? 'Yes' : 'No';
  if (header?.type === 'multiselect' || header?.type === 'list') return Array.isArray(value) ? value.join(', ') : '';
  if (header?.type === 'percent') {
    if (value === '' || value === null || value === undefined) return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${value}%`;
    const digits = normalizeDecimalDigits(header?.decimalDigits, 2);
    return `${numeric.toFixed(digits)}%`;
  }
  if (header?.type === 'number' || header?.type === 'currency') {
    if (value === '' || value === null || value === undefined) return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${value}`;
    const digits = normalizeDecimalDigits(header?.decimalDigits, 2);
    return numeric.toFixed(digits);
  }
  return `${value ?? ''}`.trim();
};

const getHeaderDisplayValueForRow = (header, rowValues = {}) => {
  if (!header) return '';
  if (header.type === 'profile') {
    return `${getProfileDisplayData(header, rowValues).label || ''}`.trim();
  }
  return formatHeaderValueForDraft(header, rowValues?.[header.id]);
};

const buildEmailDraft = ({
  prompt,
  row,
  recipient,
  headers,
  sheetTitle,
  description
}) => {
  const rowSummary = headers
    .map((header) => ({
      label: header.label,
      value: getHeaderDisplayValueForRow(header, row?.resolvedValues || row?.values || {})
    }))
    .filter((item) => item.value);

  const primaryLabel = rowSummary.find((item) => item.label.toLowerCase() !== 'email')?.value || recipient;
  const subjectPrefix = prompt?.trim() ? prompt.trim() : `Regarding ${sheetTitle}`;
  const bulletLines = rowSummary
    .filter((item) => item.label.toLowerCase() !== 'email')
    .slice(0, 6)
    .map((item) => `- ${item.label}: ${item.value}`);

  const bodySections = [
    `Hello ${primaryLabel || 'there'},`,
    '',
    prompt?.trim()
      ? `I am reaching out about ${sheetTitle}. ${prompt.trim()}`
      : `I am reaching out regarding ${sheetTitle}.`,
    description?.trim() ? '' : null,
    description?.trim() ? description.trim() : null,
    bulletLines.length ? '' : null,
    bulletLines.length ? 'Current details:' : null,
    ...bulletLines,
    '',
    'Please let me know if you have any questions.',
    '',
    'Best regards'
  ].filter(Boolean);

  return {
    subject: subjectPrefix,
    body: bodySections.join('\n')
  };
};

const buildBatchEmailDraft = ({
  prompt,
  rows,
  headers,
  recipients,
  sheetTitle,
  description
}) => {
  const previewRows = (rows || []).slice(0, 5).map((row, index) => {
    const summary = headers
      .map((header) => ({
        label: header.label,
        value: getHeaderDisplayValueForRow(header, row?.resolvedValues || row?.values || {})
      }))
      .filter((item) => item.value)
      .slice(0, 4)
      .map((item) => `${item.label}: ${item.value}`)
      .join(' | ');

    return `${index + 1}. ${summary || 'No row details available'}`;
  });

  const bodySections = [
    'Hello,',
    '',
    prompt?.trim()
      ? `I am reaching out about ${sheetTitle}. ${prompt.trim()}`
      : `I am reaching out regarding ${sheetTitle}.`,
    description?.trim() ? '' : null,
    description?.trim() ? description.trim() : null,
    previewRows.length ? '' : null,
    previewRows.length ? 'Selected row context:' : null,
    ...previewRows,
    rows?.length > 5 ? `...and ${rows.length - 5} more selected row${rows.length - 5 === 1 ? '' : 's'}.` : null,
    '',
    'Please let me know if you have any questions.',
    '',
    'Best regards'
  ].filter(Boolean);

  return {
    subject: prompt?.trim() ? prompt.trim() : `Update regarding ${sheetTitle}`,
    body: bodySections.join('\n'),
    to: (recipients || []).join(', ')
  };
};

const buildSmsDraft = ({
  prompt,
  row,
  recipient,
  headers,
  sheetTitle
}) => {
  const rowSummary = headers
    .map((header) => ({
      label: header.label,
      value: getHeaderDisplayValueForRow(header, row?.resolvedValues || row?.values || {})
    }))
    .filter((item) => item.value && item.label.toLowerCase() !== 'telephone')
    .slice(0, 4)
    .map((item) => `${item.label}: ${item.value}`);

  const opening = prompt?.trim() ? prompt.trim() : `${sheetTitle}:`;
  const summary = rowSummary.length ? ` ${rowSummary.join(' | ')}` : '';

  return {
    body: `${opening}${summary}`.trim(),
    to: recipient
  };
};

const buildBatchSmsDraft = ({
  prompt,
  rows,
  headers,
  recipients,
  sheetTitle
}) => {
  const previewRows = (rows || [])
    .slice(0, 3)
    .map((row) => (
      headers
        .map((header) => ({
          label: header.label,
          value: getHeaderDisplayValueForRow(header, row?.resolvedValues || row?.values || {})
        }))
        .filter((item) => item.value && item.label.toLowerCase() !== 'telephone')
        .slice(0, 2)
        .map((item) => `${item.label}: ${item.value}`)
        .join(' | ')
    ))
    .filter(Boolean);

  const messageParts = [
    prompt?.trim() || `${sheetTitle} update`,
    previewRows.length ? previewRows.join(' || ') : null
  ].filter(Boolean);

  return {
    body: messageParts.join(' - ').trim(),
    to: (recipients || []).join(', ')
  };
};

const getUserInitials = (user, userProfile) => {
  const source = `${userProfile?.displayName || user?.displayName || user?.email || ''}`.trim();
  if (!source) return 'U';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
};

const normalizeDecimalDigits = (value, fallback = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(6, Math.round(numeric)));
};

const normalizeLogSystemField = (value) => (
  LOG_SYSTEM_FIELDS.has(`${value || ''}`.trim()) ? `${value}`.trim() : ''
);

const getLogSystemFieldMap = (headers = []) => headers.reduce((acc, header) => {
  const systemField = normalizeLogSystemField(header?.systemField);
  if (systemField && !acc[systemField]) {
    acc[systemField] = header.id;
  }
  return acc;
}, {});

const createCustomLogDraft = (headers = []) => headers.reduce((acc, header) => {
  if (!normalizeLogSystemField(header?.systemField)) {
    acc[header.id] = getHeaderInitialValue(header);
  }
  return acc;
}, {});
const createActionFormDraft = (headers = []) => headers.reduce((acc, header) => {
  if (!normalizeLogSystemField(header?.systemField)) {
    acc[header.id] = getHeaderInitialValue(header);
  }
  return acc;
}, {});
const getActionFormNotificationLabel = (type, count = 1) => {
  const copy = ACTION_FORM_NOTIFICATION_COPY[normalizeActionFormType(type)] || ACTION_FORM_NOTIFICATION_COPY.custom;
  return count > 1 ? `${count} ${copy.plural}` : copy.single;
};
const sanitizeLinkedActionHeaders = (headers = []) => ((Array.isArray(headers) ? headers : [])).map((header) => ({
  ...header,
  options: Array.isArray(header?.options) ? header.options : [],
  conditionalOptions: Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [],
  conditionalLogic: header?.conditionalLogic || null,
  profileConfig: {
    imageHeaderId: `${header?.profileConfig?.imageHeaderId || ''}`.trim(),
    labelHeaderId: `${header?.profileConfig?.labelHeaderId || ''}`.trim()
  },
  width: Math.max(120, Number(header?.width) || DEFAULT_COLUMN_WIDTH),
  systemField: normalizeLogSystemField(header?.systemField),
  rogOverlay: header?.type === 'rog' ? Boolean(header?.rogOverlay) : false
}));
const resolveActionFieldMappingValue = (fieldMapping, sourceValues = {}) => (
  `${fieldMapping?.valueMode || ''}`.trim() === 'static'
    ? (fieldMapping?.value ?? '')
    : (sourceValues?.[fieldMapping?.sourceHeaderId] ?? '')
);
const isActionFieldValueEmpty = (header, value) => {
  if (header?.type === 'checkbox') return value !== true && value !== false;
  if (header?.type === 'multiselect' || header?.type === 'list') return !Array.isArray(value) || value.length === 0;
  return `${value ?? ''}`.trim() === '';
};
const getReferencedExternalSheetIds = (headers = [], currentSheetId = '') => {
  const normalizedHeaders = Array.isArray(headers) ? headers : [];
  const extractFormulaSheetId = (formula) => {
    const config = parseFormulaToConfig(formula, normalizedHeaders);
    return config?.type === 'lookup' ? `${config.lookupSheetId || ''}`.trim() : '';
  };

  return Array.from(new Set([
    ...normalizedHeaders
      .map((header) => normalizeSheetOptionSource(header?.optionSource)?.sheetId || '')
      .filter((sheetId) => sheetId && sheetId !== currentSheetId),
    ...normalizedHeaders.flatMap((header) => (
      (Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [])
        .map((rule) => normalizeSheetOptionSource(rule?.optionSource)?.sheetId || '')
        .filter((sheetId) => sheetId && sheetId !== currentSheetId)
    )),
    ...normalizedHeaders
      .map((header) => extractFormulaSheetId(header?.formula || ''))
      .filter((sheetId) => sheetId && sheetId !== currentSheetId),
    ...normalizedHeaders.flatMap((header) => (
      (Array.isArray(header?.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
        .map((rule) => extractFormulaSheetId(rule?.formula || ''))
        .filter((sheetId) => sheetId && sheetId !== currentSheetId)
    ))
  ]));
};

const normalizeHeaderTextAlign = (value) => (
  ['left', 'center', 'right'].includes(`${value || ''}`.trim()) ? `${value}`.trim() : 'left'
);
const normalizeHeaderRenderColor = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return '';
  const compact = normalized.startsWith('#') ? normalized.slice(1) : normalized;
  return /^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(compact) ? `#${compact}` : '';
};

const normalizeColumnAccess = (value) => (
  ['hidden', 'private', 'restricted', 'public'].includes(`${value || ''}`.trim()) ? `${value}`.trim() : 'public'
);

const getColumnTextStyle = (header) => ({
  fontWeight: header?.textBold ? 700 : 400,
  fontStyle: header?.textItalic ? 'italic' : 'normal',
  textDecoration: header?.textUnderline ? 'underline' : 'none',
  textAlign: normalizeHeaderTextAlign(header?.textAlign),
  color: normalizeHeaderRenderColor(header?.renderColor) || undefined
});

const getColumnAlignmentClass = (alignment) => (
  alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'
);

const getProfileSourceHeaderIds = (headers = []) => new Set(
  headers.flatMap((header) => (
    header?.type === 'profile'
      ? [header?.profileConfig?.imageHeaderId, header?.profileConfig?.labelHeaderId]
      : []
  ))
    .map((headerId) => `${headerId || ''}`.trim())
    .filter(Boolean)
);

const isFirebaseImageUrl = (value) => {
  const source = `${value ?? ''}`.trim();
  if (!source) return false;

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.toLowerCase();
    const isFirebaseHost = host.includes('firebasestorage.googleapis.com') || host.includes('storage.googleapis.com');
    if (!isFirebaseHost) return false;

    const decodedPath = decodeURIComponent(parsed.pathname).toLowerCase();
    const imageExtensionPattern = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;
    const mediaToken = `${parsed.searchParams.get('alt') || ''}`.toLowerCase() === 'media';

    return imageExtensionPattern.test(decodedPath) || mediaToken;
  } catch (error) {
    return false;
  }
};

const getYouTubeEmbedUrl = (value) => {
  const source = `${value ?? ''}`.trim();
  if (!source) return '';

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsed.pathname.replace(/\//g, '');
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v') || '';
      } else if (parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.split('/embed/')[1] || '';
      } else if (parsed.pathname.startsWith('/shorts/')) {
        videoId = parsed.pathname.split('/shorts/')[1] || '';
      }
    }

    const normalizedVideoId = `${videoId || ''}`.split('/')[0].split('?')[0].trim();
    return normalizedVideoId ? `https://www.youtube.com/embed/${normalizedVideoId}` : '';
  } catch (error) {
    return '';
  }
};

const formatNumericDisplayValue = (header, value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${value}`;
  return numeric.toFixed(normalizeDecimalDigits(header?.decimalDigits, 2));
};

const SUMMARY_LABELS = {
  sum: 'Sum',
  average: 'Avg',
  min: 'Min',
  max: 'Max',
  count: 'Count'
};

const getSummaryDisplayValue = (header, value, summaryType) => {
  if (value === '' || value === null || value === undefined) return '';

  if (summaryType === 'count') {
    return `${Number.isFinite(Number(value)) ? Math.round(Number(value)) : value}`;
  }

  if (
    header?.type === 'percent' ||
    header?.type === 'number' ||
    header?.type === 'currency' ||
    header?.type === 'rating'
  ) {
    const formattedValue = formatNumericDisplayValue(header, value);
    return header?.type === 'percent' ? `${formattedValue}%` : formattedValue;
  }

  return `${value}`;
};

const createInitialLogDraft = () => ({
  date: new Date().toISOString().slice(0, 10),
  note: '',
  progress: '',
  status: 'O',
  action: 'Call'
});

const getLogScopeLabel = (scopeType) => {
  if (scopeType === 'sheet') return 'Sheet';
  if (scopeType === 'column') return 'Column';
  return 'Row';
};

const getSheetLoadErrorMessage = (error, user) => {
  const errorCode = `${error?.code || ''}`.trim().toLowerCase();
  const errorMessage = `${error?.message || ''}`.trim().toLowerCase();
  const isPermissionError = errorCode === 'permission-denied' || errorMessage.includes('missing or insufficient permissions');

  if (isPermissionError) {
    if (!user?.uid) {
      return 'This sheet is restricted. Sign in with an allowed account to open it.';
    }
    return 'This sheet has been shared with selected users only. You are signed in, but your account does not currently have access. Ask the sheet owner to add your email address or user account.';
  }

  return error?.message || 'Failed to load sheet';
};

const isLegacyOwnRowsMode = (sheetData) => (
  sheetData?.visibility === 'restricted' && sheetData?.collaboratorAccess === 'edit_own_rows'
);

const isViewAllEditOwnMode = (sheetData) => (
  sheetData?.visibility === 'restricted' && sheetData?.sharedAccess === 'view_all_edit_own'
);

const isSharedSheetEditMode = (sheetData) => (
  sheetData?.visibility === 'restricted' && sheetData?.sharedAccess === 'edit'
);

const SheetViewer = () => {
  const { id, workspaceSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [workspaceAdminAccessEnabled, setWorkspaceAdminAccessEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeResize, setActiveResize] = useState(null);
  const [openMultiSelectState, setOpenMultiSelectState] = useState(null);
  const [composerState, setComposerState] = useState(null);
  const [sendingComposer, setSendingComposer] = useState(false);
  const [activeRowId, setActiveRowId] = useState(null);
  const [sidebarView, setSidebarView] = useState('details');
  const [rowDetailMode, setRowDetailMode] = useState('view');
  const [sheetEditMode, setSheetEditMode] = useState(false);
  const [logMode, setLogMode] = useState('saved');
  const [logScopeType, setLogScopeType] = useState('row');
  const [selectedLogColumnId, setSelectedLogColumnId] = useState('');
  const [logDraft, setLogDraft] = useState(createInitialLogDraft);
  const [logs, setLogs] = useState([]);
  const [customLogSheet, setCustomLogSheet] = useState(null);
  const [customLogRows, setCustomLogRows] = useState([]);
  const [customLogDraft, setCustomLogDraft] = useState({});
  const [activeActionFormId, setActiveActionFormId] = useState('');
  const [actionFormSheet, setActionFormSheet] = useState(null);
  const [actionSheetForm, setActionSheetForm] = useState(null);
  const [actionFormRows, setActionFormRows] = useState([]);
  const [actionFormDraft, setActionFormDraft] = useState({});
  const [actionFormSaving, setActionFormSaving] = useState(false);
  const [actionFormOptionRowsBySheetId, setActionFormOptionRowsBySheetId] = useState({});
  const [instantActionPrompt, setInstantActionPrompt] = useState(null);
  const [actionFormActivityByFormId, setActionFormActivityByFormId] = useState({});
  const [openRowActionMenuRowId, setOpenRowActionMenuRowId] = useState('');
  const [activeImageUrl, setActiveImageUrl] = useState('');
  const [activeYoutubeEmbedUrl, setActiveYoutubeEmbedUrl] = useState('');
  const [logSaving, setLogSaving] = useState(false);
  const [aiMode, setAiMode] = useState('report');
  const [sheetAiOpen, setSheetAiOpen] = useState(false);
  const [chatMode, setChatMode] = useState('current');
  const [currentThreadMessages, setCurrentThreadMessages] = useState([]);
  const [savedThreads, setSavedThreads] = useState([]);
  const [selectedSavedThreadId, setSelectedSavedThreadId] = useState(null);
  const [savedThreadMessages, setSavedThreadMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatMentionedRowIds, setChatMentionedRowIds] = useState([]);
  const [showChatRowMentions, setShowChatRowMentions] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [savingChatThread, setSavingChatThread] = useState(false);
  const [reportInput, setReportInput] = useState('');
  const [reportTargetType, setReportTargetType] = useState('selected-rows');
  const [selectedReportColumnId, setSelectedReportColumnId] = useState('');
  const [generatedReport, setGeneratedReport] = useState('');
  const [generatedReportTitle, setGeneratedReportTitle] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [automationRunningId, setAutomationRunningId] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [realtimeFailed, setRealtimeFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [subscriptionRetryToken, setSubscriptionRetryToken] = useState(0);
  const [optionSourceRowsBySheetId, setOptionSourceRowsBySheetId] = useState({});
  const [uploadingImageKey, setUploadingImageKey] = useState('');
  const [uploadingDocumentKey, setUploadingDocumentKey] = useState('');
  const [importingCsv, setImportingCsv] = useState(false);
  const [listDrafts, setListDrafts] = useState({});
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showChartsSidebar, setShowChartsSidebar] = useState(false);
  const [analyticsResults, setAnalyticsResults] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsFilterOptionsByHeaderId, setAnalyticsFilterOptionsByHeaderId] = useState({});
  const [embeddedAnalyticsExpanded, setEmbeddedAnalyticsExpanded] = useState(true);
  const [embeddedAnalyticsPreferenceSheetId, setEmbeddedAnalyticsPreferenceSheetId] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [sheetFormSummary, setSheetFormSummary] = useState(null);
  const [csvImportSummary, setCsvImportSummary] = useState(null);
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => getMonthStart(new Date()));
  const [calendarFocusDate, setCalendarFocusDate] = useState(() => new Date());
  const [calendarPresetView, setCalendarPresetView] = useState('week');
  const gridScrollRef = useRef(null);
  const csvImportInputRef = useRef(null);
  const realtimeFailureToastShownRef = useRef(false);
  const hasAutoOpenedRowRef = useRef(false);
  const userMenuRef = useRef(null);
  const analyticsRequestSequenceRef = useRef(0);
  const analyticsCacheRef = useRef(new Map());
  const canUsePublicAssetUploads = sheet?.visibility === 'public';
  const assetUploadOwnerSegment = canUsePublicAssetUploads ? 'public' : user?.uid;

  const handleRealtimeError = (err) => {
    console.warn('Live sheet updates failed:', err);
    setRealtimeFailed(true);
    if (!realtimeFailureToastShownRef.current) {
      realtimeFailureToastShownRef.current = true;
      toast.error('Live updates disconnected. Use refresh to reload the latest sheet data.');
    }
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const loadSheetSnapshot = useCallback(async ({ preserveLocalRows = true, showSpinner = true } = {}) => {
    if (!id) return null;

    if (showSpinner) {
      setLoading(true);
    }

    setError(null);

    try {
      const [sheetData, rowData] = await Promise.all([
        sheetService.getSheetById(id),
        sheetService.getSheetRows(id, 500)
      ]);

      setSheet(sheetData);
      setSelectedRowIds([]);
      setRows((prev) => (
        preserveLocalRows
          ? mergeRealtimeRows(rowData, prev, sheetData, user, sheetData.headers || [])
          : [
              ...createDraftRows(sheetData, user, sheetData.headers || [], rowData),
              ...normalizeRows(rowData)
            ]
      ));
      setRealtimeFailed(false);
      realtimeFailureToastShownRef.current = false;
      return sheetData;
    } catch (err) {
      setError(getSheetLoadErrorMessage(err, user));
      throw err;
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, [id, user]);

  useEffect(() => {
    let cancelled = false;

    const loadInitialSheet = async () => {
      try {
        await loadSheetSnapshot({ preserveLocalRows: false, showSpinner: true });
      } catch (err) {
        if (cancelled) return;
      }
    };

    loadInitialSheet();

    return () => {
      cancelled = true;
    };
  }, [loadSheetSnapshot]);

  useEffect(() => {
    if (!sheet?.id) return;

    applySheetPageMetadata(buildSheetShareMetadata(sheet, {
      workspaceSlug,
      pageUrl: window.location.href
    }));
  }, [sheet, workspaceSlug]);

  useEffect(() => {
    let cancelled = false;
    const workspaceId = `${sheet?.workspaceId || ''}`.trim();
    const isPlatformAdmin = `${userProfile?.role || ''}`.trim() === 'admin';

    setWorkspaceAdminAccessEnabled(false);
    if (!workspaceId || !isPlatformAdmin) return undefined;

    sheetWorkspaceService.getWorkspaceById(workspaceId)
      .then((workspace) => {
        if (!cancelled) {
          setWorkspaceAdminAccessEnabled(workspace?.adminBuilderAccessEnabled === true);
        }
      })
      .catch(() => {
        if (!cancelled) setWorkspaceAdminAccessEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sheet?.workspaceId, userProfile?.role]);

  const headers = useMemo(() => sheet?.headers || [], [sheet]);
  const csvColumns = useMemo(() => getSheetCsvColumns(headers), [headers]);
  const isEmbedded = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('embedded') === '1';
  }, [location.search]);
  const embeddedFallbackPrimaryColor = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeWorkspacePrimaryColor(params.get('themeColor'));
  }, [location.search]);
  const sheetPrimaryColor = normalizeWorkspacePrimaryColor(sheet?.primaryColor)
    || embeddedFallbackPrimaryColor
    || DEFAULT_WORKSPACE_PRIMARY_COLOR;
  const sheetThemeStyle = {
    '--sheet-primary-color': sheetPrimaryColor,
    '--sheet-primary-faint': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.05),
    '--sheet-primary-soft': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.1),
    '--sheet-primary-medium': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.15),
    '--sheet-primary-strong': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.2),
    '--sheet-primary-border': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.3),
    '--sheet-primary-border-strong': workspacePrimaryColorWithAlpha(sheetPrimaryColor, 0.4)
  };
  const isOwner = Boolean(user?.uid && sheet?.userId === user.uid);
  const fromSharedSheets = Boolean(location.state?.fromSharedSheets);
  const fromAdminAccess = Boolean(location.state?.fromAdminAccess);
  const isAdminReadOnlyView = Boolean(
    !isOwner &&
    user?.uid &&
    `${userProfile?.role || ''}`.trim() === 'admin' &&
    (sheet?.adminBuilderAccessEnabled === true || workspaceAdminAccessEnabled)
  );
  const hasWorkspaceScopedRoute = Boolean(`${workspaceSlug || ''}`.trim());
  const isCustomWorkspaceHost = isLikelyWorkspaceCustomHost(window.location.hostname);
  const isOwnRowsMode = isLegacyOwnRowsMode(sheet);
  const isViewAllEditOwn = isViewAllEditOwnMode(sheet);
  const isSharedSheetEditor = isSharedSheetEditMode(sheet);
  const isReadOnlyTemplate = Boolean(sheet?.isTemplate && !isOwner);
  const canCreateRows = Boolean(
    !isReadOnlyTemplate &&
    !isAdminReadOnlyView &&
    user?.uid &&
    (isOwner || isOwnRowsMode || isViewAllEditOwn || isSharedSheetEditor)
  );
  const canShowBackButton = isOwner || fromSharedSheets || fromAdminAccess || isAdminReadOnlyView;

  useEffect(() => {
    if (!isAdminReadOnlyView) return;
    setSidebarView('details');
    setRowDetailMode('view');
    setSheetEditMode(false);
    setActiveActionFormId('');
  }, [isAdminReadOnlyView]);

  const referencedOptionSourceSheetIds = useMemo(
    () => Array.from(new Set([
      ...headers
        .map((header) => normalizeSheetOptionSource(header.optionSource)?.sheetId || '')
        .filter((sheetId) => sheetId && sheetId !== id),
      ...headers.flatMap((header) => (
        (Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [])
          .map((rule) => normalizeSheetOptionSource(rule?.optionSource)?.sheetId || '')
          .filter((sheetId) => sheetId && sheetId !== id)
      ))
    ])),
    [headers, id]
  );
  const referencedFormulaSheetIds = useMemo(
    () => Array.from(new Set(
      headers.flatMap((header) => {
        const directConfig = parseFormulaToConfig(header?.formula || '', headers);
        const directSheetId = directConfig?.type === 'lookup' ? `${directConfig.lookupSheetId || ''}`.trim() : '';
        const conditionalSheetIds = (Array.isArray(header?.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
          .map((rule) => {
            const config = parseFormulaToConfig(rule?.formula || '', headers);
            return config?.type === 'lookup' ? `${config.lookupSheetId || ''}`.trim() : '';
          })
          .filter((sheetId) => sheetId && sheetId !== id);
        return [directSheetId, ...conditionalSheetIds].filter((sheetId) => sheetId && sheetId !== id);
      })
    )),
    [headers, id]
  );
  const referencedExternalSheetIds = useMemo(
    () => Array.from(new Set([
      ...referencedOptionSourceSheetIds,
      ...referencedFormulaSheetIds
    ])),
    [referencedFormulaSheetIds, referencedOptionSourceSheetIds]
  );

  useEffect(() => {
    if (!referencedExternalSheetIds.length) {
      setOptionSourceRowsBySheetId({});
      return;
    }

    let isActive = true;

    const loadOptionSourceRows = async () => {
      const results = await Promise.allSettled(
        referencedExternalSheetIds.map(async (sheetId) => {
          const sourceSheet = await sheetService.getSheetById(sheetId);
          const rows = await sheetService.getSheetRows(sheetId, 500);
          return [sheetId, { rows, headers: sourceSheet?.headers || [] }];
        })
      );

      if (!isActive) return;

      const nextRowsBySheetId = results.reduce((acc, result) => {
        if (result.status !== 'fulfilled') return acc;
        const [sheetId, sourceData] = result.value;
        acc[sheetId] = sourceData;
        return acc;
      }, {});

      setOptionSourceRowsBySheetId(nextRowsBySheetId);
    };

    loadOptionSourceRows();

    return () => {
      isActive = false;
    };
  }, [referencedExternalSheetIds]);

  useEffect(() => {
    if (!id) return undefined;

    const unsubscribe = sheetService.subscribeToSheet(
      id,
      (nextSheet) => {
        setSheet(nextSheet);
        setError(null);
        setRealtimeFailed(false);
        realtimeFailureToastShownRef.current = false;
      },
      (err) => {
        setError(getSheetLoadErrorMessage(err, user));
        handleRealtimeError(err);
      }
    );

    return () => {
      unsubscribe?.();
    };
  }, [id, subscriptionRetryToken, user]);

  useEffect(() => {
    if (!id || !sheet) return undefined;

    const unsubscribe = sheetService.subscribeToSheetRows(
      id,
      {
        maxResults: 500,
        sheetData: sheet
      },
      (nextRows) => {
        setRows((prev) => mergeRealtimeRows(nextRows, prev, sheet, user, sheet.headers || []));
        setLoading(false);
        setRealtimeFailed(false);
        realtimeFailureToastShownRef.current = false;
      },
      (err) => {
        handleRealtimeError(err);
      }
    );

    return () => {
      unsubscribe?.();
    };
  }, [id, sheet, subscriptionRetryToken, user]);

  useEffect(() => {
    const redirectWorkspaceOnlySheet = async () => {
      if (!id || (!sheet?.workspaceOnly && !`${sheet?.workspaceId || ''}`.trim())) return;
      if (isEmbedded) return;
      if (hasWorkspaceScopedRoute || isCustomWorkspaceHost) return;

      const workspaceId = `${sheet?.workspaceId || ''}`.trim();
      if (!workspaceId) return;

      try {
        const workspace = await sheetWorkspaceService.getWorkspaceById(workspaceId);
        const resolvedWorkspaceSlug = `${workspace?.slug || ''}`.trim();
        if (!resolvedWorkspaceSlug) return;
        navigate(getSheetPath(id, resolvedWorkspaceSlug), {
          replace: true,
          state: location.state
        });
      } catch (error) {
        console.warn('Failed to redirect workspace-only sheet to workspace path:', error);
      }
    };

    redirectWorkspaceOnlySheet();
  }, [hasWorkspaceScopedRoute, id, isCustomWorkspaceHost, isEmbedded, location.state, navigate, sheet]);

  const handleOpenSheetForm = () => {
    if (!sheet?.id) return;
    if (hasPublishedSheetForm) {
      window.open(getSheetFormUrl(sheet.id), '_blank', 'noopener,noreferrer');
      return;
    }
    if (isOwner) {
      navigate(`/sheets/${sheet.id}/edit`, {
        state: {
          returnTo: location.pathname,
          returnToState: location.state
        }
      });
      return;
    }
    toast.error('This sheet does not have a published form yet.');
  };

  const handleBackNavigation = async () => {
    if (fromAdminAccess || isAdminReadOnlyView) {
      navigate('/sheets/admin-access');
      return;
    }

    const workspaceId = `${sheet?.workspaceId || ''}`.trim();
    if (workspaceId) {
      try {
        const workspace = await sheetWorkspaceService.getWorkspaceById(workspaceId);
        const workspaceSlug = `${workspace?.slug || ''}`.trim();
        if (workspaceSlug) {
          navigate(getWorkspacePath(workspaceSlug));
          return;
        }
      } catch (error) {
        console.warn('Failed to resolve workspace for back navigation:', error);
      }
    }

    navigate('/sheets', fromSharedSheets ? { state: { initialView: 'shared' } } : undefined);
  };
  const canAccessHeader = useCallback((header) => {
    const columnAccess = normalizeColumnAccess(header?.columnAccess);
    if (columnAccess === 'hidden') return false;
    if (columnAccess === 'private') return isOwner || isAdminReadOnlyView;
    if (columnAccess === 'restricted') return Boolean(user?.uid);
    return true;
  }, [isAdminReadOnlyView, isOwner, user?.uid]);
  const canAccessDetailHeader = useCallback((header) => {
    const columnAccess = normalizeColumnAccess(header?.columnAccess);
    if (columnAccess === 'private') return isOwner || isAdminReadOnlyView;
    if (columnAccess === 'restricted') return Boolean(user?.uid);
    return true;
  }, [isAdminReadOnlyView, isOwner, user?.uid]);
  const headerOptionRowsBySheetId = useMemo(
    () => ({
      ...optionSourceRowsBySheetId,
      ...(sheet?.id ? { [sheet.id]: { rows, headers } } : {})
    }),
    [headers, optionSourceRowsBySheetId, rows, sheet?.id]
  );
  const resolvedAllHeaders = useMemo(
    () => headers.map((header) => ({
      ...header,
      options: Array.isArray(header.options) && header.options.length
        ? header.options
        : getFixedOptionsForHeaderType(header.type),
      conditionalOptions: sanitizeHeaderConditionalOptions(header.conditionalOptions, headers, header.id),
      profileConfig: {
        imageHeaderId: `${header?.profileConfig?.imageHeaderId || ''}`.trim(),
        labelHeaderId: `${header?.profileConfig?.labelHeaderId || ''}`.trim()
      },
      conditionalLogic: sanitizeHeaderConditionalLogic(header.conditionalLogic, headers, header.id),
      width: Math.max(120, Number(header.width) || DEFAULT_COLUMN_WIDTH),
      textBold: Boolean(header.textBold),
      textItalic: Boolean(header.textItalic),
      textUnderline: Boolean(header.textUnderline),
      textAlign: normalizeHeaderTextAlign(header.textAlign),
      renderColor: normalizeHeaderRenderColor(header.renderColor),
      columnAccess: normalizeColumnAccess(header.columnAccess),
      rogOverlay: header.type === 'rog' ? Boolean(header.rogOverlay) : false
    })),
    [headers]
  );
  const resolvedHeaders = useMemo(
    () => resolvedAllHeaders.filter((header) => canAccessHeader(header)),
    [canAccessHeader, resolvedAllHeaders]
  );
  const renderedSourceHeaderIds = useMemo(
    () => getProfileSourceHeaderIds(resolvedHeaders),
    [resolvedHeaders]
  );
  const displayedTableHeaders = useMemo(
    () => resolvedHeaders.filter((header) => !renderedSourceHeaderIds.has(header.id)),
    [renderedSourceHeaderIds, resolvedHeaders]
  );
  const tableHeaderIds = useMemo(
    () => new Set(displayedTableHeaders.map((header) => header.id)),
    [displayedTableHeaders]
  );
  const sheetGridHeaders = useMemo(() => {
    if (!sheetEditMode) return displayedTableHeaders;
    return resolvedAllHeaders.filter((header) => {
      if (tableHeaderIds.has(header.id)) return true;
      const columnAccess = normalizeColumnAccess(header?.columnAccess);
      return columnAccess === 'hidden' && canAccessDetailHeader(header);
    });
  }, [sheetEditMode, resolvedAllHeaders, tableHeaderIds, canAccessDetailHeader, displayedTableHeaders]);
  const customLogHeaders = useMemo(
    () => (customLogSheet?.headers || []).map((header) => ({
      ...header,
      options: Array.isArray(header?.options) ? header.options : [],
      conditionalOptions: sanitizeHeaderConditionalOptions(customLogSheet?.headers ? header?.conditionalOptions : [], customLogSheet?.headers || [], header?.id),
      conditionalLogic: sanitizeHeaderConditionalLogic(header?.conditionalLogic, customLogSheet?.headers || [], header?.id),
      profileConfig: {
        imageHeaderId: `${header?.profileConfig?.imageHeaderId || ''}`.trim(),
        labelHeaderId: `${header?.profileConfig?.labelHeaderId || ''}`.trim()
      },
      width: Math.max(120, Number(header?.width) || DEFAULT_COLUMN_WIDTH),
      systemField: normalizeLogSystemField(header?.systemField),
      rogOverlay: header?.type === 'rog' ? Boolean(header?.rogOverlay) : false
    })),
    [customLogSheet]
  );
  const customLogSystemFieldMap = useMemo(
    () => getLogSystemFieldMap(customLogHeaders),
    [customLogHeaders]
  );
  const customLogUserHeaders = useMemo(
    () => customLogHeaders.filter((header) => !normalizeLogSystemField(header.systemField)),
    [customLogHeaders]
  );
  const customLogOptionRowsBySheetId = useMemo(
    () => ({
      ...headerOptionRowsBySheetId,
      ...(customLogSheet?.id ? { [customLogSheet.id]: customLogRows } : {})
    }),
    [customLogRows, customLogSheet?.id, headerOptionRowsBySheetId]
  );
  const visibleCustomLogUserHeaders = useMemo(
    () => customLogUserHeaders.filter((header) => !evaluateHeaderConditionalState(header, customLogDraft, customLogHeaders).hidden),
    [customLogDraft, customLogHeaders, customLogUserHeaders]
  );
  const hasRequiredCustomLogSystemFields = useMemo(
    () => !customLogSheet?.id || (
      Boolean(customLogSystemFieldMap.logScope) &&
      Boolean(customLogSystemFieldMap.sourceSheetId) &&
      Boolean(customLogSystemFieldMap.sourceRowId) &&
      Boolean(customLogSystemFieldMap.sourceColumnId)
    ),
    [customLogSheet?.id, customLogSystemFieldMap]
  );
  const sheetActionForms = useMemo(
    () => (isAdminReadOnlyView ? [] : normalizeSheetActionForms(sheet?.actionForms || [])),
    [isAdminReadOnlyView, sheet?.actionForms]
  );
  const linkedSheetActionForms = useMemo(
    () => sheetActionForms.filter((actionForm) => actionForm.mode === 'linked_form'),
    [sheetActionForms]
  );
  const hasPublishedSheetForm = Boolean(sheetFormSummary?.id);
  const activeActionFormConfig = useMemo(
    () => sheetActionForms.find((actionForm) => actionForm.id === activeActionFormId) || null,
    [activeActionFormId, sheetActionForms]
  );
  const actionFormHeaders = useMemo(
    () => ((Array.isArray(actionSheetForm?.sheetHeaders) ? actionSheetForm.sheetHeaders : (actionFormSheet?.headers || []))).map((header) => ({
      ...header,
      options: Array.isArray(header?.options) ? header.options : [],
      conditionalOptions: sanitizeHeaderConditionalOptions(
        (Array.isArray(actionSheetForm?.sheetHeaders) || actionFormSheet?.headers) ? header?.conditionalOptions : [],
        Array.isArray(actionSheetForm?.sheetHeaders) ? actionSheetForm.sheetHeaders : (actionFormSheet?.headers || []),
        header?.id
      ),
      conditionalLogic: sanitizeHeaderConditionalLogic(
        header?.conditionalLogic,
        Array.isArray(actionSheetForm?.sheetHeaders) ? actionSheetForm.sheetHeaders : (actionFormSheet?.headers || []),
        header?.id
      ),
      profileConfig: {
        imageHeaderId: `${header?.profileConfig?.imageHeaderId || ''}`.trim(),
        labelHeaderId: `${header?.profileConfig?.labelHeaderId || ''}`.trim()
      },
      width: Math.max(120, Number(header?.width) || DEFAULT_COLUMN_WIDTH),
      systemField: normalizeLogSystemField(header?.systemField),
      rogOverlay: header?.type === 'rog' ? Boolean(header?.rogOverlay) : false
    })),
    [actionFormSheet, actionSheetForm]
  );
  const usesCustomActionSheetForm = useMemo(
    () => Boolean(actionSheetForm && normalizeActionSheetFormSubmissionMode(actionSheetForm?.submissionMode) === 'singleRow'),
    [actionSheetForm]
  );
  const actionFormRenderFields = useMemo(
    () => (
      usesCustomActionSheetForm
        ? (Array.isArray(actionSheetForm?.fields) ? actionSheetForm.fields : [])
        : actionFormHeaders.filter((header) => !normalizeLogSystemField(header.systemField))
    ),
    [actionFormHeaders, actionSheetForm, usesCustomActionSheetForm]
  );
  const buildActionFormDraftWithMappedValues = () => {
    const baseDraft = usesCustomActionSheetForm && actionSheetForm
      ? (() => {
          const initialValues = sheetFormService.createInitialValues(actionSheetForm);
          return initialValues && typeof initialValues === 'object' && !Array.isArray(initialValues) ? initialValues : {};
        })()
      : createActionFormDraft(actionFormHeaders);

    if (!activeRow || !activeActionFormConfig?.fieldMappings?.length) {
      return baseDraft;
    }

    const sourceValues = activeRow?.resolvedValues || activeRow?.values || {};
    activeActionFormConfig.fieldMappings.forEach((fieldMapping) => {
      if (!fieldMapping?.targetHeaderId) return;
      baseDraft[fieldMapping.targetHeaderId] = resolveActionFieldMappingValue(fieldMapping, sourceValues);
    });

    return baseDraft;
  };
  const visibleActionFormUserHeaders = useMemo(
    () => actionFormRenderFields.filter((header) => !evaluateHeaderConditionalState(header, actionFormDraft, actionFormHeaders).hidden),
    [actionFormDraft, actionFormHeaders, actionFormRenderFields]
  );
  const actionFormLayoutItems = useMemo(
    () => (usesCustomActionSheetForm ? buildActionSheetFormLayoutItems(actionSheetForm) : []),
    [actionSheetForm, usesCustomActionSheetForm]
  );
  const visibleActionFormLayoutItems = useMemo(
    () => actionFormLayoutItems.filter((item) => item.type !== 'field' || !evaluateHeaderConditionalState(item.field, actionFormDraft, actionFormHeaders).hidden),
    [actionFormDraft, actionFormHeaders, actionFormLayoutItems]
  );
  const buildLinkedActionDraft = useCallback((headers, linkedForm, sourceRow, actionConfig) => {
    const baseDraft = linkedForm && normalizeActionSheetFormSubmissionMode(linkedForm?.submissionMode) === 'singleRow'
      ? (() => {
          const initialValues = sheetFormService.createInitialValues(linkedForm);
          return initialValues && typeof initialValues === 'object' && !Array.isArray(initialValues) ? initialValues : {};
        })()
      : createActionFormDraft(headers);

    if (!sourceRow || !actionConfig?.fieldMappings?.length) {
      return baseDraft;
    }

    const sourceValues = sourceRow?.resolvedValues || sourceRow?.values || {};
    actionConfig.fieldMappings.forEach((fieldMapping) => {
      if (!fieldMapping?.targetHeaderId) return;
      baseDraft[fieldMapping.targetHeaderId] = resolveActionFieldMappingValue(fieldMapping, sourceValues);
    });

    return baseDraft;
  }, []);
  const loadLinkedActionContext = useCallback(async (actionConfig) => {
    const linkedActionSheetId = `${actionConfig?.targetSheetId || ''}`.trim();
    if (!linkedActionSheetId) throw new Error('Choose a target sheet for this action first.');

    const [linkedSheet, linkedRows, linkedForm] = await Promise.all([
      sheetService.getSheetById(linkedActionSheetId),
      sheetService.getSheetRows(linkedActionSheetId, 500),
      sheetFormService.getSheetFormById(linkedActionSheetId).catch(() => null)
    ]);

    const normalizedRows = normalizeRows(linkedRows);
    const sourceHeaders = Array.isArray(linkedForm?.sheetHeaders) ? linkedForm.sheetHeaders : (linkedSheet?.headers || []);
    const linkedHeaders = sanitizeLinkedActionHeaders(sourceHeaders).map((header) => ({
      ...header,
      conditionalOptions: sanitizeHeaderConditionalOptions(sourceHeaders ? header?.conditionalOptions : [], sourceHeaders, header?.id),
      conditionalLogic: sanitizeHeaderConditionalLogic(header?.conditionalLogic, sourceHeaders, header?.id)
    }));

    const referencedSheetIds = getReferencedExternalSheetIds(linkedHeaders, linkedSheet?.id || '');
    const referencedResults = await Promise.allSettled(
      referencedSheetIds.map(async (sheetId) => {
        const sourceSheet = await sheetService.getSheetById(sheetId);
        const rows = await sheetService.getSheetRows(sheetId, 500);
        return [sheetId, { rows: normalizeRows(rows), headers: sourceSheet?.headers || [] }];
      })
    );

    const optionRowsBySheetId = referencedResults.reduce((acc, result) => {
      if (result.status !== 'fulfilled') return acc;
      const [sheetId, sourceData] = result.value;
      acc[sheetId] = sourceData;
      return acc;
    }, linkedSheet?.id ? {
      [linkedSheet.id]: { rows: normalizedRows, headers: linkedSheet?.headers || [] }
    } : {});

    return {
      linkedSheet,
      linkedForm,
      linkedHeaders,
      linkedRows: normalizedRows,
      optionRowsBySheetId
    };
  }, []);
  const getInstantActionPromptHeaders = useCallback((headers = [], draftValues = {}, actionConfig) => {
    const mappedTargetHeaderIds = new Set(
      (Array.isArray(actionConfig?.fieldMappings) ? actionConfig.fieldMappings : [])
        .map((fieldMapping) => `${fieldMapping?.targetHeaderId || ''}`.trim())
        .filter(Boolean)
    );

    return (Array.isArray(headers) ? headers : []).filter((header) => {
      if (normalizeLogSystemField(header?.systemField)) return false;
      if (mappedTargetHeaderIds.has(header.id)) return false;
      const conditionalState = evaluateHeaderConditionalState(header, draftValues, headers);
      if (conditionalState.hidden || conditionalState.readOnly) return false;
      return header?.required === true || ACTION_INSTANT_SAVE_PROMPT_FIELD_TYPES.has(`${header?.type || ''}`.trim());
    });
  }, []);
  const actionSummariesByRowId = useMemo(() => {
    if (!sheet?.id) return {};

    return Object.values(actionFormActivityByFormId).reduce((acc, formData) => {
      const sourceSheetIdHeaderId = formData?.systemFieldMap?.sourceSheetId;
      const sourceRowIdHeaderId = formData?.systemFieldMap?.sourceRowId;
      if (!sourceSheetIdHeaderId || !sourceRowIdHeaderId) return acc;

      formData.rows.forEach((linkedRow) => {
        const values = linkedRow?.values || {};
        if (`${values[sourceSheetIdHeaderId] || ''}`.trim() !== sheet.id) return;
        const sourceRowId = `${values[sourceRowIdHeaderId] || ''}`.trim();
        if (!sourceRowId) return;

        if (!acc[sourceRowId]) {
          acc[sourceRowId] = { count: 0, labels: [], entries: [] };
        }
        acc[sourceRowId].count += 1;
        acc[sourceRowId].labels.push(getActionFormNotificationLabel(formData.actionForm?.type, 1));
        acc[sourceRowId].entries.push({
          formId: formData.actionForm?.id,
          formType: formData.actionForm?.type,
          formName: formData.actionForm?.name || formData.actionForm?.buttonLabel || 'Action',
          row: linkedRow,
          sheetTitle: formData.sheet?.title || ''
        });
      });

      return acc;
    }, {});
  }, [actionFormActivityByFormId, sheet?.id]);
  const getRowActiveActionSummary = useCallback((rowId) => {
    const summary = actionSummariesByRowId[rowId];
    if (!summary || !summary.count) return null;
    const labels = Array.from(new Set(summary.labels));
    return {
      ...summary,
      labels,
      primaryLabel: labels.length === 1 ? labels[0] : `${summary.count} Actions`
    };
  }, [actionSummariesByRowId]);
  const getRowPrimaryActionConfig = useCallback((rowId) => {
    const actionSummary = getRowActiveActionSummary(rowId);
    const matchingFormId = `${actionSummary?.entries?.[0]?.formId || ''}`.trim();
    const configuredActionForm = (
      sheetActionForms.find((actionForm) => actionForm.id === matchingFormId) ||
      sheetActionForms[0] ||
      null
    );
    if (!configuredActionForm) return null;
    return {
      formId: configuredActionForm.id,
      label: configuredActionForm.buttonLabel || configuredActionForm.name || 'Action'
    };
  }, [getRowActiveActionSummary, sheetActionForms]);
  const activeRowActionEntries = useMemo(() => {
    if (!activeRowId || !activeActionFormId) return [];
    return (actionSummariesByRowId[activeRowId]?.entries || []).filter((entry) => entry.formId === activeActionFormId);
  }, [actionSummariesByRowId, activeActionFormId, activeRowId]);
  const getResolvedOptionsForHeader = useCallback(
    (header, rowValues = {}) => getResolvedChoiceOptions(header, headerOptionRowsBySheetId, rowValues, resolvedAllHeaders),
    [headerOptionRowsBySheetId, resolvedAllHeaders]
  );

  const tableMinWidth = useMemo(
    () => SELECT_COLUMN_WIDTH +
      displayedTableHeaders.reduce((total, header) => total + header.width, 0) +
      (3 * META_COLUMN_WIDTH) +
      ((isOwner && isOwnRowsMode) ? META_COLUMN_WIDTH * 2.5 : 0),
    [displayedTableHeaders, isOwner, isOwnRowsMode]
  );
  const resolvedRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      resolvedValues: resolveRowFormulas(resolvedAllHeaders, row.values || {}, headerOptionRowsBySheetId)
    })),
    [headerOptionRowsBySheetId, resolvedAllHeaders, rows]
  );
  const filterableHeaderIds = useMemo(
    () => sanitizeFilterHeaderIds(sheet?.filterHeaderIds || [], resolvedHeaders),
    [resolvedHeaders, sheet?.filterHeaderIds]
  );
  const filterHeaders = useMemo(
    () => resolvedHeaders.filter((header) => filterableHeaderIds.includes(header.id)),
    [filterableHeaderIds, resolvedHeaders]
  );
  const filteredRows = useMemo(
    () => resolvedRows.filter((row) => {
      if (row.isNew) return true;
      return filterHeaders.every((header) => doesRowMatchFilter(row, header, activeFilters[header.id]));
    }),
    [activeFilters, filterHeaders, resolvedRows]
  );
  const hasAnalytics = Array.isArray(sheet?.analytics) && sheet.analytics.some((item) => item?.placement?.showInSheet !== false);
  useEffect(() => {
    if (isEmbedded || !showChartsSidebar || !hasAnalytics || !sheet?.id || !user?.uid) return undefined;
    let active = true;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    sheetService.calculateSheetAnalytics({
      sheetId: sheet.id,
      selectedRowIds,
      runtimeFilters: activeFilters
    }).then((response) => {
      if (active) setAnalyticsResults(Array.isArray(response?.results) ? response.results : []);
    }).catch((error) => {
      if (active) setAnalyticsError(error?.message || 'Unable to calculate analytics.');
    }).finally(() => {
      if (active) setAnalyticsLoading(false);
    });
    return () => { active = false; };
  }, [activeFilters, hasAnalytics, isEmbedded, selectedRowIds, sheet?.id, showChartsSidebar, user?.uid]);

  useEffect(() => {
    if (!isEmbedded || !sheet?.id) return;
    const storageKey = `sheet-analytics-band:${sheet.id}`;
    setEmbeddedAnalyticsExpanded(window.sessionStorage.getItem(storageKey) !== 'collapsed');
    setEmbeddedAnalyticsPreferenceSheetId(sheet.id);
  }, [isEmbedded, sheet?.id]);

  const toggleEmbeddedAnalytics = useCallback(() => {
    setEmbeddedAnalyticsExpanded((current) => {
      const next = !current;
      if (sheet?.id) {
        window.sessionStorage.setItem(`sheet-analytics-band:${sheet.id}`, next ? 'expanded' : 'collapsed');
      }
      return next;
    });
  }, [sheet?.id]);

  useEffect(() => {
    if (!isEmbedded || embeddedAnalyticsPreferenceSheetId !== sheet?.id || !embeddedAnalyticsExpanded || !hasAnalytics || !sheet?.id || !user?.uid) return undefined;
    const normalizedFilters = Object.fromEntries(Object.entries(activeFilters)
      .filter(([, value]) => `${value ?? ''}`.trim())
      .sort(([left], [right]) => left.localeCompare(right)));
    const cacheKey = JSON.stringify({
      sheetId: sheet.id,
      analytics: sheet.analytics || [],
      filters: normalizedFilters,
      selectedRowIds: [...selectedRowIds].sort()
    });
    const cached = analyticsCacheRef.current.get(cacheKey);
    if (cached) {
      setAnalyticsResults(cached.results);
      setAnalyticsFilterOptionsByHeaderId(cached.filterOptionsByHeaderId);
      setAnalyticsError('');
      setAnalyticsLoading(false);
      return undefined;
    }

    const requestSequence = analyticsRequestSequenceRef.current + 1;
    analyticsRequestSequenceRef.current = requestSequence;
    let active = true;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    const cancelCalculation = scheduleSheetAnalyticsCalculation(() => {
      sheetService.calculateSheetAnalytics({
        sheetId: sheet.id,
        selectedRowIds,
        runtimeFilters: normalizedFilters
      }).then((response) => {
        if (!active || requestSequence !== analyticsRequestSequenceRef.current) return;
        const nextValue = {
          results: Array.isArray(response?.results) ? response.results : [],
          filterOptionsByHeaderId: response?.filterOptionsByHeaderId && typeof response.filterOptionsByHeaderId === 'object'
            ? response.filterOptionsByHeaderId
            : {}
        };
        setAnalyticsResults(nextValue.results);
        setAnalyticsFilterOptionsByHeaderId(nextValue.filterOptionsByHeaderId);
        setBoundedSheetAnalyticsCacheEntry(analyticsCacheRef.current, cacheKey, nextValue);
      }).catch((error) => {
        if (active && requestSequence === analyticsRequestSequenceRef.current) {
          setAnalyticsError(error?.message || 'Unable to calculate analytics.');
        }
      }).finally(() => {
        if (active && requestSequence === analyticsRequestSequenceRef.current) setAnalyticsLoading(false);
      });
    });

    return () => {
      active = false;
      cancelCalculation();
    };
  }, [activeFilters, embeddedAnalyticsExpanded, embeddedAnalyticsPreferenceSheetId, hasAnalytics, isEmbedded, selectedRowIds, sheet?.analytics, sheet?.id, user?.uid]);
  const summaryRow = useMemo(
    () => resolvedHeaders.reduce((acc, header) => {
      const summaryType = `${header.summaryType || 'none'}`.trim();
      if (summaryType === 'none') {
        acc[header.id] = null;
        return acc;
      }

      const values = filteredRows
        .map((row) => row.resolvedValues?.[header.id] ?? row.values?.[header.id])
        .filter((value) => value !== '' && value !== null && value !== undefined);

      if (summaryType === 'count') {
        acc[header.id] = values.length;
        return acc;
      }

      const numericValues = values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      if (!numericValues.length) {
        acc[header.id] = null;
        return acc;
      }

      if (summaryType === 'sum') acc[header.id] = numericValues.reduce((total, value) => total + value, 0);
      if (summaryType === 'average') acc[header.id] = numericValues.reduce((total, value) => total + value, 0) / numericValues.length;
      if (summaryType === 'min') acc[header.id] = Math.min(...numericValues);
      if (summaryType === 'max') acc[header.id] = Math.max(...numericValues);
      return acc;
    }, {}),
    [filteredRows, resolvedHeaders]
  );
  const hasSummaryRow = useMemo(
    () => resolvedHeaders.some((header) => `${header.summaryType || 'none'}`.trim() !== 'none'),
    [resolvedHeaders]
  );
  const tableMetaColumnCount = 5 + ((isOwner && isOwnRowsMode) ? 1 : 0);
  const tableColSpan = sheetGridHeaders.length + tableMetaColumnCount;
  const activeRow = useMemo(
    () => resolvedRows.find((row) => row.id === activeRowId) || null,
    [activeRowId, resolvedRows]
  );
  const sheetRenderPreset = useMemo(
    () => normalizeSheetRenderPreset(sheet?.renderPreset),
    [sheet?.renderPreset]
  );
  const sheetRenderPresetConfig = useMemo(
    () => getSheetRenderPresetConfig(sheet?.renderPresetConfig),
    [sheet?.renderPresetConfig]
  );
  const embeddedPresetGridStyle = useMemo(
    () => (isEmbedded
      ? { gridTemplateColumns: `repeat(${sheetRenderPresetConfig.uiColumns}, minmax(0, 1fr))` }
      : undefined),
    [isEmbedded, sheetRenderPresetConfig.uiColumns]
  );
  const sheetRenderFontFamily = useMemo(
    () => getSheetRenderFontFamily(sheet?.renderFont),
    [sheet?.renderFont]
  );
  const presetProfileHeader = useMemo(() => {
    if (sheetRenderPreset !== 'profiles') return null;
    const configuredHeaderId = sheetRenderPresetConfig.profileHeaderId;
    if (configuredHeaderId) {
      return resolvedHeaders.find((header) => header.id === configuredHeaderId && header.type === 'profile') || null;
    }
    return resolvedHeaders.find((header) => header.type === 'profile') || null;
  }, [resolvedHeaders, sheetRenderPreset, sheetRenderPresetConfig.profileHeaderId]);
  const profilePresetHeaders = useMemo(() => {
    if (sheetRenderPreset !== 'profiles') {
      return {
        bodyHeader: null,
        actionHeader: null
      };
    }
    const findHeader = (headerId) => resolvedHeaders.find((header) => header.id === headerId) || null;
    return {
      bodyHeader: findHeader(sheetRenderPresetConfig.bodyHeaderId),
      actionHeader: findHeader(sheetRenderPresetConfig.actionHeaderId)
    };
  }, [
    resolvedHeaders,
    sheetRenderPreset,
    sheetRenderPresetConfig.actionHeaderId,
    sheetRenderPresetConfig.bodyHeaderId
  ]);
  const listCardPresetHeaders = useMemo(() => {
    if (sheetRenderPreset !== 'list-card') {
      return {
        imageHeader: null,
        categoryHeader: null,
        titleHeader: null,
        bodyHeader: null,
        tagHeader: null
      };
    }

    const findHeader = (headerId) => resolvedHeaders.find((header) => header.id === headerId) || null;
    const tagHeader = (() => {
      const configuredHeader = findHeader(sheetRenderPresetConfig.tagHeaderId);
      if (configuredHeader && (configuredHeader.type === 'list' || configuredHeader.type === 'multiselect')) {
        return configuredHeader;
      }
      return resolvedHeaders.find((header) => header.type === 'list' || header.type === 'multiselect') || null;
    })();

    return {
      imageHeader: findHeader(sheetRenderPresetConfig.imageHeaderId),
      categoryHeader: (() => {
        const configuredHeader = findHeader(sheetRenderPresetConfig.categoryHeaderId);
        if (configuredHeader && !['multiselect', 'list', 'image', 'textarea'].includes(configuredHeader.type)) {
          return configuredHeader;
        }
        return null;
      })(),
      titleHeader: findHeader(sheetRenderPresetConfig.titleHeaderId),
      bodyHeader: findHeader(sheetRenderPresetConfig.bodyHeaderId),
      tagHeader
    };
  }, [
    resolvedHeaders,
    sheetRenderPreset,
    sheetRenderPresetConfig.bodyHeaderId,
    sheetRenderPresetConfig.categoryHeaderId,
    sheetRenderPresetConfig.imageHeaderId,
    sheetRenderPresetConfig.tagHeaderId,
    sheetRenderPresetConfig.titleHeaderId
  ]);
  const kanbanPresetHeaders = useMemo(() => {
    if (sheetRenderPreset !== 'kanban') {
      return {
        statusHeader: null,
        titleHeader: null,
        bodyHeader: null,
        metaHeader: null
      };
    }
    const findHeader = (headerId) => resolvedHeaders.find((header) => header.id === headerId) || null;
    return {
      statusHeader: (() => {
        const configured = findHeader(sheetRenderPresetConfig.statusHeaderId);
        if (configured && ['select', 'buttons', 'yesno', 'rog', 'text'].includes(configured.type)) return configured;
        return resolvedHeaders.find((header) => ['select', 'buttons', 'yesno', 'rog', 'text'].includes(header.type)) || null;
      })(),
      titleHeader: findHeader(sheetRenderPresetConfig.titleHeaderId),
      bodyHeader: findHeader(sheetRenderPresetConfig.bodyHeaderId),
      metaHeader: findHeader(sheetRenderPresetConfig.metaHeaderId)
    };
  }, [
    resolvedHeaders,
    sheetRenderPreset,
    sheetRenderPresetConfig.bodyHeaderId,
    sheetRenderPresetConfig.metaHeaderId,
    sheetRenderPresetConfig.statusHeaderId,
    sheetRenderPresetConfig.titleHeaderId
  ]);
  const calendarPresetHeaders = useMemo(() => {
    if (sheetRenderPreset !== 'calendar') {
      return {
        imageHeader: null,
        dateHeader: null,
        titleHeader: null,
        metaHeader: null
      };
    }
    const findHeader = (headerId) => resolvedHeaders.find((header) => header.id === headerId) || null;
    return {
      imageHeader: findHeader(sheetRenderPresetConfig.imageHeaderId),
      dateHeader: (() => {
        const configured = findHeader(sheetRenderPresetConfig.dateHeaderId);
        if (configured && (configured.type === 'date' || configured.type === 'datetime')) return configured;
        return resolvedHeaders.find((header) => header.type === 'date' || header.type === 'datetime') || null;
      })(),
      titleHeader: findHeader(sheetRenderPresetConfig.titleHeaderId),
      metaHeader: findHeader(sheetRenderPresetConfig.metaHeaderId)
    };
  }, [
    resolvedHeaders,
    sheetRenderPresetConfig.imageHeaderId,
    sheetRenderPreset,
    sheetRenderPresetConfig.dateHeaderId,
    sheetRenderPresetConfig.metaHeaderId,
    sheetRenderPresetConfig.titleHeaderId
  ]);
  const usesProfilePresetLayout = Boolean(sheetRenderPreset === 'profiles' && presetProfileHeader);
  const usesListCardPresetLayout = Boolean(
    sheetRenderPreset === 'list-card' &&
    (listCardPresetHeaders.imageHeader || listCardPresetHeaders.categoryHeader || listCardPresetHeaders.titleHeader || listCardPresetHeaders.bodyHeader || listCardPresetHeaders.tagHeader)
  );
  const usesKanbanPresetLayout = Boolean(sheetRenderPreset === 'kanban' && kanbanPresetHeaders.statusHeader);
  const usesCalendarPresetLayout = Boolean(sheetRenderPreset === 'calendar' && calendarPresetHeaders.dateHeader);
  const usesCardPresetLayout = usesProfilePresetLayout || usesListCardPresetLayout;
  const usesPresetRenderLayout = usesCardPresetLayout || usesKanbanPresetLayout || usesCalendarPresetLayout;
  const activeRowNumber = useMemo(
    () => Math.max(1, filteredRows.findIndex((row) => row.id === activeRowId) + 1),
    [activeRowId, filteredRows]
  );
  const rowLabelHeader = useMemo(
    () => resolvedHeaders.find((header) => header.isRowLabel) || null,
    [resolvedHeaders]
  );
  const getRowReferenceLabel = useCallback((row, fallbackNumber) => {
    if (rowLabelHeader && row) {
      const formattedValue = getHeaderDisplayValueForRow(rowLabelHeader, row.resolvedValues || row.values || {}).trim();
      if (formattedValue) {
        return formattedValue;
      }
    }

    return `Row ${fallbackNumber}`;
  }, [rowLabelHeader]);
  const activeRowLabel = useMemo(
    () => (activeRow ? getRowReferenceLabel(activeRow, activeRowNumber) : `Row ${activeRowNumber}`),
    [activeRow, activeRowNumber, getRowReferenceLabel]
  );
  const selectedRows = useMemo(
    () => filteredRows.filter((row) => selectedRowIds.includes(row.id)),
    [filteredRows, selectedRowIds]
  );
  const chatMentionedRows = useMemo(
    () => resolvedRows.filter((row) => !row.isNew && chatMentionedRowIds.includes(row.id)),
    [chatMentionedRowIds, resolvedRows]
  );
  const reportTargetRowCount = selectedRows.length > 0 ? selectedRows.length : (activeRow ? 1 : 0);
  const defaultReportTargetType = useMemo(() => {
    if (selectedRows.length > 1) return 'selected-rows';
    if (activeRow) return 'active-row';
    return 'sheet';
  }, [activeRow, selectedRows.length]);
  const selectedReportColumn = useMemo(
    () => resolvedHeaders.find((header) => header.id === selectedReportColumnId) || null,
    [resolvedHeaders, selectedReportColumnId]
  );
  const reportTargetSummary = useMemo(() => {
    const visibleSavedRowCount = filteredRows.filter((row) => !row.isNew).length;
    if (reportTargetType === 'column') {
      return selectedReportColumn
        ? `Analyze ${selectedReportColumn.label} across the visible rows.`
        : 'Choose a column to analyze across the visible rows.';
    }
    if (reportTargetType === 'sheet') {
      return `Analyze the full visible sheet across ${visibleSavedRowCount} row${visibleSavedRowCount === 1 ? '' : 's'}.`;
    }
    if (reportTargetType === 'selected-rows') {
      return reportTargetRowCount > 0
        ? `Analyze ${reportTargetRowCount} selected row${reportTargetRowCount === 1 ? '' : 's'}.`
        : 'Select one or more rows to generate a multi-row report.';
    }
    return activeRow
      ? `Analyze ${activeRowLabel} with visible sheet context and logs.`
      : 'Open a row to generate a row-level report.';
  }, [activeRow, activeRowLabel, filteredRows, reportTargetRowCount, reportTargetType, selectedReportColumn]);
  const canEditSavedRow = useCallback((row) => {
    if (isReadOnlyTemplate || isAdminReadOnlyView) return false;
    if (isOwner) return true;
    if (isSharedSheetEditor) return Boolean(user?.uid);
    if (isOwnRowsMode || isViewAllEditOwn) {
      return Boolean(user?.uid && row?.createdBy === user.uid);
    }
    return false;
  }, [isAdminReadOnlyView, isOwnRowsMode, isOwner, isReadOnlyTemplate, isSharedSheetEditor, isViewAllEditOwn, user?.uid]);
  const getGridHeadersForRow = useCallback((row) => {
    const rowEditable = row ? (row.isNew ? canCreateRows : canEditSavedRow(row)) : false;
    if (!sheetEditMode) {
      return displayedTableHeaders;
    }
    return sheetGridHeaders.filter((header) => {
      if (tableHeaderIds.has(header.id)) return true;
      if (!rowEditable) return false;
      const columnAccess = normalizeColumnAccess(header?.columnAccess);
      return columnAccess === 'hidden' && canAccessDetailHeader(header);
    });
  }, [
    canAccessDetailHeader,
    canCreateRows,
    canEditSavedRow,
    displayedTableHeaders,
    sheetEditMode,
    sheetGridHeaders,
    tableHeaderIds
  ]);
  const visibleDetailHeaders = useMemo(() => {
    if (!activeRow) return [];
    const rowValues = activeRow.resolvedValues || activeRow.values || {};
    const canRevealHiddenColumns = activeRow.isNew ? canCreateRows : canEditSavedRow(activeRow);
    const detailHeaders = (canRevealHiddenColumns ? resolvedAllHeaders : resolvedHeaders)
      .filter((header) => canAccessDetailHeader(header));

    if (rowDetailMode === 'edit') {
      return detailHeaders;
    }
    return resolvedHeaders
      .filter((header) => canAccessDetailHeader(header))
      .filter((header) => !evaluateHeaderConditionalState(header, rowValues, resolvedAllHeaders).hidden)
      .filter((header) => !renderedSourceHeaderIds.has(header.id));
  }, [
    activeRow,
    canAccessDetailHeader,
    canCreateRows,
    canEditSavedRow,
    renderedSourceHeaderIds,
    resolvedAllHeaders,
    resolvedHeaders,
    rowDetailMode
  ]);
  const profilePresetExcludedHeaderIds = useMemo(() => new Set(
    [
      presetProfileHeader?.id,
      profilePresetHeaders.bodyHeader?.id,
      profilePresetHeaders.actionHeader?.id
    ].filter(Boolean)
  ), [
    presetProfileHeader?.id,
    profilePresetHeaders.actionHeader?.id,
    profilePresetHeaders.bodyHeader?.id
  ]);
  const profilePresetSidebarHeaders = useMemo(
    () => visibleDetailHeaders.filter((header) => !profilePresetExcludedHeaderIds.has(header.id)),
    [profilePresetExcludedHeaderIds, visibleDetailHeaders]
  );
  const listCardPresetExcludedHeaderIds = useMemo(() => new Set(
    [
      listCardPresetHeaders.imageHeader?.id,
      listCardPresetHeaders.categoryHeader?.id,
      listCardPresetHeaders.titleHeader?.id,
      listCardPresetHeaders.bodyHeader?.id,
      listCardPresetHeaders.tagHeader?.id
    ].filter(Boolean)
  ), [
    listCardPresetHeaders.bodyHeader?.id,
    listCardPresetHeaders.categoryHeader?.id,
    listCardPresetHeaders.imageHeader?.id,
    listCardPresetHeaders.tagHeader?.id,
    listCardPresetHeaders.titleHeader?.id
  ]);
  const listCardPresetSidebarHeaders = useMemo(
    () => visibleDetailHeaders.filter((header) => !listCardPresetExcludedHeaderIds.has(header.id)),
    [listCardPresetExcludedHeaderIds, visibleDetailHeaders]
  );
  const filteredLogs = useMemo(() => {
    if (!activeRow) return [];
    return logs.filter((item) => {
      if (logScopeType === 'sheet') return item.scopeType === 'sheet';
      if (logScopeType === 'column') return item.scopeType === 'column' && item.columnId === selectedLogColumnId;
      return item.scopeType === 'row' && item.rowId === activeRow.id;
    });
  }, [activeRow, logScopeType, logs, selectedLogColumnId]);
  const filteredCustomLogs = useMemo(() => {
    if (!sheet?.id || !customLogSheet?.id || !activeRow) return [];
    const scopeHeaderId = customLogSystemFieldMap.logScope;
    const sourceSheetIdHeaderId = customLogSystemFieldMap.sourceSheetId;
    const sourceRowIdHeaderId = customLogSystemFieldMap.sourceRowId;
    const sourceColumnIdHeaderId = customLogSystemFieldMap.sourceColumnId;

    return customLogRows.filter((row) => {
      const values = row.values || {};
      if (sourceSheetIdHeaderId && `${values[sourceSheetIdHeaderId] || ''}`.trim() !== sheet.id) return false;
      if (scopeHeaderId && `${values[scopeHeaderId] || ''}`.trim() !== logScopeType) return false;
      if (logScopeType === 'column') {
        return !sourceColumnIdHeaderId || `${values[sourceColumnIdHeaderId] || ''}`.trim() === selectedLogColumnId;
      }
      if (logScopeType === 'row') {
        return !sourceRowIdHeaderId || `${values[sourceRowIdHeaderId] || ''}`.trim() === activeRow.id;
      }
      return true;
    });
  }, [activeRow, customLogRows, customLogSheet?.id, customLogSystemFieldMap, logScopeType, selectedLogColumnId, sheet?.id]);
  const displayedLogs = customLogSheet?.id ? filteredCustomLogs : filteredLogs;
  const selectedSavedThread = useMemo(
    () => savedThreads.find((item) => item.id === selectedSavedThreadId) || null,
    [savedThreads, selectedSavedThreadId]
  );
  const activeChatMessages = chatMode === 'saved' ? savedThreadMessages : currentThreadMessages;
  const reportSkill = useMemo(
    () => normalizeReportSkillForViewer(sheet, resolvedHeaders),
    [resolvedHeaders, sheet]
  );
  const sheetAutomations = useMemo(
    () => normalizeSheetAutomationsForViewer(sheet, resolvedHeaders),
    [resolvedHeaders, sheet]
  );

  const renderLinkedSheetFieldInput = ({ header, draft, setDraft, optionRowsBySheetId, headers = [] }) => {
    const value = draft[header.id];
    const onChange = (nextValue) => setDraft((prev) => ({ ...prev, [header.id]: nextValue }));
    const conditionalState = evaluateHeaderConditionalState(header, draft, headers);
    const readOnly = conditionalState.readOnly;
    const inputType = getInputTypeForHeader(header.type);
    const inputMode = getInputModeForHeader(header.type);
    const step = header.type === 'currency' ? '0.01' : (header.type === 'number' || header.type === 'percent') ? 'any' : undefined;

    if (header.type === 'checkbox') {
      return (
        <label className="flex min-h-[44px] items-center rounded-xl border border-slate-200 bg-white px-3 py-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
          />
        </label>
      );
    }

    if (header.type === 'select') {
      const resolvedOptions = getResolvedChoiceOptions(header, optionRowsBySheetId, draft, headers);
      return (
        <select
          value={`${value ?? ''}`}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
        >
          <option value="">Select...</option>
          {resolvedOptions.map((option) => (
            <option key={`${header.id}_${option}`} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (header.type === 'buttons' || header.type === 'yesno' || header.type === 'rog') {
      const resolvedOptions = getResolvedChoiceOptions(header, optionRowsBySheetId, draft, headers);
      return (
        <div className={`flex flex-wrap gap-2 rounded-xl p-2 ${getRogOverlayClass(header, value)}`}>
          {resolvedOptions.map((option) => (
            <button
              key={`${header.id}_${option}`}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(option)}
              className={getChoiceButtonClasses({
                headerType: header.type,
                option,
                selectedValue: value,
                disabled: readOnly
              })}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    if (header.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      const resolvedOptions = resolveHeaderOptions(header, optionRowsBySheetId, draft, headers);
      return (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="space-y-2">
            {resolvedOptions.map((option) => (
              <label key={`${header.id}_${option}`} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  disabled={readOnly}
                  onChange={(e) => onChange(
                    e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((item) => item !== option)
                  )}
                  className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (header.type === 'profile') {
      const profile = getProfileDisplayData(header, draft);
      return (
        <div className="flex min-h-[44px] items-center">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
            {profile.imageUrl ? (
              <img
                src={profile.imageUrl}
                alt={profile.label || header.label || 'Profile'}
                className="h-9 w-9 rounded-full border border-slate-200 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-500">
                {(profile.label || header.label || '?').slice(0, 1)}
              </div>
            )}
            <span className="text-sm text-slate-700">{profile.label || 'Profile preview'}</span>
          </div>
        </div>
      );
    }

    if (header.type === 'rating') {
      return renderRatingStars(value, onChange, false);
    }

    if (header.type === 'textarea') {
      return (
        <textarea
          rows={5}
          value={value ?? ''}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          placeholder={header.label}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080] read-only:bg-slate-100 read-only:text-slate-500"
        />
      );
    }

    if (header.type === 'percent') {
      return (
        <div className="relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={header.label}
            inputMode={inputMode}
            step={step}
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-3 pr-8 text-sm text-slate-900 outline-none focus:border-[#008080]"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
      );
    }

    return (
      <input
        type={inputType}
        value={value ?? ''}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        placeholder={header.label}
        inputMode={inputMode}
        step={step}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080] read-only:bg-slate-100 read-only:text-slate-500"
      />
    );
  };
  const renderCustomLogFieldInput = (header) => renderLinkedSheetFieldInput({
    header,
    draft: customLogDraft,
    setDraft: setCustomLogDraft,
    optionRowsBySheetId: customLogOptionRowsBySheetId,
    headers: customLogHeaders
  });
  const renderActionFormFieldInput = (header) => renderLinkedSheetFieldInput({
    header,
    draft: actionFormDraft,
    setDraft: setActionFormDraft,
    optionRowsBySheetId: actionFormOptionRowsBySheetId,
    headers: actionFormHeaders
  });
  const renderInstantActionPromptFieldInput = (header) => renderLinkedSheetFieldInput({
    header,
    draft: instantActionPrompt?.draft || {},
    setDraft: (updater) => setInstantActionPrompt((prev) => {
      if (!prev) return prev;
      const nextDraft = typeof updater === 'function' ? updater(prev.draft || {}) : updater;
      return { ...prev, draft: nextDraft };
    }),
    optionRowsBySheetId: instantActionPrompt?.optionRowsBySheetId || {},
    headers: instantActionPrompt?.headers || []
  });

  const getCustomLogSummaryItems = useCallback((logRow) => customLogUserHeaders
    .map((header) => {
      const rawValue = logRow?.values?.[header.id];
      const value = Array.isArray(rawValue) ? rawValue.join(', ') : `${rawValue ?? ''}`.trim();
      return value ? { label: header.label, value } : null;
    })
    .filter(Boolean), [customLogUserHeaders]);

  useEffect(() => {
    setGeneratedReport('');
    setGeneratedReportTitle('');
    setReportInput(reportSkill.objective || reportSkill.title || '');
  }, [activeRow?.id, reportSkill.objective, reportSkill.title]);

  useEffect(() => {
    setReportTargetType((prev) => (REPORT_TARGET_TYPES.has(prev) ? prev : defaultReportTargetType));
  }, [defaultReportTargetType]);

  useEffect(() => {
    if (selectedReportColumnId && resolvedHeaders.some((header) => header.id === selectedReportColumnId)) return;
    setSelectedReportColumnId(resolvedHeaders[0]?.id || '');
  }, [resolvedHeaders, selectedReportColumnId]);

  const getUserDisplayName = () => userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User';

  const openProfileModal = () => {
    setIsUserMenuOpen(false);
    setIsProfileModalOpen(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error(error?.message || 'Failed to sign out.');
    }
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    setError(null);
    setSubscriptionRetryToken((prev) => prev + 1);
    loadSheetSnapshot({ preserveLocalRows: true, showSpinner: true });
  };

  const handleCellChange = (rowId, headerId, value) => {
    setRows((prev) => prev.map((row) => (
      row.id === rowId
        ? {
            ...row,
            values: { ...row.values, [headerId]: value },
            isDirty: true
          }
        : row
    )));
  };

  const handleListDraftChange = (rowId, headerId, value) => {
    setListDrafts((prev) => ({ ...prev, [`${rowId}_${headerId}`]: value }));
  };

  const handleAddListItem = (row, header) => {
    const controlKey = `${row.id}_${header.id}`;
    const nextItem = `${listDrafts[controlKey] || ''}`.trim();
    if (!nextItem) return;

    handleCellChange(row.id, header.id, [...normalizeListItems(row.values?.[header.id]), nextItem]);
    setListDrafts((prev) => ({ ...prev, [controlKey]: '' }));
  };

  const handleRemoveListItem = (row, header, itemIndex) => {
    handleCellChange(
      row.id,
      header.id,
      normalizeListItems(row.values?.[header.id]).filter((_, index) => index !== itemIndex)
    );
  };

  const handleImageUpload = async (row, header, file) => {
    if (!file || !row?.id || !header?.id) return;
    if (!assetUploadOwnerSegment) {
      toast.error('Sign in to upload images.');
      return;
    }

    const uploadKey = `${row.id}_${header.id}`;

    try {
      setUploadingImageKey(uploadKey);
      const safeName = `${Date.now()}_${file.name || 'image'}`.replace(/[^\w.-]+/g, '_');
      const imageRef = ref(storage, `sheets/${id}/${assetUploadOwnerSegment}/images/${safeName}`);
      const { url } = await uploadFileAndGetUrl(imageRef, file);
      handleCellChange(row.id, header.id, url);
      toast.success('Image uploaded.');
    } catch (error) {
      console.error('Image upload failed', error);
      toast.error(error?.message || 'Failed to upload image.');
    } finally {
      setUploadingImageKey((current) => (current === uploadKey ? '' : current));
    }
  };

  const handleDocumentUpload = async (row, header, file) => {
    if (!file || !row?.id || !header?.id) return;
    if (!assetUploadOwnerSegment) {
      toast.error('Sign in to upload documents.');
      return;
    }

    const uploadKey = `${row.id}_${header.id}`;

    try {
      setUploadingDocumentKey(uploadKey);
      const safeName = `${Date.now()}_${file.name || 'document'}`.replace(/[^\w.-]+/g, '_');
      const documentRef = ref(storage, `sheets/${id}/${assetUploadOwnerSegment}/documents/${safeName}`);
      const { url } = await uploadFileAndGetUrl(documentRef, file);
      handleCellChange(row.id, header.id, url);
      toast.success('Document uploaded.');
    } catch (error) {
      console.error('Document upload failed', error);
      toast.error(error?.message || 'Failed to upload document.');
    } finally {
      setUploadingDocumentKey((current) => (current === uploadKey ? '' : current));
    }
  };

  const handleOpenRowDetail = (rowId) => {
    const targetRow = resolvedRows.find((row) => row.id === rowId);
    setOpenRowActionMenuRowId('');
    setActiveActionFormId('');
    setSheetAiOpen(false);
    setActiveRowId(rowId);
    setRowDetailMode(targetRow?.isNew ? 'edit' : 'view');
    setOpenMultiSelectState(null);
  };

  const handleCloseRowDetail = () => {
    setOpenRowActionMenuRowId('');
    setSheetAiOpen(false);
    setActiveRowId(null);
    setRowDetailMode('view');
    setOpenMultiSelectState(null);
  };

  const handleOpenSheetAi = () => {
    setOpenRowActionMenuRowId('');
    setActiveActionFormId('');
    setActiveRowId(null);
    setRowDetailMode('view');
    setOpenMultiSelectState(null);
    setSidebarView('ai');
    setAiMode('report');
    setChatMode('current');
    setSheetAiOpen(true);
  };

  const handleOpenActionSidebar = () => {
    if (!linkedSheetActionForms.length) return;
    setOpenRowActionMenuRowId('');
    setActiveRowId(null);
    setRowDetailMode('view');
    setOpenMultiSelectState(null);
    setActiveActionFormId((prev) => prev || linkedSheetActionForms[0].id);
  };
  const handleOpenActionDetailsForRow = useCallback((rowId, formId = '') => {
    if (!rowId || !linkedSheetActionForms.length) return;
    const targetRow = resolvedRows.find((row) => row.id === rowId);
    setOpenRowActionMenuRowId('');
    setActiveRowId(rowId);
    setRowDetailMode(targetRow?.isNew ? 'edit' : 'view');
    setOpenMultiSelectState(null);
    setActiveActionFormId(formId || linkedSheetActionForms[0]?.id || '');
  }, [linkedSheetActionForms, resolvedRows]);

  const handleCloseActionSidebar = () => {
    setOpenRowActionMenuRowId('');
    setActiveActionFormId('');
    setActionFormDraft(buildActionFormDraftWithMappedValues());
  };
  const resolveRowActionComparisonValue = useCallback((config, sourceValues = {}) => {
    if (!config) return '';
    if (config.valueMode === 'sourceHeader') {
      return sourceValues?.[config.sourceHeaderId] ?? '';
    }
    return config.value ?? '';
  }, []);
  const doesRowMatchUpdateConditions = useCallback((rowValues = {}, conditions = [], sourceValues = {}) => (
    conditions.every((condition) => {
      const targetValue = rowValues?.[condition.targetHeaderId];
      const compareValue = resolveRowActionComparisonValue(condition, sourceValues);
      const normalizedTarget = Array.isArray(targetValue) ? targetValue.join(', ') : `${targetValue ?? ''}`;
      const normalizedCompare = Array.isArray(compareValue) ? compareValue.join(', ') : `${compareValue ?? ''}`;

      switch (condition.operator) {
        case 'notEquals':
          return normalizedTarget !== normalizedCompare;
        case 'contains':
          return normalizedTarget.toLowerCase().includes(normalizedCompare.toLowerCase());
        case 'isEmpty':
          return normalizedTarget.trim() === '';
        case 'isNotEmpty':
          return normalizedTarget.trim() !== '';
        case 'equals':
        default:
          return normalizedTarget === normalizedCompare;
      }
    })
  ), [resolveRowActionComparisonValue]);
  const isActionAvailableForRow = useCallback((row, actionForm) => {
    if (!row || !actionForm) return false;
    if (actionForm.mode !== 'update_rows') return true;

    const targetSheetId = actionForm.targetSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET
      ? sheet?.id
      : actionForm.targetSheetId;
    if (!targetSheetId) return false;
    if (targetSheetId !== sheet?.id) return true;

    const rowValues = row?.resolvedValues || row?.values || {};
    const conditions = Array.isArray(actionForm.conditions) ? actionForm.conditions : [];
    if (!conditions.length) return true;

    return doesRowMatchUpdateConditions(rowValues, conditions, rowValues);
  }, [doesRowMatchUpdateConditions, sheet?.id]);
  const getRowActionMenuItems = useCallback((rowId) => {
    const sourceRow = resolvedRows.find((row) => row.id === rowId);
    if (!sourceRow) return [];
    const rowSummary = getRowActiveActionSummary(rowId);
    return sheetActionForms
      .filter((actionForm) => isActionAvailableForRow(sourceRow, actionForm))
      .map((actionForm) => {
        const actionEntries = (rowSummary?.entries || []).filter((entry) => entry.formId === actionForm.id);
        return {
          id: actionForm.id,
          mode: actionForm.mode,
          label: actionForm.buttonLabel || actionForm.name || 'Action',
          description: actionEntries.length
            ? `${actionEntries.length} linked row${actionEntries.length === 1 ? '' : 's'} in ${actionEntries[0]?.sheetTitle || 'linked sheet'}`
            : (actionForm.description || (
              actionForm.mode === 'update_rows'
                ? 'Update matching rows immediately.'
                : (actionForm.linkedFormBehavior === 'instant_save'
                  ? 'Create a new linked row immediately.'
                  : '')
            ))
        };
      });
  }, [getRowActiveActionSummary, isActionAvailableForRow, resolvedRows, sheetActionForms]);
  const buildRowUpdatePatchValues = useCallback((updates = [], sourceValues = {}) => (
    updates.reduce((acc, update) => {
      acc[update.targetHeaderId] = resolveRowActionComparisonValue(update, sourceValues);
      return acc;
    }, {})
  ), [resolveRowActionComparisonValue]);
  const handleRunRowUpdateAction = useCallback(async (rowId, actionConfig) => {
    if (!sheet?.id || !rowId || !actionConfig || actionConfig.mode !== 'update_rows') return;

    const targetSheetId = actionConfig.targetSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET
      ? sheet.id
      : actionConfig.targetSheetId;
    if (!targetSheetId) {
      toast.error('Choose a target sheet for this action first.');
      return;
    }

    if (!actionConfig.updates?.length) {
      toast.error('Add at least one column update to this action.');
      return;
    }

    const activeSourceRow = resolvedRows.find((row) => row.id === rowId);
    if (!activeSourceRow) return;
    const sourceValues = activeSourceRow.resolvedValues || activeSourceRow.values || {};
    const confirmed = window.confirm(`Run "${actionConfig.buttonLabel || actionConfig.name || 'this action'}" now?`);
    if (!confirmed) return;

    try {
      const isCurrentSheetTarget = targetSheetId === sheet.id;
      const matchingRows = isCurrentSheetTarget
        ? [activeSourceRow]
        : normalizeRows(await sheetService.getSheetRows(targetSheetId, 500)).filter((targetRow) => (
            doesRowMatchUpdateConditions(targetRow.values || {}, actionConfig.conditions || [], sourceValues)
          ));

      if (!matchingRows.length) {
        toast.error('No rows matched this action.');
        return;
      }

      const patchValues = buildRowUpdatePatchValues(actionConfig.updates || [], sourceValues);
      for (const targetRow of matchingRows) {
        await sheetService.updateSheetRow(
          targetSheetId,
          targetRow.id,
          {
            values: { ...(targetRow.values || {}), ...patchValues },
            contributorId: targetRow.contributorId,
            storageMode: targetRow.storageMode
          },
          user?.uid
        );
      }

      if (targetSheetId === sheet.id) {
        await loadSheetSnapshot({ preserveLocalRows: false, showSpinner: false });
      }

      toast.success(`${matchingRows.length} row${matchingRows.length === 1 ? '' : 's'} updated.`);
    } catch (error) {
      toast.error(error?.message || 'Failed to run row update action.');
    }
  }, [buildRowUpdatePatchValues, doesRowMatchUpdateConditions, loadSheetSnapshot, resolvedRows, sheet?.id, user?.uid]);
  const handleSaveInstantLinkedRowAction = useCallback(async ({
    actionConfig,
    sourceRow,
    linkedSheet,
    linkedForm,
    linkedHeaders,
    draftValues
  }) => {
    const targetHeaders = Array.isArray(linkedHeaders) ? linkedHeaders : [];
    const systemFieldMap = targetHeaders.reduce((acc, header) => {
      const systemField = normalizeLogSystemField(header?.systemField);
      if (systemField && !acc[systemField]) {
        acc[systemField] = header.id;
      }
      return acc;
    }, {});
    const values = { ...(draftValues || {}) };

    if (systemFieldMap.sourceSheetId) values[systemFieldMap.sourceSheetId] = sheet?.id || '';
    if (systemFieldMap.sourceSheetTitle) values[systemFieldMap.sourceSheetTitle] = sheet?.title || '';
    if (systemFieldMap.sourceRowId) values[systemFieldMap.sourceRowId] = sourceRow.id;
    if (systemFieldMap.sourceRowLabel) {
      values[systemFieldMap.sourceRowLabel] = getRowReferenceLabel(
        sourceRow,
        Math.max(1, resolvedRows.findIndex((row) => row.id === sourceRow.id) + 1)
      );
    }
    if (systemFieldMap.loggedAt) values[systemFieldMap.loggedAt] = new Date().toISOString();
    if (systemFieldMap.loggedBy) values[systemFieldMap.loggedBy] = user?.uid || '';
    if (systemFieldMap.loggedByEmail) values[systemFieldMap.loggedByEmail] = user?.email || '';

    if (linkedForm?.id && normalizeActionSheetFormSubmissionMode(linkedForm?.submissionMode) === 'singleRow') {
      await sheetFormService.submitSheetForm(linkedForm.id, values, user);
    } else {
      await sheetService.saveSheetRow(actionConfig.targetSheetId, { values }, user?.uid);
    }

    const refreshedRows = normalizeRows(await sheetService.getSheetRows(actionConfig.targetSheetId, 500).catch(() => []));
    setActionFormActivityByFormId((prev) => (
      prev?.[actionConfig.id]
        ? {
            ...prev,
            [actionConfig.id]: {
              ...prev[actionConfig.id],
              sheet: linkedSheet || prev[actionConfig.id].sheet,
              rows: refreshedRows,
              headers: Array.isArray(linkedSheet?.headers) ? linkedSheet.headers : prev[actionConfig.id].headers,
              systemFieldMap: (Array.isArray(linkedSheet?.headers) ? linkedSheet.headers : prev[actionConfig.id].headers || []).reduce((acc, header) => {
                const systemField = normalizeLogSystemField(header?.systemField);
                if (systemField && !acc[systemField]) {
                  acc[systemField] = header.id;
                }
                return acc;
              }, {})
            }
          }
        : prev
    ));

    const targetSheetTitle = `${linkedSheet?.title || 'the target sheet'}`.trim() || 'the target sheet';
    toast.success(`Row added to ${targetSheetTitle}.`);
  }, [getRowReferenceLabel, resolvedRows, sheet?.id, sheet?.title, user]);
  const handleInvokeLinkedRowAction = useCallback(async (rowId, actionConfig) => {
    if (!rowId || !actionConfig || actionConfig.mode !== 'linked_form') return;

    if (normalizeActionLinkedFormBehavior(actionConfig.linkedFormBehavior) === 'instant_save') {
      const sourceRow = resolvedRows.find((row) => row.id === rowId);
      if (!sourceRow || !actionConfig.targetSheetId) return;

      try {
        const {
          linkedSheet,
          linkedForm,
          linkedHeaders,
          optionRowsBySheetId
        } = await loadLinkedActionContext(actionConfig);
        const baseDraft = buildLinkedActionDraft(linkedHeaders, linkedForm, sourceRow, actionConfig);
        const promptHeaders = getInstantActionPromptHeaders(linkedHeaders, baseDraft, actionConfig);

        if (promptHeaders.length) {
          setInstantActionPrompt({
            rowId,
            actionConfig,
            sourceRow,
            linkedSheet,
            linkedForm,
            linkedHeaders,
            draft: baseDraft,
            optionRowsBySheetId,
            saving: false
          });
          return;
        }

        await handleSaveInstantLinkedRowAction({
          actionConfig,
          sourceRow,
          linkedSheet,
          linkedForm,
          linkedHeaders,
          draftValues: baseDraft
        });
        return;
      } catch (error) {
        toast.error(error?.message || 'Failed to add the row.');
        return;
      }
    }

    if (actionConfig.allowTargetRowSelection === true && actionConfig.targetSheetId && sheet?.id) {
      try {
        const publishedForm = await sheetFormService.getSheetFormById(actionConfig.targetSheetId);
        if (publishedForm) {
          window.open(
            getActionSheetFormUrl(actionConfig.targetSheetId, {
              actionId: actionConfig.id,
              sourceSheetId: sheet.id
            }),
            '_blank',
            'noopener,noreferrer'
          );
          return;
        }
        toast.error('Publish the target sheet form to use target-row prefills.');
      } catch (error) {
        toast.error(error?.message || 'Unable to open the target form.');
      }
    }

    handleOpenActionDetailsForRow(rowId, actionConfig.id);
  }, [buildLinkedActionDraft, getInstantActionPromptHeaders, handleOpenActionDetailsForRow, handleSaveInstantLinkedRowAction, loadLinkedActionContext, resolvedRows, sheet?.id]);
  const handleSelectRowActionItem = useCallback(async (rowId, actionItem) => {
    if (!actionItem?.id) return;
    const actionConfig = sheetActionForms.find((candidate) => candidate.id === actionItem.id);
    setOpenRowActionMenuRowId('');
    if (!actionConfig) return;
    if (actionConfig.mode === 'update_rows') {
      handleRunRowUpdateAction(rowId, actionConfig);
      return;
    }

    handleInvokeLinkedRowAction(rowId, actionConfig);
  }, [handleInvokeLinkedRowAction, handleRunRowUpdateAction, sheetActionForms]);
  const handleRunConfiguredRowAction = useCallback((rowId, formId) => {
    if (!rowId || !formId) return;
    const actionConfig = sheetActionForms.find((candidate) => candidate.id === formId);
    if (!actionConfig) return;
    if (actionConfig.mode === 'update_rows') {
      handleRunRowUpdateAction(rowId, actionConfig);
      return;
    }
    handleInvokeLinkedRowAction(rowId, actionConfig);
  }, [handleInvokeLinkedRowAction, handleRunRowUpdateAction, sheetActionForms]);
  const instantActionPromptHeaders = useMemo(
    () => instantActionPrompt
      ? getInstantActionPromptHeaders(instantActionPrompt.linkedHeaders, instantActionPrompt.draft, instantActionPrompt.actionConfig)
      : [],
    [getInstantActionPromptHeaders, instantActionPrompt]
  );
  const handleSubmitInstantActionPrompt = useCallback(async () => {
    if (!instantActionPrompt) return;

    const missingHeader = instantActionPromptHeaders.find((header) => (
      header?.required === true && isActionFieldValueEmpty(header, instantActionPrompt.draft?.[header.id])
    ));
    if (missingHeader) {
      toast.error(`${missingHeader.label || 'A field'} is required.`);
      return;
    }

    setInstantActionPrompt((prev) => (prev ? { ...prev, saving: true } : prev));
    try {
      await handleSaveInstantLinkedRowAction({
        actionConfig: instantActionPrompt.actionConfig,
        sourceRow: instantActionPrompt.sourceRow,
        linkedSheet: instantActionPrompt.linkedSheet,
        linkedForm: instantActionPrompt.linkedForm,
        linkedHeaders: instantActionPrompt.linkedHeaders,
        draftValues: instantActionPrompt.draft
      });
      setInstantActionPrompt(null);
    } catch (error) {
      setInstantActionPrompt((prev) => (prev ? { ...prev, saving: false } : prev));
      toast.error(error?.message || 'Failed to add the row.');
    }
  }, [handleSaveInstantLinkedRowAction, instantActionPrompt, instantActionPromptHeaders]);

  const handleResizeStart = (event, headerId) => {
    if (!isOwner) return;
    event.preventDefault();
    const currentHeader = resolvedHeaders.find((header) => header.id === headerId);
    setActiveResize({
      headerId,
      startX: event.clientX,
      startWidth: Math.max(120, Number(currentHeader?.width) || DEFAULT_COLUMN_WIDTH)
    });
  };

  useEffect(() => {
    if (!activeResize || !isOwner) return undefined;

    const handleMouseMove = (event) => {
      const nextWidth = Math.max(120, activeResize.startWidth + (event.clientX - activeResize.startX));
      setSheet((prev) => (
        prev
          ? {
              ...prev,
              headers: (prev.headers || []).map((header) => (
                header.id === activeResize.headerId ? { ...header, width: nextWidth } : header
              ))
            }
          : prev
      ));
    };

    const handleMouseUp = async () => {
      const nextHeaders = (sheet?.headers || []).map((header) => ({
        ...header,
        width: Math.max(120, Number(header.width) || DEFAULT_COLUMN_WIDTH)
      }));

      setActiveResize(null);

      if (!sheet?.id) return;

      try {
        await sheetService.updateSheet(sheet.id, { headers: nextHeaders });
        await sheetService.publishSheet(sheet.id, {
          ...sheet,
          headers: nextHeaders
        }, user?.uid);
      } catch (err) {
        toast.error(err?.message || 'Failed to save column width.');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeResize, isOwner, sheet, user?.uid]);

  useEffect(() => {
    setActiveFilters((prev) => {
      const next = Object.fromEntries(
        filterableHeaderIds.map((headerId) => [headerId, prev[headerId] || '']).filter(([, value]) => value)
      );
      const sameKeys = Object.keys(prev).length === Object.keys(next).length
        && Object.keys(prev).every((key) => prev[key] === next[key]);
      return sameKeys ? prev : next;
    });
  }, [filterableHeaderIds]);

  useEffect(() => {
    if (!filterHeaders.length) {
      setShowFilters(false);
    }
  }, [filterHeaders.length]);

  useEffect(() => {
    if (isEmbedded) setShowFilters(false);
  }, [isEmbedded, sheet?.id]);

  useEffect(() => {
    const visibleRowIds = new Set(filteredRows.map((row) => row.id));
    setSelectedRowIds((prev) => {
      const next = prev.filter((rowId) => visibleRowIds.has(rowId));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredRows]);

  useEffect(() => {
    hasAutoOpenedRowRef.current = false;
    setActiveRowId(null);
  }, [id]);

  useEffect(() => {
    if (isEmbedded || hasAutoOpenedRowRef.current || activeRowId || !resolvedRows.length) return;
    hasAutoOpenedRowRef.current = true;
    setActiveRowId(resolvedRows[0].id);
  }, [activeRowId, isEmbedded, resolvedRows]);

  useEffect(() => {
    if (!activeRowId) return;
    if (!resolvedRows.some((row) => row.id === activeRowId)) {
      setActiveRowId(null);
    }
  }, [activeRowId, resolvedRows]);

  useEffect(() => {
    if (!resolvedHeaders.length) {
      setSelectedLogColumnId('');
      return;
    }
    setSelectedLogColumnId((prev) => (
      resolvedHeaders.some((header) => header.id === prev) ? prev : resolvedHeaders[0].id
    ));
  }, [resolvedHeaders]);

  useEffect(() => {
    const linkedLogSheetId = `${sheet?.customLogSheetId || ''}`.trim();
    if (!linkedLogSheetId) {
      setCustomLogSheet(null);
      setCustomLogRows([]);
      return;
    }

    let isActive = true;

    const loadCustomLogSheet = async () => {
      try {
        const [linkedSheet, linkedRows] = await Promise.all([
          sheetService.getSheetById(linkedLogSheetId),
          sheetService.getSheetRows(linkedLogSheetId, 500)
        ]);
        if (!isActive) return;
        setCustomLogSheet(linkedSheet);
        setCustomLogRows(normalizeRows(linkedRows));
      } catch (err) {
        if (!isActive) return;
        setCustomLogSheet(null);
        setCustomLogRows([]);
        toast.error(err?.message || 'Failed to load custom log sheet.');
      }
    };

    loadCustomLogSheet();

    return () => {
      isActive = false;
    };
  }, [sheet?.customLogSheetId]);

  useEffect(() => {
    const linkedActionSheetId = `${activeActionFormConfig?.targetSheetId || ''}`.trim();
    if (!linkedActionSheetId) {
      setActionFormSheet(null);
      setActionSheetForm(null);
      setActionFormRows([]);
      setActionFormOptionRowsBySheetId({});
      return;
    }

    let isActive = true;

    const loadActionFormSheet = async () => {
      try {
        const [linkedSheet, linkedRows, linkedForm] = await Promise.all([
          sheetService.getSheetById(linkedActionSheetId),
          sheetService.getSheetRows(linkedActionSheetId, 500),
          sheetFormService.getSheetFormById(linkedActionSheetId).catch(() => null)
        ]);
        if (!isActive) return;
        setActionFormSheet(linkedSheet);
        setActionSheetForm(linkedForm);
        setActionFormRows(normalizeRows(linkedRows));
      } catch (err) {
        if (!isActive) return;
        setActionFormSheet(null);
        setActionSheetForm(null);
        setActionFormRows([]);
        setActionFormOptionRowsBySheetId({});
        toast.error(err?.message || 'Failed to load action form sheet.');
      }
    };

    loadActionFormSheet();

    return () => {
      isActive = false;
    };
  }, [activeActionFormConfig?.targetSheetId]);
  useEffect(() => {
    let isActive = true;

    const loadSheetFormSummary = async () => {
      const targetSheetId = `${sheet?.id || ''}`.trim();
      if (!targetSheetId) {
        setSheetFormSummary(null);
        return;
      }

      try {
        const form = await sheetFormService.getSheetFormById(targetSheetId);
        if (!isActive) return;
        setSheetFormSummary(form);
      } catch (error) {
        if (!isActive) return;
        setSheetFormSummary(null);
      }
    };

    loadSheetFormSummary();

    return () => {
      isActive = false;
    };
  }, [sheet?.id]);

  useEffect(() => {
    if (!linkedSheetActionForms.length) {
      setActionFormActivityByFormId({});
      return;
    }

    let isActive = true;

    const loadActionActivity = async () => {
      const results = await Promise.allSettled(
        linkedSheetActionForms.map(async (actionForm) => {
          const linkedSheet = await sheetService.getSheetById(actionForm.targetSheetId);
          const linkedRows = await sheetService.getSheetRows(actionForm.targetSheetId, 500);
          const headers = Array.isArray(linkedSheet?.headers) ? linkedSheet.headers : [];
          const systemFieldMap = headers.reduce((acc, header) => {
            const systemField = normalizeLogSystemField(header?.systemField);
            if (systemField && !acc[systemField]) {
              acc[systemField] = header.id;
            }
            return acc;
          }, {});
          return [
            actionForm.id,
            {
              actionForm,
              sheet: linkedSheet,
              rows: normalizeRows(linkedRows),
              headers,
              systemFieldMap
            }
          ];
        })
      );

      if (!isActive) return;

      const nextActivity = results.reduce((acc, result) => {
        if (result.status !== 'fulfilled') return acc;
        const [formId, formData] = result.value;
        acc[formId] = formData;
        return acc;
      }, {});

      setActionFormActivityByFormId(nextActivity);
    };

    loadActionActivity();

    return () => {
      isActive = false;
    };
  }, [linkedSheetActionForms]);

  useEffect(() => {
    setCustomLogDraft(createCustomLogDraft(customLogHeaders));
  }, [customLogHeaders]);

  useEffect(() => {
    const baseDraft = usesCustomActionSheetForm && actionSheetForm
      ? (() => {
          const initialValues = sheetFormService.createInitialValues(actionSheetForm);
          return initialValues && typeof initialValues === 'object' && !Array.isArray(initialValues) ? initialValues : {};
        })()
      : createActionFormDraft(actionFormHeaders);

    if (activeRow?.id && activeActionFormConfig?.fieldMappings?.length) {
      const sourceValues = activeRow?.resolvedValues || activeRow?.values || {};
      activeActionFormConfig.fieldMappings.forEach((fieldMapping) => {
        if (!fieldMapping?.targetHeaderId) return;
        baseDraft[fieldMapping.targetHeaderId] = resolveActionFieldMappingValue(fieldMapping, sourceValues);
      });
    }

    setActionFormDraft(baseDraft);
  }, [actionFormHeaders, actionSheetForm, activeActionFormConfig, activeRow, usesCustomActionSheetForm]);

  useEffect(() => {
    if (!activeActionFormId) return;
    if (linkedSheetActionForms.some((actionForm) => actionForm.id === activeActionFormId)) return;
    setActiveActionFormId(linkedSheetActionForms[0]?.id || '');
  }, [activeActionFormId, linkedSheetActionForms]);

  useEffect(() => {
    const referencedSheetIds = getReferencedExternalSheetIds(actionFormHeaders, actionFormSheet?.id || '');
    if (!referencedSheetIds.length) {
      setActionFormOptionRowsBySheetId(actionFormSheet?.id ? {
        [actionFormSheet.id]: { rows: actionFormRows, headers: actionFormSheet?.headers || [] }
      } : {});
      return;
    }

    let isActive = true;

    const loadActionOptionRows = async () => {
      const results = await Promise.allSettled(
        referencedSheetIds.map(async (sheetId) => {
          const sourceSheet = await sheetService.getSheetById(sheetId);
          const rows = await sheetService.getSheetRows(sheetId, 500);
          return [sheetId, { rows, headers: sourceSheet?.headers || [] }];
        })
      );

      if (!isActive) return;

      const nextRowsBySheetId = results.reduce((acc, result) => {
        if (result.status !== 'fulfilled') return acc;
        const [sheetId, sourceData] = result.value;
        acc[sheetId] = sourceData;
        return acc;
      }, actionFormSheet?.id ? {
        [actionFormSheet.id]: { rows: actionFormRows, headers: actionFormSheet?.headers || [] }
      } : {});

      setActionFormOptionRowsBySheetId(nextRowsBySheetId);
    };

    loadActionOptionRows();

    return () => {
      isActive = false;
    };
  }, [actionFormHeaders, actionFormRows, actionFormSheet]);

  useEffect(() => {
    if (!activeRowId) return;
    setSidebarView('details');
    setLogMode('saved');
    setLogScopeType('row');
    setLogDraft(createInitialLogDraft());
    setCustomLogDraft(createCustomLogDraft(customLogHeaders));
    setChatMode('current');
    setCurrentThreadMessages([]);
    setSavedThreadMessages([]);
    setSelectedSavedThreadId(null);
    setChatInput('');
  }, [activeRowId, customLogHeaders]);

  useEffect(() => {
    if (customLogSheet?.id) {
      setLogs([]);
      return undefined;
    }
    if (!sheet?.id || !activeRow) {
      setLogs([]);
      return undefined;
    }
    if (logScopeType === 'row' && activeRow.isNew) {
      setLogs([]);
      return undefined;
    }

    return sheetService.subscribeToSheetLogs(
      sheet.id,
      logScopeType === 'row'
        ? {
            maxResults: 100,
            scopeType: 'row',
            rowData: activeRow
          }
        : {
            maxResults: 100,
            scopeType: logScopeType
          },
      setLogs,
      (err) => toast.error(err?.message || 'Failed to load logs.')
    );
  }, [activeRow, customLogSheet?.id, logScopeType, sheet?.id]);

  useEffect(() => {
    if (!sheet?.id || !activeRow) {
      setSavedThreads([]);
      return undefined;
    }
    if (activeRow.isNew) {
      setSavedThreads([]);
      return undefined;
    }

    return sheetService.subscribeToSheetAiThreads(
      sheet.id,
      { rowData: activeRow, maxResults: 50 },
      setSavedThreads,
      (err) => toast.error(err?.message || 'Failed to load saved chats.')
    );
  }, [activeRow, sheet?.id]);

  useEffect(() => {
    if (!selectedSavedThreadId) {
      setSavedThreadMessages([]);
      return undefined;
    }
    if (!activeRow || activeRow.isNew) {
      setSavedThreadMessages([]);
      return undefined;
    }

    return sheetService.subscribeToSheetAiMessages(
      sheet.id,
      activeRow,
      selectedSavedThreadId,
      setSavedThreadMessages,
      (err) => toast.error(err?.message || 'Failed to load chat messages.')
    );
  }, [activeRow, selectedSavedThreadId, sheet?.id]);

  useEffect(() => {
    if (chatMode !== 'saved') return;
    if (!savedThreads.length) {
      setSelectedSavedThreadId(null);
      return;
    }
    setSelectedSavedThreadId((prev) => (
      savedThreads.some((item) => item.id === prev) ? prev : savedThreads[0].id
    ));
  }, [chatMode, savedThreads]);

  useEffect(() => {
    const scrollContainer = gridScrollRef.current;
    if (!scrollContainer) return undefined;

    const handleScroll = () => {
      setOpenMultiSelectState(null);
      setOpenRowActionMenuRowId('');
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const scrollContainer = gridScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollLeft = 0;
    scrollContainer.scrollTop = 0;
  }, [id, resolvedAllHeaders.length]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-multiselect-root="true"]')) {
        setOpenMultiSelectState(null);
      }
      if (!event.target.closest('[data-row-action-menu-root="true"]')) {
        setOpenRowActionMenuRowId('');
      }
    };

    const handleResize = () => {
      setOpenMultiSelectState(null);
      setOpenRowActionMenuRowId('');
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleAddRow = () => {
    if (!canCreateRows) return;
    const nextDraftRow = createDraftRow(resolvedAllHeaders);
    setRows((prev) => [...prev, nextDraftRow]);
    setActiveRowId(nextDraftRow.id);
    setRowDetailMode('edit');
    setSidebarView('details');
    setOpenMultiSelectState(null);
  };

  const handleToggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) => (
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    ));
  };

  const handleToggleAllRows = () => {
    setSelectedRowIds((prev) => (
      filteredRows.length > 0 && filteredRows.every((row) => prev.includes(row.id))
        ? prev.filter((rowId) => !filteredRows.some((row) => row.id === rowId))
        : Array.from(new Set([...prev, ...filteredRows.map((row) => row.id)]))
    ));
  };

  const handleFilterChange = (headerId, value) => {
    setActiveFilters((prev) => {
      const nextValue = `${value ?? ''}`;
      if (!nextValue.trim()) {
        const next = { ...prev };
        delete next[headerId];
        return next;
      }
      return { ...prev, [headerId]: nextValue };
    });
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleRefreshSheet = async () => {
    setRefreshing(true);
    try {
      await loadSheetSnapshot({ preserveLocalRows: true, showSpinner: false });
    } catch (err) {
      handleRealtimeError(err);
    } finally {
      setSubscriptionRetryToken((prev) => prev + 1);
      setRefreshing(false);
    }
  };

  const handleDuplicateRow = (row) => {
    if (!canCreateRows) return;
    setRows((prev) => [
      ...prev,
      {
        ...createDraftRow(resolvedAllHeaders),
        values: { ...row.values }
      }
    ]);
    toast.success('Row duplicated as a new draft.');
  };

  const handleRemoveRow = async (row) => {
    if (row.isNew) {
      setSelectedRowIds((prev) => prev.filter((id) => id !== row.id));
      setRows((prev) => {
        const next = prev.filter((item) => item.id !== row.id);
        return next.length ? next : createDraftRows(sheet, user, resolvedAllHeaders);
      });
      return;
    }

    if (!canEditSavedRow(row)) return;

    const confirmed = window.confirm('Delete this row? This cannot be undone.');
    if (!confirmed) return;

    try {
      await sheetService.deleteSheetRow(sheet.id, row.id, {
        contributorId: row.contributorId,
        storageMode: row.storageMode
      });
      setSelectedRowIds((prev) => prev.filter((id) => id !== row.id));
      setRows((prev) => {
        const next = prev.filter((item) => item.id !== row.id);
        return next.some((item) => item.isNew) ? next : [...createDraftRows(sheet, user, resolvedAllHeaders, next), ...next];
      });
      toast.success('Row deleted.');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete row.');
    }
  };

  const handleSaveRows = async () => {
    if (!sheet) return false;

    setSaving(true);
    try {
      const rowsToCreate = resolvedRows.filter((row) =>
        row.isNew && canCreateRows && resolvedHeaders.some((header) => `${row.resolvedValues?.[header.id] || ''}`.trim())
      );
      const rowsToUpdate = resolvedRows.filter((row) => row.isDirty && !row.isNew && canEditSavedRow(row));

      if (!rowsToCreate.length && !rowsToUpdate.length) {
        throw new Error('There are no row changes to save.');
      }

      const createdRows = [];

      for (const row of rowsToUpdate) {
        await sheetService.updateSheetRow(
          sheet.id,
          row.id,
          { values: row.resolvedValues, contributorId: row.contributorId, storageMode: row.storageMode },
          user?.uid
        );
      }

      for (const row of rowsToCreate) {
        const created = await sheetService.saveSheetRow(sheet.id, { values: row.resolvedValues }, user?.uid);
        createdRows.push(created);
      }

      const refreshedRows = await sheetService.getSheetRows(sheet.id);
      setSelectedRowIds([]);
      setRows([
        ...createDraftRows(sheet, user, resolvedAllHeaders, refreshedRows),
        ...normalizeRows(refreshedRows)
      ]);

      const totalSaved = rowsToUpdate.length + createdRows.length;
      toast.success(`${totalSaved} row${totalSaved === 1 ? '' : 's'} saved.`);
      return true;
    } catch (err) {
      toast.error(err?.message || 'Failed to save rows.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCsvTemplate = useCallback(() => {
    if (!csvColumns.length) {
      toast.error('Add at least one importable column before downloading a CSV template.');
      return;
    }

    const template = buildSheetCsvTemplate(headers);
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = `${sheet?.title || 'sheet'}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'sheet';

    link.href = url;
    link.download = `${safeTitle}-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('CSV template downloaded.');
  }, [csvColumns.length, headers, sheet?.title]);

  const handleOpenCsvImport = useCallback(() => {
    if (!canCreateRows) return;
    csvImportInputRef.current?.click();
  }, [canCreateRows]);

  const handleImportCsv = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !sheet?.id) return;

    try {
      const pendingRowChanges = resolvedRows.some((row) => {
        if (row.isNew) {
          return canCreateRows && resolvedHeaders.some((header) => `${row.resolvedValues?.[header.id] || ''}`.trim());
        }
        return row.isDirty && canEditSavedRow(row);
      });

      if (!canCreateRows) {
        throw new Error('You do not have permission to import rows into this sheet.');
      }
      if (pendingRowChanges) {
        throw new Error('Save or remove your current draft row changes before importing CSV data.');
      }

      const text = await file.text();
      const result = parseSheetCsvImport(text, headers);

      if (!result.rows.length) {
        throw new Error('No non-empty rows were found in the CSV file.');
      }

      setImportingCsv(true);
      for (const row of result.rows) {
        await sheetService.saveSheetRow(sheet.id, row, user?.uid);
      }

      await loadSheetSnapshot({ preserveLocalRows: false, showSpinner: false });
      setCsvImportSummary({
        fileName: file.name,
        importedCount: result.rows.length,
        ignoredColumns: result.ignoredColumns,
        missingColumns: result.missingColumns,
        skippedRowCount: result.skippedRowCount
      });

      toast.success(`Imported ${result.rows.length} row${result.rows.length === 1 ? '' : 's'} from ${file.name}.`);
    } catch (error) {
      toast.error(error?.message || 'Failed to import the CSV file.');
    } finally {
      setImportingCsv(false);
    }
  }, [canCreateRows, canEditSavedRow, headers, loadSheetSnapshot, resolvedHeaders, resolvedRows, sheet?.id, user?.uid]);

  const handleSaveAndReturnToView = async () => {
    const didSave = await handleSaveRows();
    if (didSave) {
      setRowDetailMode('view');
    }
  };

  const handleSaveLog = async () => {
    if (!sheet || !activeRow) return;
    if (logScopeType === 'row' && activeRow.isNew) {
      toast.error('Save the row first before adding row logs.');
      return;
    }

    const targetLabel = logScopeType === 'sheet'
      ? sheet.title
      : logScopeType === 'column'
        ? (resolvedHeaders.find((header) => header.id === selectedLogColumnId)?.label || 'Column')
        : activeRowLabel;

    setLogSaving(true);
    try {
      if (customLogSheet?.id) {
        if (!hasRequiredCustomLogSystemFields) {
          throw new Error('The custom log sheet must map logScope, sourceSheetId, sourceRowId, and sourceColumnId system fields.');
        }
        const values = customLogHeaders.reduce((acc, header) => {
          switch (normalizeLogSystemField(header.systemField)) {
            case 'logScope':
              acc[header.id] = logScopeType;
              break;
            case 'sourceSheetId':
              acc[header.id] = sheet.id;
              break;
            case 'sourceSheetTitle':
              acc[header.id] = sheet.title || '';
              break;
            case 'sourceRowId':
              acc[header.id] = logScopeType === 'row' ? activeRow.id : '';
              break;
            case 'sourceColumnId':
              acc[header.id] = logScopeType === 'column' ? selectedLogColumnId : '';
              break;
            case 'sourceRowLabel':
              acc[header.id] = logScopeType === 'row' ? activeRowLabel : '';
              break;
            case 'sourceColumnLabel':
              acc[header.id] = logScopeType === 'column'
                ? (resolvedHeaders.find((headerItem) => headerItem.id === selectedLogColumnId)?.label || '')
                : '';
              break;
            case 'loggedAt':
              acc[header.id] = new Date().toISOString();
              break;
            case 'loggedBy':
              acc[header.id] = user?.uid || '';
              break;
            case 'loggedByEmail':
              acc[header.id] = user?.email || '';
              break;
            default:
              acc[header.id] = customLogDraft[header.id];
              break;
          }
          return acc;
        }, {});

        await sheetService.saveSheetRow(customLogSheet.id, { values }, user?.uid);
        const refreshedLogRows = await sheetService.getSheetRows(customLogSheet.id, 500);
        setCustomLogRows(normalizeRows(refreshedLogRows));
        setCustomLogDraft(createCustomLogDraft(customLogHeaders));
        setLogMode('saved');
        toast.success('Log saved.');
        return;
      }

      await sheetService.saveSheetLog(sheet.id, {
        ...logDraft,
        scopeType: logScopeType,
        rowId: logScopeType === 'row' ? activeRow.id : undefined,
        contributorId: logScopeType === 'row' ? activeRow.contributorId : undefined,
        storageMode: logScopeType === 'row' ? activeRow.storageMode : undefined,
        columnId: logScopeType === 'column' ? selectedLogColumnId : undefined,
        targetLabel
      }, user?.uid);
      setLogDraft(createInitialLogDraft());
      setLogMode('saved');
      toast.success('Log saved.');
    } catch (err) {
      toast.error(err?.message || 'Failed to save log.');
    } finally {
      setLogSaving(false);
    }
  };

  const handleSubmitActionForm = async () => {
    if (!activeActionFormConfig || !actionFormSheet?.id || !activeRow) return;
    if (!visibleActionFormUserHeaders.length) {
      toast.error('Add at least one user field to the linked action sheet.');
      return;
    }

    setActionFormSaving(true);
    try {
      const actionSystemFieldMap = actionFormHeaders.reduce((acc, header) => {
        const systemField = normalizeLogSystemField(header.systemField);
        if (systemField && !acc[systemField]) {
          acc[systemField] = header.id;
        }
        return acc;
      }, {});
      const values = { ...actionFormDraft };
      if (actionSystemFieldMap.sourceSheetId) values[actionSystemFieldMap.sourceSheetId] = sheet?.id || '';
      if (actionSystemFieldMap.sourceSheetTitle) values[actionSystemFieldMap.sourceSheetTitle] = sheet?.title || '';
      if (actionSystemFieldMap.sourceRowId) values[actionSystemFieldMap.sourceRowId] = activeRow.id;
      if (actionSystemFieldMap.sourceRowLabel) values[actionSystemFieldMap.sourceRowLabel] = activeRowLabel;
      if (actionSystemFieldMap.loggedAt) values[actionSystemFieldMap.loggedAt] = new Date().toISOString();
      if (actionSystemFieldMap.loggedBy) values[actionSystemFieldMap.loggedBy] = user?.uid || '';
      if (actionSystemFieldMap.loggedByEmail) values[actionSystemFieldMap.loggedByEmail] = user?.email || '';

      if (usesCustomActionSheetForm && actionSheetForm?.id) {
        await sheetFormService.submitSheetForm(actionSheetForm.id, values, user);
      } else {
        await sheetService.saveSheetRow(actionFormSheet.id, { values }, user?.uid);
      }
      const refreshedRows = await sheetService.getSheetRows(actionFormSheet.id, 500);
      setActionFormRows(normalizeRows(refreshedRows));
      setActionFormDraft(buildActionFormDraftWithMappedValues());
      toast.success(`${activeActionFormConfig.name || activeActionFormConfig.buttonLabel || 'Action'} submitted.`);
    } catch (err) {
      toast.error(err?.message || 'Failed to submit action form.');
    } finally {
      setActionFormSaving(false);
    }
  };

  const buildSheetChatContext = (messages) => ({
    sheetTitle: sheet?.title || 'Untitled Sheet',
    sheetDescription: sheet?.description || '',
    headers: resolvedHeaders.map((header) => ({
      id: header.id,
      label: header.label,
      type: header.type
    })),
    activeRow: null,
    sheetRowCount: resolvedRows.filter((row) => !row.isNew).length,
    visibleRows: resolvedRows
      .filter((row) => !row.isNew)
      .map((row) => ({
        id: row.id,
        label: getRowReferenceLabel(row, resolvedRows.findIndex((item) => item.id === row.id) + 1),
        values: resolvedHeaders.reduce((acc, header) => {
          acc[header.id] = row.resolvedValues?.[header.id] ?? row.values?.[header.id] ?? '';
          return acc;
        }, {})
      })),
    mentionedRows: chatMentionedRows.map((row) => ({
      id: row.id,
      label: getRowReferenceLabel(row, resolvedRows.findIndex((item) => item.id === row.id) + 1),
      values: resolvedHeaders.reduce((acc, header) => {
        acc[header.id] = row.resolvedValues?.[header.id] ?? row.values?.[header.id] ?? '';
        return acc;
      }, {})
    })),
    messages: messages.map((item) => ({
      role: item.role,
      content: item.content
    }))
  });

  const buildReportLogsContext = useCallback((options = {}) => {
    if (!sheet || !activeRow) return [];
    const includeRelatedLogs = options.includeRelatedLogs !== false;
    const includeSheetLogs = Boolean(options.includeSheetLogs);
    const maxLogs = Math.max(1, Math.min(50, Number(options.maxLogs) || reportSkill.maxLogs));
    if (!includeRelatedLogs && !includeSheetLogs) return [];

    const relatedLogItems = customLogSheet?.id
      ? filteredCustomLogs.map((log) => ({
          id: log.id,
          scopeType: `${log.values?.[customLogSystemFieldMap.logScope] || logScopeType || 'row'}`.trim() || 'row',
          targetLabel: (
            log.values?.[customLogSystemFieldMap.sourceColumnLabel]
            || log.values?.[customLogSystemFieldMap.sourceRowLabel]
            || sheet.title
          ),
          createdAt: log.values?.[customLogSystemFieldMap.loggedAt] || log.createdAt || null,
          details: getCustomLogSummaryItems(log)
        }))
      : filteredLogs.map((log) => ({
          id: log.id,
          scopeType: log.scopeType || 'row',
          targetLabel: log.targetLabel || '',
          createdAt: log.updatedAt || log.createdAt || log.date || null,
          note: log.note || '',
          status: log.status || '',
          progress: log.progress,
          action: log.action || ''
        }));

    const sheetWideLogItems = reportSkill.includeSheetLogs
      ? (customLogSheet?.id
          ? customLogRows
            .filter((log) => {
              const sourceSheetId = customLogSystemFieldMap.sourceSheetId
                ? `${log.values?.[customLogSystemFieldMap.sourceSheetId] || ''}`.trim()
                : sheet.id;
              const scopeValue = customLogSystemFieldMap.logScope
                ? `${log.values?.[customLogSystemFieldMap.logScope] || ''}`.trim()
                : '';
              return sourceSheetId === sheet.id && scopeValue === 'sheet';
            })
            .map((log) => ({
              id: log.id,
              scopeType: 'sheet',
              targetLabel: sheet.title,
              createdAt: log.values?.[customLogSystemFieldMap.loggedAt] || log.createdAt || null,
              details: getCustomLogSummaryItems(log)
            }))
          : logs
            .filter((log) => log.scopeType === 'sheet')
            .map((log) => ({
              id: log.id,
              scopeType: 'sheet',
              targetLabel: log.targetLabel || sheet.title,
              createdAt: log.updatedAt || log.createdAt || log.date || null,
              note: log.note || '',
              status: log.status || '',
              progress: log.progress,
            action: log.action || ''
            })))
      : [];

    const scopedRelatedLogItems = includeRelatedLogs ? relatedLogItems : [];
    return [...scopedRelatedLogItems, ...sheetWideLogItems].slice(0, maxLogs);
  }, [
    activeRow,
    customLogSheet?.id,
    customLogRows,
    customLogSystemFieldMap,
    filteredCustomLogs,
    filteredLogs,
    getCustomLogSummaryItems,
    logScopeType,
    logs,
    reportSkill.includeSheetLogs,
    reportSkill.maxLogs,
    sheet
  ]);

  const buildReportContext = useCallback(() => {
    const focusHeaderIds = reportSkill.focusHeaderIds.length ? new Set(reportSkill.focusHeaderIds) : null;
    const reportHeaders = resolvedHeaders
      .filter((header) => !focusHeaderIds || focusHeaderIds.has(header.id))
      .map((header) => ({
        id: header.id,
        label: header.label,
        type: header.type
      }));
    const visibleSavedRows = filteredRows.filter((row) => !row.isNew);
    const resolvedTargetType = REPORT_TARGET_TYPES.has(reportTargetType) ? reportTargetType : defaultReportTargetType;
    const scopedRows = resolvedTargetType === 'selected-rows'
      ? (selectedRows.length ? selectedRows : (activeRow ? [activeRow] : []))
      : visibleSavedRows;
    const normalizedReportRows = scopedRows
      .slice(0, reportSkill.maxVisibleRows)
      .map((row, index) => {
        const rowValues = reportHeaders.reduce((acc, header) => {
          acc[header.id] = row.resolvedValues?.[header.id] ?? row.values?.[header.id] ?? '';
          return acc;
        }, {});

        if (resolvedTargetType === 'column' && selectedReportColumn) {
          return {
            id: row.id,
            label: getRowReferenceLabel(row, index + 1),
            values: {
              [selectedReportColumn.id]: rowValues[selectedReportColumn.id] ?? ''
            }
          };
        }

        return {
          id: row.id,
          label: getRowReferenceLabel(row, index + 1),
          values: rowValues
        };
      });
    const reportTarget = {
      type: resolvedTargetType,
      label: resolvedTargetType === 'column'
        ? (selectedReportColumn?.label || 'Column')
        : resolvedTargetType === 'sheet'
          ? (sheet?.title || 'Sheet')
          : resolvedTargetType === 'selected-rows'
            ? `${scopedRows.length} selected row${scopedRows.length === 1 ? '' : 's'}`
            : activeRowLabel,
      rowCount: scopedRows.length,
      column: selectedReportColumn
        ? {
            id: selectedReportColumn.id,
            label: selectedReportColumn.label,
            type: selectedReportColumn.type
          }
        : null
    };
    const activeReportRow = resolvedTargetType === 'active-row' && activeRow
      ? {
          id: activeRow.id,
          values: reportHeaders.reduce((acc, header) => {
            acc[header.id] = activeRow.resolvedValues?.[header.id] ?? activeRow.values?.[header.id] ?? '';
            return acc;
          }, {})
        }
      : null;

    return {
      mode: 'report',
      sheetTitle: sheet?.title || 'Untitled Sheet',
      sheetDescription: sheet?.description || '',
      headers: reportHeaders,
      activeRow: activeReportRow,
      visibleRows: reportSkill.includeVisibleRows ? normalizedReportRows : [],
      reportTarget,
      relatedLogs: buildReportLogsContext({
        includeRelatedLogs: reportSkill.includeRelatedLogs,
        includeSheetLogs: reportSkill.includeSheetLogs,
        maxLogs: reportSkill.maxLogs
      }),
      skillConfig: {
        ...reportSkill,
        prompt: `${reportInput || ''}`.trim()
      },
      messages: []
    };
  }, [
    activeRow,
    activeRowLabel,
    buildReportLogsContext,
    defaultReportTargetType,
    filteredRows,
    getRowReferenceLabel,
    reportInput,
    reportSkill,
    reportTargetType,
    resolvedHeaders,
    selectedReportColumn,
    selectedRows,
    sheet
  ]);

  const buildAutomationContext = useCallback((automation, promptOverride = '') => {
    const focusHeaderIds = automation.focusHeaderIds.length ? new Set(automation.focusHeaderIds) : null;
    const automationHeaders = resolvedHeaders
      .filter((header) => !focusHeaderIds || focusHeaderIds.has(header.id))
      .map((header) => ({
        id: header.id,
        label: header.label,
        type: header.type
      }));

    return {
      mode: 'report',
      sheetTitle: sheet?.title || 'Untitled Sheet',
      sheetDescription: sheet?.description || '',
      headers: automationHeaders,
      activeRow: activeRow
        ? {
            id: activeRow.id,
            values: automationHeaders.reduce((acc, header) => {
              acc[header.id] = activeRow.resolvedValues?.[header.id] ?? activeRow.values?.[header.id] ?? '';
              return acc;
            }, {})
          }
        : null,
      visibleRows: automation.includeVisibleRows
        ? filteredRows
          .filter((row) => !row.isNew)
          .slice(0, automation.maxVisibleRows)
          .map((row) => ({
            id: row.id,
            values: automationHeaders.reduce((acc, header) => {
              acc[header.id] = row.resolvedValues?.[header.id] ?? row.values?.[header.id] ?? '';
              return acc;
            }, {})
          }))
        : [],
      relatedLogs: automation.includeRelatedLogs || automation.includeSheetLogs
        ? buildReportLogsContext({
          includeRelatedLogs: automation.includeRelatedLogs,
          includeSheetLogs: automation.includeSheetLogs,
          maxLogs: automation.maxLogs
        })
        : [],
      skillConfig: {
        title: automation.name,
        objective: automation.type === 'email_digest'
          ? 'Create an email-ready digest from the current sheet context.'
          : automation.name,
        prompt: `${promptOverride || automation.instructions || ''}`.trim(),
        scope: automation.scope,
        tone: automation.tone,
        format: automation.format,
        instructions: automation.instructions,
        includeVisibleRows: automation.includeVisibleRows,
        includeRelatedLogs: automation.includeRelatedLogs,
        includeSheetLogs: automation.includeSheetLogs
      },
      messages: []
    };
  }, [activeRow, buildReportLogsContext, filteredRows, resolvedHeaders, sheet]);

  const handleSendSidebarChat = async () => {
    if (!sheet) return;
    const prompt = `${chatInput || ''}`.trim();
    if (!prompt) return;
    if (chatMode === 'saved' && !selectedSavedThreadId) {
      toast.error('Choose a saved chat first or switch to Current.');
      return;
    }

    const nextUserMessage = {
      id: `chat_user_${Date.now()}`,
      role: 'user',
      content: prompt,
      createdAt: new Date()
    };
    const conversation = [...activeChatMessages, nextUserMessage];

    setChatInput('');
    setChatLoading(true);

    if (chatMode === 'current') {
      setCurrentThreadMessages(conversation);
    }

    try {
      const result = await sheetService.chatWithSheetData(buildSheetChatContext(conversation));
      const assistantMessage = {
        id: `chat_assistant_${Date.now()}`,
        role: 'assistant',
        content: `${result?.answer || ''}`.trim() || 'No response returned.',
        createdAt: new Date()
      };

      if (chatMode === 'saved' && activeRow) {
        await sheetService.appendSheetAiMessage(sheet.id, activeRow, selectedSavedThreadId, nextUserMessage, user?.uid);
        await sheetService.appendSheetAiMessage(sheet.id, activeRow, selectedSavedThreadId, assistantMessage, user?.uid);
      } else {
        setCurrentThreadMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (err) {
      if (chatMode === 'current') {
        setCurrentThreadMessages((prev) => prev.filter((item) => item.id !== nextUserMessage.id));
      }
      toast.error(err?.message || 'Failed to chat with sheet data.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!sheet) return;
    if (!reportSkill.enabled) {
      toast.error('Enable the report AI skill in the sheet builder first.');
      return;
    }
    if (reportTargetType === 'active-row' && !activeRow) {
      toast.error('Open a row first to generate a row report.');
      return;
    }
    if (reportTargetType === 'selected-rows' && !reportTargetRowCount) {
      toast.error('Select one or more rows to generate a report.');
      return;
    }
    if (reportTargetType === 'column' && !selectedReportColumn) {
      toast.error('Choose a column to generate a report.');
      return;
    }

    setReportLoading(true);
    try {
      const result = await sheetService.chatWithSheetData(buildReportContext());
      const nextContent = `${result?.answer || ''}`.trim();
      setGeneratedReport(nextContent || 'No report content returned.');
      setGeneratedReportTitle(`${result?.titleSuggestion || reportSkill.title || 'Generated report'}`.trim());
    } catch (err) {
      toast.error(err?.message || 'Failed to generate report.');
    } finally {
      setReportLoading(false);
    }
  };

  const handleRunAutomation = async (automation) => {
    if (!sheet || !activeRow) return;
    if (!automation?.enabled) {
      toast.error('Enable this automation in the sheet builder first.');
      return;
    }

    setAutomationRunningId(automation.id);
    try {
      const result = await sheetService.chatWithSheetData(buildAutomationContext(automation));
      const content = `${result?.answer || ''}`.trim() || 'No output returned.';

      if (automation.type === 'email_digest') {
        if (!automation.recipients.length) {
          throw new Error('This email automation has no recipient emails configured.');
        }

        const subject = automation.name || `${sheet?.title || 'Sheet'} digest`;
        await sheetService.sendEmailForSheet({
          subject,
          body: content,
          recipients: automation.recipients.map((email) => ({ email }))
        });
        toast.success(`Automation email sent to ${automation.recipients.length} recipient${automation.recipients.length === 1 ? '' : 's'}.`);
        return;
      }

      setGeneratedReportTitle(automation.name || `${sheet?.title || 'Sheet'} report`);
      setGeneratedReport(content);
      setSidebarView('ai');
      setAiMode('report');
      toast.success('Automation output generated.');
    } catch (err) {
      toast.error(err?.message || 'Failed to run automation.');
    } finally {
      setAutomationRunningId('');
    }
  };

  const handleSaveCurrentThread = async () => {
    if (!sheet || !activeRow || !currentThreadMessages.length) return;
    if (activeRow.isNew) {
      toast.error('Save the row first before saving chats.');
      return;
    }

    setSavingChatThread(true);
    try {
      const savedThread = await sheetService.saveSheetAiThread(
        sheet.id,
        {
          id: activeRow.id,
          rowId: activeRow.id,
          contributorId: activeRow.contributorId,
          storageMode: activeRow.storageMode,
          rowLabel: activeRowLabel,
          title: currentThreadMessages.find((item) => item.role === 'user')?.content || `${activeRowLabel} chat`
        },
        currentThreadMessages,
        user?.uid
      );
      setCurrentThreadMessages([]);
      setChatMode('saved');
      setSelectedSavedThreadId(savedThread.id);
      toast.success('Chat saved.');
    } catch (err) {
      toast.error(err?.message || 'Failed to save chat.');
    } finally {
      setSavingChatThread(false);
    }
  };

  const handleOpenComposer = (row, header) => {
    const recipient = `${row?.resolvedValues?.[header.id] || row?.values?.[header.id] || ''}`.trim();
    if (!isValidEmail(recipient)) {
      toast.error('Enter a valid email address first.');
      return;
    }

    const draft = buildEmailDraft({
      row,
      recipient,
      headers: resolvedHeaders,
      sheetTitle: sheet.title,
      description: sheet.description || '',
      prompt: ''
    });

    setShowChartsSidebar(false);
    setComposerState({
      rowId: row.id,
      headerId: header.id,
      to: recipient,
      subject: draft.subject,
      body: draft.body,
      aiPrompt: '',
      recipientLabel: header.label
    });
  };

  const handleOpenSmsComposer = (row, header) => {
    const recipient = `${row?.resolvedValues?.[header.id] || row?.values?.[header.id] || ''}`.trim();
    if (!isValidPhone(recipient)) {
      toast.error('Enter a valid phone number first.');
      return;
    }

    const draft = buildSmsDraft({
      row,
      recipient,
      headers: resolvedHeaders,
      sheetTitle: sheet.title,
      prompt: ''
    });

    setShowChartsSidebar(false);
    setComposerState({
      channel: 'sms',
      rowId: row.id,
      headerId: header.id,
      recipients: [{
        phone: recipient,
        rowId: row.id,
        rowLabel: getRowReferenceLabel(row),
        headerId: header.id,
        headerLabel: header.label
      }],
      to: draft.to,
      body: draft.body,
      aiPrompt: '',
      recipientLabel: header.label
    });
  };

  const handleGenerateComposerDraft = () => {
    if (!composerState || !sheet) return;
    if (composerState.channel === 'sms') {
      if (composerState.mode === 'batch') {
        const smsRows = rows
          .filter((row) => composerState.rowIds?.includes(row.id))
          .map((row) => ({
            ...row,
            resolvedValues: resolveRowFormulas(resolvedAllHeaders, row.values || {}, headerOptionRowsBySheetId)
          }));

        const draft = buildBatchSmsDraft({
          rows: smsRows,
          recipients: (composerState.recipients || []).map((recipient) => recipient.phone),
          headers: resolvedHeaders,
          sheetTitle: sheet.title,
          prompt: composerState.aiPrompt
        });

        setComposerState((prev) => (
          prev
            ? {
                ...prev,
                to: draft.to,
                body: draft.body
              }
            : prev
        ));
        toast.success('SMS draft generated.');
        return;
      }

      const currentRow = rows.find((row) => row.id === composerState.rowId);
      const draft = buildSmsDraft({
        row: currentRow,
        recipient: composerState.to,
        headers: resolvedHeaders,
        sheetTitle: sheet.title,
        prompt: composerState.aiPrompt
      });

      setComposerState((prev) => (
        prev
          ? {
              ...prev,
              body: draft.body
            }
          : prev
      ));
      toast.success('SMS draft generated.');
      return;
    }

    if (composerState.mode === 'batch') {
      const selectedRows = rows
        .filter((row) => composerState.rowIds?.includes(row.id))
        .map((row) => ({
          ...row,
          resolvedValues: resolveRowFormulas(resolvedAllHeaders, row.values || {}, headerOptionRowsBySheetId)
        }));

      const draft = buildBatchEmailDraft({
        rows: selectedRows,
        recipients: composerState.recipients || [],
        headers: resolvedHeaders,
        sheetTitle: sheet.title,
        description: sheet.description || '',
        prompt: composerState.aiPrompt
      });

      setComposerState((prev) => (
        prev
          ? {
              ...prev,
              to: draft.to,
              subject: draft.subject,
              body: draft.body
            }
          : prev
      ));
      toast.success('Batch draft generated.');
      return;
    }

    const currentRow = rows.find((row) => row.id === composerState.rowId);
    const draft = buildEmailDraft({
      row: currentRow,
      recipient: composerState.to,
      headers: resolvedHeaders,
      sheetTitle: sheet.title,
      description: sheet.description || '',
      prompt: composerState.aiPrompt
    });

    setComposerState((prev) => (
      prev
        ? {
            ...prev,
            subject: draft.subject,
            body: draft.body
          }
        : prev
    ));
    toast.success('Draft generated.');
  };

  const handleSendSmsComposer = async () => {
    if (!composerState || composerState.channel !== 'sms' || !sheet) return;

    const message = `${composerState.body || ''}`.trim();
    if (!message) {
      toast.error('Enter an SMS message first.');
      return;
    }

    const recipients = Array.from(new Map(
      (Array.isArray(composerState.recipients) ? composerState.recipients : [])
        .map((recipient) => {
          const phone = `${recipient?.phone || ''}`.trim();
          if (!isValidPhone(phone)) return null;
          return [normalizeWhatsAppPhoneValue(phone), recipient];
        })
        .filter(Boolean)
    ).values());

    if (!recipients.length) {
      toast.error('No valid phone numbers are available for this SMS.');
      return;
    }

    setSendingComposer(true);
    try {
      const result = await sheetService.sendBulkSmsForSheet({
        message,
        recipients
      });

      const logTargets = recipients.filter((recipient) => recipient.rowId);
      if (logTargets.length) {
        await Promise.all(logTargets.map((recipient) => (
          sheetService.saveSheetLog(sheet.id, {
            scopeType: 'row',
            rowId: recipient.rowId,
            targetLabel: recipient.rowLabel || recipient.headerLabel || recipient.phone,
            action: 'Message',
            status: 'G',
            note: `BulkSMS sent to ${recipient.phone}${result?.batchId ? ` (batch ${result.batchId})` : ''}.`
          }, user?.uid).catch((logError) => {
            console.warn('Failed to save SMS log:', logError);
            return null;
          })
        )));
      }

      toast.success(`SMS queued for ${result?.recipientCount || recipients.length} recipient${(result?.recipientCount || recipients.length) === 1 ? '' : 's'}.`);
      setComposerState(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to send SMS.');
    } finally {
      setSendingComposer(false);
    }
  };

  const handleSendEmailComposer = async () => {
    if (!composerState || composerState.channel === 'sms' || !sheet) return;

    const subject = `${composerState.subject || ''}`.trim();
    const body = `${composerState.body || ''}`.trim();
    if (!subject) {
      toast.error('Enter an email subject first.');
      return;
    }
    if (!body) {
      toast.error('Enter an email message first.');
      return;
    }

    const recipients = Array.from(new Map(
      (
        Array.isArray(composerState.recipients) && composerState.recipients.length
          ? composerState.recipients
          : `${composerState.to || ''}`.split(',')
      )
        .map((recipient) => {
          const email = `${typeof recipient === 'string' ? recipient : (recipient?.email || recipient?.address || '')}`.trim().toLowerCase();
          if (!isValidEmail(email)) return null;
          return [
            email,
            typeof recipient === 'string'
              ? { email }
              : {
                  email,
                  rowId: `${recipient?.rowId || ''}`.trim(),
                  rowLabel: `${recipient?.rowLabel || ''}`.trim(),
                  headerId: `${recipient?.headerId || ''}`.trim(),
                  headerLabel: `${recipient?.headerLabel || ''}`.trim()
                }
          ];
        })
        .filter(Boolean)
    ).values());

    if (!recipients.length) {
      toast.error('No valid email addresses are available for this email.');
      return;
    }

    setSendingComposer(true);
    try {
      const result = await sheetService.sendEmailForSheet({
        subject,
        body,
        recipients
      });

      if (composerState.mode === 'batch' && Array.isArray(composerState.rowIds) && composerState.rowIds.length) {
        await Promise.all(composerState.rowIds.map((rowId) => (
          sheetService.saveSheetLog(sheet.id, {
            scopeType: 'row',
            rowId,
            targetLabel: rowId,
            action: 'Email',
            status: 'G',
            note: `Email sent to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}. Subject: "${subject}".`
          }, user?.uid).catch((logError) => {
            console.warn('Failed to save batch email log entry:', logError);
          })
        )));
      } else if (composerState.rowId) {
        await sheetService.saveSheetLog(sheet.id, {
          scopeType: 'row',
          rowId: composerState.rowId,
          targetLabel: composerState.recipientLabel || recipients[0]?.email || composerState.rowId,
          action: 'Email',
          status: 'G',
          note: `Email sent to ${recipients.map((item) => item.email).join(', ')}. Subject: "${subject}".`
        }, user?.uid).catch((logError) => {
          console.warn('Failed to save single email log entry:', logError);
        });
      }

      toast.success(`Email sent to ${result?.recipientCount || recipients.length} recipient${(result?.recipientCount || recipients.length) === 1 ? '' : 's'}.`);
      setComposerState(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to send email.');
    } finally {
      setSendingComposer(false);
    }
  };

  const handleOpenBatchEmailComposer = () => {
    const emailHeaders = resolvedHeaders.filter((header) => header.type === 'email');
    if (!emailHeaders.length) {
      toast.error('Add an email column before using batch email.');
      return;
    }

    const selectedRows = resolvedRows.filter((row) => selectedRowIds.includes(row.id));
    if (!selectedRows.length) {
      toast.error('Select at least one row.');
      return;
    }

    const recipients = [...new Set(selectedRows.flatMap((row) => (
      emailHeaders
        .map((header) => `${row.values?.[header.id] || ''}`.trim())
        .filter((value) => isValidEmail(value))
    )))];

    if (!recipients.length) {
      toast.error('No valid email addresses were found in the selected rows.');
      return;
    }

    const draft = buildBatchEmailDraft({
      rows: selectedRows,
      recipients,
      headers: resolvedHeaders,
      sheetTitle: sheet.title,
      description: sheet.description || '',
      prompt: ''
    });

    setShowChartsSidebar(false);
    setComposerState({
      mode: 'batch',
      rowIds: selectedRows.map((row) => row.id),
      recipients,
      to: draft.to,
      subject: draft.subject,
      body: draft.body,
      aiPrompt: '',
      recipientLabel: `${recipients.length} recipients`
    });
  };

  const handleOpenBatchSmsComposer = () => {
    const phoneHeaders = resolvedHeaders.filter((header) => header.type === 'telephone');
    if (!phoneHeaders.length) {
      toast.error('Add a phone column before using batch SMS.');
      return;
    }

    const selectedRows = resolvedRows.filter((row) => selectedRowIds.includes(row.id));
    if (!selectedRows.length) {
      toast.error('Select at least one row.');
      return;
    }

    const recipients = Array.from(new Map(
      selectedRows.flatMap((row) => (
        phoneHeaders
          .map((header) => {
            const phone = `${row.resolvedValues?.[header.id] || row.values?.[header.id] || ''}`.trim();
            if (!isValidPhone(phone)) return null;
            return [
              normalizeWhatsAppPhoneValue(phone),
              {
                phone,
                rowId: row.id,
                rowLabel: getRowReferenceLabel(row),
                headerId: header.id,
                headerLabel: header.label
              }
            ];
          })
          .filter(Boolean)
      ))
    ).values());

    if (!recipients.length) {
      toast.error('No valid phone numbers were found in the selected rows.');
      return;
    }

    const draft = buildBatchSmsDraft({
      rows: selectedRows,
      recipients: recipients.map((recipient) => recipient.phone),
      headers: resolvedHeaders,
      sheetTitle: sheet.title,
      prompt: ''
    });

    setShowChartsSidebar(false);
    setComposerState({
      channel: 'sms',
      mode: 'batch',
      rowIds: selectedRows.map((row) => row.id),
      recipients,
      to: draft.to,
      body: draft.body,
      aiPrompt: '',
      recipientLabel: `${recipients.length} recipients`
    });
  };

  const allRowsSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedRowIds.includes(row.id));
  const hasEmailHeaders = useMemo(
    () => resolvedHeaders.some((header) => header.type === 'email'),
    [resolvedHeaders]
  );
  const hasPhoneHeaders = useMemo(
    () => resolvedHeaders.some((header) => header.type === 'telephone'),
    [resolvedHeaders]
  );
  const selectedEmailCount = useMemo(() => {
    const emailHeaders = resolvedHeaders.filter((header) => header.type === 'email');
    return new Set(selectedRows.flatMap((row) => (
      emailHeaders
        .map((header) => `${row.resolvedValues?.[header.id] || row.values?.[header.id] || ''}`.trim())
        .filter((value) => isValidEmail(value))
    ))).size;
  }, [resolvedHeaders, selectedRows]);
  const selectedPhoneCount = useMemo(() => {
    const phoneHeaders = resolvedHeaders.filter((header) => header.type === 'telephone');
    return new Set(selectedRows.flatMap((row) => (
      phoneHeaders
        .map((header) => normalizeWhatsAppPhoneValue(row.resolvedValues?.[header.id] || row.values?.[header.id] || ''))
        .filter((value) => value.length >= 7)
    ))).size;
  }, [resolvedHeaders, selectedRows]);
  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const hasPendingRowChanges = useMemo(() => (
    resolvedRows.some((row) => {
      if (row.isNew) {
        return canCreateRows && resolvedHeaders.some((header) => `${row.resolvedValues?.[header.id] || ''}`.trim());
      }
      return row.isDirty && canEditSavedRow(row);
    })
  ), [canCreateRows, resolvedHeaders, resolvedRows, canEditSavedRow]);
  const canEditAnyRow = useMemo(
    () => filteredRows.some((row) => (row.isNew ? canCreateRows : canEditSavedRow(row))),
    [filteredRows, canCreateRows, canEditSavedRow]
  );
  useEffect(() => {
    if (sheetEditMode && !canEditAnyRow) {
      setSheetEditMode(false);
    }
  }, [sheetEditMode, canEditAnyRow]);

  const renderCellView = (row, header, { compact = false } = {}) => {
    const rowValues = row?.resolvedValues || row?.values || {};
    const conditionalState = evaluateHeaderConditionalState(header, rowValues, resolvedAllHeaders);
    const value = rowValues[header.id];
    const fieldTextStyle = getColumnTextStyle(header);
    const fieldColorStyle = fieldTextStyle.color ? { color: fieldTextStyle.color } : undefined;
    const fieldAlignmentClass = getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign));
    const emptyState = compact ? 'â€”' : 'No value';
    const shellClassName = compact
      ? `flex min-h-[54px] items-center rounded-2xl border border-transparent bg-transparent px-3 py-2 text-sm text-slate-700 ${fieldAlignmentClass}`
      : `rounded-2xl border border-slate-200/90 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm ${fieldAlignmentClass}`;

    if (conditionalState.hidden) {
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          <span className="inline-flex rounded-full border border-dashed border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            Hidden
          </span>
        </div>
      );
    }

    if (header.id === ACTIVE_ACTIONS_HEADER_ID) {
      const actionSummary = getRowActiveActionSummary(row?.id);
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {actionSummary ? (
            <button
              type="button"
              onClick={() => handleOpenActionDetailsForRow(row.id, actionSummary.entries[0]?.formId || '')}
              className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 transition hover:bg-amber-100"
            >
              {actionSummary.primaryLabel}
            </button>
          ) : (
            <span className="text-sm text-slate-400">No active actions</span>
          )}
        </div>
      );
    }

    if (header.type === 'profile') {
      const profile = getProfileDisplayData(header, rowValues);
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          <div className="flex min-w-0 items-center gap-3">
            {profile.imageUrl ? (
              <img
                src={profile.imageUrl}
                alt={profile.label || header.label || 'Profile'}
                className={`${compact ? 'h-10 w-10' : 'h-11 w-11'} rounded-full border border-slate-200 object-cover shadow-sm`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className={`${compact ? 'h-10 w-10' : 'h-11 w-11'} flex items-center justify-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-500`}>
                {(profile.label || header.label || '?').slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              {!compact && (
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {header.label}
                </p>
              )}
              <p className="truncate text-sm font-semibold text-slate-900" style={fieldColorStyle}>
                {profile.label || emptyState}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (header.type === 'image') {
      const imageUrl = `${value ?? ''}`.trim();
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {imageUrl ? (
            <button
              type="button"
              onClick={() => setActiveImageUrl(imageUrl)}
              className="inline-flex"
              title="Open image"
            >
              <img
                src={imageUrl}
                alt={header.label || 'Image'}
                className={`${compact ? 'h-14 w-14' : 'h-20 w-20'} border border-slate-200 object-cover shadow-sm`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </button>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'checkbox') {
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${
            value ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {value ? 'Checked' : 'Unchecked'}
          </span>
        </div>
      );
    }

    if (header.type === 'buttons' || header.type === 'select' || header.type === 'yesno' || header.type === 'rog') {
      const displayValue = `${value ?? ''}`.trim();
      const showImagePreview = isFirebaseImageUrl(displayValue);
      const rogTone = header.type === 'rog' ? getRogTone(displayValue) : null;
      return (
        <div className={`${shellClassName} ${getRogOverlayClass(header, displayValue)}`} style={fieldTextStyle}>
          {showImagePreview ? (
            <img
              src={displayValue}
              alt={header.label || 'Selected option image'}
              className={`${compact ? 'h-14 w-14' : 'h-20 w-20'} border border-slate-200 object-cover shadow-sm`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : displayValue ? (
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
              rogTone ? rogTone.soft : 'bg-[#008080]/10 text-slate-900'
            }`}>
              <span style={fieldColorStyle}>{displayValue}</span>
            </span>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'multiselect' || header.type === 'list') {
      const items = Array.isArray(value) ? value : normalizeListItems(value);
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          <div className={`flex min-h-[30px] flex-wrap gap-2 ${fieldAlignmentClass}`}>
            {items.length ? items.map((item, index) => (
              <span key={`${row.id}_${header.id}_view_${index}`} className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-900" style={fieldColorStyle}>
                {item}
              </span>
            )) : (
              <span className="text-sm text-slate-400">{emptyState}</span>
            )}
          </div>
        </div>
      );
    }

    if (header.type === 'rating') {
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {value ? renderRatingStars(value, () => {}, true) : <span className="text-sm text-slate-400">{emptyState}</span>}
        </div>
      );
    }

    if (header.type === 'textarea') {
      const textValue = `${value ?? ''}`.trim();
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {textValue ? (
            <div
              className="max-w-none text-sm leading-6 text-slate-700"
              style={fieldColorStyle}
              dangerouslySetInnerHTML={{ __html: markdownToHtml(textValue) }}
            />
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'address') {
      const addressValue = `${value ?? ''}`.trim();
      const mapUrl = getMapNavigationUrl(addressValue);
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {addressValue ? (
            <a href={mapUrl || undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#008080]" style={fieldColorStyle}>
              <MapPin className="h-4 w-4 text-[#008080]" />
              <span className="truncate">{addressValue}</span>
            </a>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'youtube' || header.type === 'document') {
      const linkValue = `${value ?? ''}`.trim();
      const href = header.type === 'youtube' ? getYouTubeEmbedUrl(linkValue) || linkValue : linkValue;
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {linkValue ? (
            <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-[#008080]" style={fieldColorStyle}>
              {header.type === 'youtube' ? <Play className="h-3.5 w-3.5" /> : <ExternalLink className="h-3.5 w-3.5" />}
              <span>{header.type === 'youtube' ? 'Open video' : 'Open document'}</span>
            </a>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'email') {
      const emailValue = `${value ?? ''}`.trim();
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {emailValue ? (
            <a href={`mailto:${emailValue}`} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#008080]" style={fieldColorStyle}>
              <Mail className="h-4 w-4 text-[#008080]" />
              <span className="truncate">{emailValue}</span>
            </a>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    if (header.type === 'telephone') {
      const phoneValue = `${value ?? ''}`.trim();
      return (
        <div className={shellClassName} style={fieldTextStyle}>
          {phoneValue ? (
            <a href={`tel:${normalizePhoneValue(phoneValue)}`} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#008080]" style={fieldColorStyle}>
              <Phone className="h-4 w-4 text-[#008080]" />
              <span className="truncate">{phoneValue}</span>
            </a>
          ) : (
            <span className="text-sm text-slate-400">{emptyState}</span>
          )}
        </div>
      );
    }

    const displayValue = formatHeaderValueForDraft(header, value);
    return (
      <div className={shellClassName} style={fieldTextStyle}>
        <span className={`${displayValue ? 'text-slate-700' : 'text-slate-400'} text-sm`} style={displayValue ? fieldColorStyle : undefined}>
          {displayValue || emptyState}
        </span>
      </div>
    );
  };

  const getProfilePresetData = useCallback((row, fallbackLabel = '') => {
    const rowValues = row?.resolvedValues || row?.values || {};
    const body = profilePresetHeaders.bodyHeader
      ? getHeaderDisplayValueForRow(profilePresetHeaders.bodyHeader, rowValues).trim()
      : '';
    const profileLabel = getProfileDisplayData(presetProfileHeader, rowValues).label || fallbackLabel;
    return {
      title: profileLabel,
      body,
      actionHeader: profilePresetHeaders.actionHeader,
      titleColor: presetProfileHeader?.renderColor || '',
      bodyColor: profilePresetHeaders.bodyHeader?.renderColor || ''
    };
  }, [presetProfileHeader, profilePresetHeaders]);
  const getListCardPresetData = useCallback((row, fallbackLabel = '') => {
    const rowValues = row?.resolvedValues || row?.values || {};
    const imageUrl = listCardPresetHeaders.imageHeader
      ? `${rowValues?.[listCardPresetHeaders.imageHeader.id] ?? ''}`.trim()
      : '';
    const category = listCardPresetHeaders.categoryHeader
      ? getHeaderDisplayValueForRow(listCardPresetHeaders.categoryHeader, rowValues).trim()
      : '';
    const title = listCardPresetHeaders.titleHeader
      ? getHeaderDisplayValueForRow(listCardPresetHeaders.titleHeader, rowValues).trim()
      : '';
    const body = listCardPresetHeaders.bodyHeader
      ? getHeaderDisplayValueForRow(listCardPresetHeaders.bodyHeader, rowValues).trim()
      : '';
    const tags = listCardPresetHeaders.tagHeader
      ? normalizeListItems(rowValues?.[listCardPresetHeaders.tagHeader.id])
      : [];

    return {
      imageUrl,
      imageLayout: sheetRenderPresetConfig.imageLayout === 'full-width' ? 'full-width' : 'inline',
      category,
      title,
      body,
      tags,
      cardLabel: fallbackLabel || title || 'Item',
      categoryColor: listCardPresetHeaders.categoryHeader?.renderColor || '',
      titleColor: listCardPresetHeaders.titleHeader?.renderColor || '',
      bodyColor: listCardPresetHeaders.bodyHeader?.renderColor || ''
    };
  }, [listCardPresetHeaders, sheetRenderPresetConfig.imageLayout]);
  const getKanbanPresetData = useCallback((row, fallbackLabel = '') => {
    const rowValues = row?.resolvedValues || row?.values || {};
    const status = kanbanPresetHeaders.statusHeader
      ? getHeaderDisplayValueForRow(kanbanPresetHeaders.statusHeader, rowValues).trim()
      : '';
    const title = kanbanPresetHeaders.titleHeader
      ? getHeaderDisplayValueForRow(kanbanPresetHeaders.titleHeader, rowValues).trim()
      : '';
    const body = kanbanPresetHeaders.bodyHeader
      ? getHeaderDisplayValueForRow(kanbanPresetHeaders.bodyHeader, rowValues).trim()
      : '';
    const meta = kanbanPresetHeaders.metaHeader
      ? getHeaderDisplayValueForRow(kanbanPresetHeaders.metaHeader, rowValues).trim()
      : '';
    return {
      status,
      title: title || fallbackLabel,
      body,
      meta,
      titleColor: kanbanPresetHeaders.titleHeader?.renderColor || '',
      bodyColor: kanbanPresetHeaders.bodyHeader?.renderColor || '',
      metaColor: kanbanPresetHeaders.metaHeader?.renderColor || ''
    };
  }, [kanbanPresetHeaders]);
  const getCalendarPresetData = useCallback((row, fallbackLabel = '') => {
    const rowValues = row?.resolvedValues || row?.values || {};
    const imageUrl = calendarPresetHeaders.imageHeader ? `${rowValues?.[calendarPresetHeaders.imageHeader.id] ?? ''}`.trim() : '';
    const rawDate = calendarPresetHeaders.dateHeader ? rowValues?.[calendarPresetHeaders.dateHeader.id] : null;
    const date = parseRowDateValue(rawDate);
    const title = calendarPresetHeaders.titleHeader
      ? getHeaderDisplayValueForRow(calendarPresetHeaders.titleHeader, rowValues).trim()
      : '';
    const meta = calendarPresetHeaders.metaHeader
      ? getHeaderDisplayValueForRow(calendarPresetHeaders.metaHeader, rowValues).trim()
      : '';
    return {
      imageUrl,
      date,
      title: title || fallbackLabel,
      meta,
      titleColor: calendarPresetHeaders.titleHeader?.renderColor || '',
      metaColor: calendarPresetHeaders.metaHeader?.renderColor || '',
      dateColor: calendarPresetHeaders.dateHeader?.renderColor || ''
    };
  }, [calendarPresetHeaders]);
  const kanbanColumns = useMemo(() => {
    if (!usesKanbanPresetLayout || !kanbanPresetHeaders.statusHeader) return [];
    const resolvedOptions = getFilterOptionsForHeader(kanbanPresetHeaders.statusHeader, filteredRows, headerOptionRowsBySheetId, resolvedAllHeaders)
      .map((option) => `${option?.value || ''}`.trim())
      .filter(Boolean);
    const rowStatuses = filteredRows
      .map((row, index) => getKanbanPresetData(row, getRowReferenceLabel(row, index + 1)).status)
      .filter(Boolean);
    return Array.from(new Set([...resolvedOptions, ...rowStatuses]))
      .map((status) => ({
        id: status,
        label: status,
        rows: filteredRows.filter((row, index) => getKanbanPresetData(row, getRowReferenceLabel(row, index + 1)).status === status)
      }));
  }, [filteredRows, getKanbanPresetData, getRowReferenceLabel, headerOptionRowsBySheetId, kanbanPresetHeaders.statusHeader, resolvedAllHeaders, usesKanbanPresetLayout]);
  const calendarRowsWithDates = useMemo(() => {
    if (!usesCalendarPresetLayout) return [];
    return filteredRows
      .map((row, index) => ({
        row,
        ...getCalendarPresetData(row, getRowReferenceLabel(row, index + 1))
      }))
      .filter((item) => item.date);
  }, [filteredRows, getCalendarPresetData, getRowReferenceLabel, usesCalendarPresetLayout]);
  useEffect(() => {
    if (!usesCalendarPresetLayout) return;
    const firstDate = calendarRowsWithDates[0]?.date;
    if (firstDate) {
      setCalendarViewMonth(getMonthStart(firstDate));
      setCalendarFocusDate(firstDate);
    }
  }, [calendarRowsWithDates, usesCalendarPresetLayout]);
  const calendarRowsByDateKey = useMemo(() => (
    calendarRowsWithDates.reduce((accumulator, item) => {
      const key = getDateKey(item.date);
      if (!accumulator[key]) {
        accumulator[key] = [];
      }
      accumulator[key].push(item);
      return accumulator;
    }, {})
  ), [calendarRowsWithDates]);
  const calendarSelectedDayRows = useMemo(() => {
    if (!usesCalendarPresetLayout) return [];
    return calendarRowsByDateKey[getDateKey(calendarFocusDate)] || [];
  }, [calendarFocusDate, calendarRowsByDateKey, usesCalendarPresetLayout]);
  const calendarSelectedWeekDays = useMemo(() => {
    if (!usesCalendarPresetLayout) return [];
    const weekStart = getWeekStart(calendarFocusDate);
    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(weekStart, index);
      const key = getDateKey(date);
      return {
        date,
        key,
        isFocused: key === getDateKey(calendarFocusDate),
        rows: calendarRowsByDateKey[key] || []
      };
    });
  }, [calendarFocusDate, calendarRowsByDateKey, usesCalendarPresetLayout]);
  const selectedWeekHasRows = calendarSelectedWeekDays.some((day) => day.rows.length > 0);
  useEffect(() => {
    if (!usesCalendarPresetLayout) return;
    setCalendarPresetView((prev) => {
      if (prev === 'day' && calendarSelectedDayRows.length) return prev;
      if (prev === 'week' && selectedWeekHasRows) return prev;
      if (calendarSelectedDayRows.length) return 'day';
      if (selectedWeekHasRows) return 'week';
      return 'month';
    });
  }, [calendarSelectedDayRows.length, selectedWeekHasRows, usesCalendarPresetLayout]);
  const calendarDays = useMemo(() => {
    if (!usesCalendarPresetLayout) return [];
    const start = getMonthGridStart(calendarViewMonth);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
      const key = getDateKey(date);
      return {
        date,
        key,
        inMonth: date.getMonth() === calendarViewMonth.getMonth(),
        rows: calendarRowsWithDates.filter((item) => getDateKey(item.date) === key)
      };
    });
  }, [calendarRowsWithDates, calendarViewMonth, usesCalendarPresetLayout]);

  const renderCellInput = (row, header) => {
    const liveResolvedRowValues = resolveRowFormulas(
      resolvedAllHeaders,
      row?.values || {},
      headerOptionRowsBySheetId
    );
    const isFormulaColumn = hasHeaderFormula(header);
    const isFixedDefault = header.defaultValueMode === 'fixed';
    const conditionalState = evaluateHeaderConditionalState(header, liveResolvedRowValues, resolvedAllHeaders);
    const readOnly = conditionalState.readOnly || isFormulaColumn || isFixedDefault || (row.isNew ? !canCreateRows : !canEditSavedRow(row));
    const value = isFormulaColumn ? liveResolvedRowValues?.[header.id] : row.values?.[header.id];
    const percentDisplayValue = value === '' || value === null || value === undefined ? '' : `${formatNumericDisplayValue(header, value)}%`;
    const fieldTextStyle = getColumnTextStyle(header);
    const fieldAlignmentClass = getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign));
    const inputType = getInputTypeForHeader(header.type);
    const inputMode = getInputModeForHeader(header.type);
    const step = header.type === 'currency' ? '0.01' : (header.type === 'number' || header.type === 'percent') ? 'any' : undefined;

    if (conditionalState.hidden) {
      return (
        <div
          className={`flex min-h-[42px] items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-2.5 py-2 text-sm text-slate-400 ${fieldAlignmentClass}`}
          style={fieldTextStyle}
        >
          Hidden by logic
        </div>
      );
    }

    if (header.id === ACTIVE_ACTIONS_HEADER_ID) {
      const actionSummary = getRowActiveActionSummary(row?.id);
      return (
        <div
          className={`flex min-h-[42px] items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-2.5 py-2 text-sm text-slate-500 ${fieldAlignmentClass}`}
          style={fieldTextStyle}
        >
          {actionSummary ? (
            <button
              type="button"
              onClick={() => handleOpenActionDetailsForRow(row.id, actionSummary.entries[0]?.formId || '')}
              className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 transition hover:bg-amber-100"
            >
              {actionSummary.primaryLabel}
            </button>
          ) : (
            'No active actions'
          )}
        </div>
      );
    }

    if (isFormulaColumn) {
      return (
        <input
          type="text"
          value={
            header.type === 'percent'
              ? percentDisplayValue
              : (header.type === 'number' || header.type === 'currency')
                ? formatNumericDisplayValue(header, value)
                : (value ?? '')
          }
          readOnly
          className={`w-full rounded-md border border-dashed border-slate-200 bg-slate-50 px-2.5 py-2 text-sm text-slate-500 outline-none ${fieldAlignmentClass}`}
          style={fieldTextStyle}
        />
      );
    }

    if (header.type === 'checkbox') {
      return (
        <label className="flex h-full min-h-[38px] items-center justify-center">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={readOnly}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
          />
        </label>
      );
    }

    if (header.type === 'select') {
      const selectedValue = `${value ?? ''}`.trim();
      const resolvedOptions = getResolvedOptionsForHeader(header, liveResolvedRowValues);
      const resolvedOptionsSignature = resolvedOptions.join('||');
      const showImagePreview = isFirebaseImageUrl(selectedValue);
      return (
        <div className="relative">
          <select
            key={`${row.id}_${header.id}_${resolvedOptionsSignature}`}
            value={selectedValue}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            disabled={readOnly}
            className={showImagePreview
              ? `absolute inset-0 z-10 h-20 w-20 cursor-pointer opacity-0 ${readOnly ? 'pointer-events-none' : ''}`
              : `w-full rounded-md border px-2.5 py-2 text-sm outline-none transition ${fieldAlignmentClass} ${
                  readOnly
                    ? 'border-transparent bg-transparent text-slate-700'
                    : 'border-slate-200 bg-white text-slate-900 focus:border-[#008080]'
                }`}
            style={showImagePreview ? undefined : fieldTextStyle}
          >
            <option value="">Select...</option>
            {resolvedOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {showImagePreview ? (
            <img
              src={selectedValue}
              alt={header.label || 'Selected option image'}
              className={`h-20 w-20 border border-slate-200 object-cover ${readOnly ? '' : 'cursor-pointer'}`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
        </div>
      );
    }

    if (header.type === 'buttons' || header.type === 'yesno' || header.type === 'rog') {
      const selectedValue = `${value ?? ''}`;
      const resolvedOptions = getResolvedOptionsForHeader(header, liveResolvedRowValues);
      return (
        <div className={`flex min-h-[42px] flex-wrap gap-2 rounded-xl p-2 ${fieldAlignmentClass} ${getRogOverlayClass(header, selectedValue)}`} style={fieldTextStyle}>
          {resolvedOptions.map((option) => {
            return (
              <button
                key={option}
                type="button"
                disabled={readOnly}
                onClick={() => handleCellChange(row.id, header.id, option)}
                className={getChoiceButtonClasses({
                  headerType: header.type,
                  option,
                  selectedValue,
                  disabled: readOnly,
                  readOnlyGhost: true
                })}
              >
                {option}
              </button>
            );
          })}
          
          {!resolvedOptions.length && (
            <div className="flex min-h-[38px] items-center text-sm text-slate-400">
              No options configured
            </div>
          )}
        </div>
      );
    }

    if (header.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      const resolvedOptions = getResolvedOptionsForHeader(header, liveResolvedRowValues);
      const controlId = `${row.id}_${header.id}`;
      const isOpen = openMultiSelectState?.id === controlId;
      return (
        <div className="relative" data-multiselect-root="true">
          <button
            type="button"
            disabled={readOnly}
            onClick={(event) => {
              if (isOpen) {
                setOpenMultiSelectState(null);
                return;
              }

              const rect = event.currentTarget.getBoundingClientRect();
              const estimatedDropdownHeight = 240;
              const spaceBelow = window.innerHeight - rect.bottom;
              const openUpward = spaceBelow < estimatedDropdownHeight && rect.top > spaceBelow;

              setOpenMultiSelectState({
                id: controlId,
                openUpward
              });
            }}
            className={`w-full rounded-md border px-2.5 py-2 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-700'
                : 'border-slate-200 bg-white text-slate-900 focus:border-[#008080]'
            }`}
            style={fieldTextStyle}
          >
            {selectedValues.length ? selectedValues.join(', ') : 'Select option(s)'}
          </button>
          {!readOnly && isOpen && (
            <div
              className={`absolute left-0 right-0 z-30 rounded-xl border border-slate-200 bg-white p-2 shadow-lg ${
                openMultiSelectState?.openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
            >
              <div className="max-h-60 space-y-1 overflow-y-auto pr-1">
                {resolvedOptions.map((option) => {
                  const isChecked = selectedValues.includes(option);
                  return (
                    <label
                      key={option}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const nextValues = e.target.checked
                            ? [...selectedValues, option]
                            : selectedValues.filter((item) => item !== option);
                          handleCellChange(row.id, header.id, nextValues);
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      <span>{option}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (header.type === 'rating') {
      return renderRatingStars(value, (nextValue) => handleCellChange(row.id, header.id, nextValue), readOnly);
    }

    if (header.type === 'textarea') {
      return (
        <textarea
          rows={4}
          value={value ?? ''}
          onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
          placeholder={header.label}
          readOnly={readOnly}
          className={`w-full rounded-md border px-2.5 py-2 text-sm outline-none transition ${fieldAlignmentClass} ${
            readOnly
              ? 'border-transparent bg-transparent text-slate-500'
              : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
          }`}
          style={fieldTextStyle}
        />
      );
    }

    if (header.type === 'address') {
      const addressValue = `${value ?? ''}`.trim();
      const mapUrl = getMapNavigationUrl(addressValue);
      return (
        <div className="group relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            placeholder={header.label}
            readOnly={readOnly}
            inputMode={inputMode}
            className={`w-full rounded-md border py-2 pl-2.5 pr-10 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-500'
                : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
            }`}
            style={fieldTextStyle}
          />
          <a
            href={mapUrl || undefined}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => {
              if (!mapUrl) event.preventDefault();
            }}
            title={mapUrl ? 'Open map navigation' : 'Enter an address'}
            className={`absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition ${
              mapUrl
                ? 'text-[#008080] opacity-0 group-hover:opacity-100 hover:bg-[#008080]/10 hover:text-[#008080]'
                : 'cursor-not-allowed text-slate-300'
            }`}
          >
            <MapPin className="h-4 w-4" />
          </a>
        </div>
      );
    }

    if (header.type === 'list') {
      const controlKey = `${row.id}_${header.id}`;
      const listItems = normalizeListItems(value);
      return (
        <div className="space-y-2">
          {!readOnly && (
            <div className="flex gap-2">
              <input
                type="text"
                value={listDrafts[controlKey] || ''}
                onChange={(e) => handleListDraftChange(row.id, header.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddListItem(row, header);
                  }
                }}
                placeholder={header.label || 'Add item'}
                className={`w-full rounded-md border px-2.5 py-2 text-sm outline-none transition ${fieldAlignmentClass} border-slate-200 bg-white text-slate-900 focus:border-[#008080]`}
                style={fieldTextStyle}
              />
              <button
                type="button"
                onClick={() => handleAddListItem(row, header)}
                className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-[#008080]/40 hover:text-[#008080]"
              >
                Add
              </button>
            </div>
          )}
          <div className={`flex min-h-[38px] flex-wrap gap-2 ${fieldAlignmentClass}`} style={fieldTextStyle}>
            {listItems.length ? listItems.map((item, index) => (
              <span key={`${row.id}_${header.id}_${index}`} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                {item}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveListItem(row, header, index)}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                    aria-label={`Remove ${item}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            )) : (
              <span className="text-sm text-slate-400">{readOnly ? 'No items' : 'Add the first item'}</span>
            )}
          </div>
        </div>
      );
    }

    if (header.type === 'youtube') {
      const youtubeUrl = `${value ?? ''}`.trim();
      const embedUrl = getYouTubeEmbedUrl(youtubeUrl);
      return (
        <div className="group relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            placeholder={header.label}
            readOnly={readOnly}
            inputMode={inputMode}
            className={`w-full rounded-md border py-2 pl-2.5 pr-10 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-500'
                : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
            }`}
            style={fieldTextStyle}
          />
          <button
            type="button"
            onClick={() => embedUrl && setActiveYoutubeEmbedUrl(embedUrl)}
            disabled={!embedUrl}
            title={embedUrl ? 'Open YouTube video' : 'Enter a valid YouTube URL'}
            className={`absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition ${
              embedUrl
                ? 'text-[#008080] opacity-0 group-hover:opacity-100 hover:bg-[#008080]/10 hover:text-[#008080]'
                : 'cursor-not-allowed text-slate-300'
            }`}
          >
            <Play className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (header.type === 'document') {
      const documentUrl = `${value ?? ''}`.trim();
      const uploadKey = `${row.id}_${header.id}`;
      const isUploading = uploadingDocumentKey === uploadKey;
      return (
        <div className={`flex min-h-[40px] items-center gap-2 ${fieldAlignmentClass}`} style={fieldTextStyle}>
          {!documentUrl && !readOnly && (
            <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition ${
              isUploading
                ? 'border-slate-200 bg-slate-100 text-slate-400'
                : 'border-slate-200 bg-white text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
            }`}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv"
                className="hidden"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleDocumentUpload(row, header, file);
                  }
                  e.target.value = '';
                }}
              />
              <span>{isUploading ? 'Uploading...' : 'Upload document'}</span>
            </label>
          )}

          {documentUrl ? (
            <>
              <a
                href={documentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:text-[#008080]"
                title="Open document"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>Open document</span>
              </a>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleCellChange(row.id, header.id, '')}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                  aria-label="Remove document"
                  title="Remove document"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </>
          ) : null}

          {!documentUrl && readOnly ? (
            <span className="text-sm text-slate-400">No document</span>
          ) : null}
        </div>
      );
    }

    if (header.type === 'image') {
      const imageUrl = `${value ?? ''}`.trim();
      const uploadKey = `${row.id}_${header.id}`;
      const isUploading = uploadingImageKey === uploadKey;
      return (
        <div className={`flex min-h-[64px] items-center ${fieldAlignmentClass}`} style={fieldTextStyle}>
          {!imageUrl && !readOnly && (
            <label className={`inline-flex cursor-pointer items-center gap-2 border px-3 py-2 text-xs font-semibold transition ${
              isUploading
                ? 'border-slate-200 bg-slate-100 text-slate-400'
                : 'border-slate-200 bg-white text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
            }`}>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImageUpload(row, header, file);
                  }
                  e.target.value = '';
                }}
              />
              <span>{isUploading ? 'Uploading...' : 'Upload image'}</span>
            </label>
          )}
          {imageUrl ? (
            <div className="relative inline-flex">
              <button
                type="button"
                onClick={() => setActiveImageUrl(imageUrl)}
                className=""
                title="Open image"
                aria-label="Open image"
              >
                <img
                  src={imageUrl}
                  alt={header.label || 'Image'}
                  className="h-20 w-20 border border-slate-200 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </button>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleCellChange(row.id, header.id, '')}
                  className="absolute -right-2 -top-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                  aria-label="Remove image"
                  title="Remove image"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : null}
          {!imageUrl && readOnly ? (
            <span className="text-sm text-slate-400">No image</span>
          ) : null}
        </div>
      );
    }

    if (header.type === 'profile') {
      const profile = getProfileDisplayData(header, row.resolvedValues || row.values || {});
      return (
        <div className={`flex min-h-[64px] items-center ${fieldAlignmentClass}`} style={fieldTextStyle}>
          <div className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${readOnly ? '' : 'bg-white'}`}>
            {profile.imageUrl ? (
              <img
                src={profile.imageUrl}
                alt={profile.label || header.label || 'Profile'}
                className="h-10 w-10 rounded-full border border-slate-200 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-500">
                {(profile.label || header.label || '?').slice(0, 1)}
              </div>
            )}
            <span className={`${readOnly ? 'text-slate-700' : 'text-slate-800'} text-sm`}>
              {profile.label || 'No profile selected'}
            </span>
          </div>
        </div>
      );
    }

    if (header.type === 'percent') {
      return (
        <div className="relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            placeholder={header.label}
            readOnly={readOnly}
            inputMode={inputMode}
            step={step}
            className={`w-full rounded-md border py-2 pl-2.5 pr-8 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-700'
                : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
            }`}
            style={fieldTextStyle}
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
      );
    }

    if (header.type === 'email') {
      const emailValue = `${value ?? ''}`.trim();
      const canOpenComposer = isValidEmail(emailValue);
      return (
        <div className="group relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            placeholder={header.label}
            readOnly={readOnly}
            inputMode={inputMode}
            className={`w-full rounded-md border py-2 pl-2.5 pr-10 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-500'
                : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
            }`}
            style={fieldTextStyle}
          />
          <button
            type="button"
            onClick={() => handleOpenComposer(row, header)}
            disabled={!canOpenComposer}
            title={canOpenComposer ? 'Open email composer' : 'Enter a valid email address'}
            className={`absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition ${
              canOpenComposer
                ? 'text-[#008080] opacity-0 group-hover:opacity-100 hover:bg-[#008080]/10 hover:text-[#008080]'
                : 'cursor-not-allowed text-slate-300'
            }`}
          >
            <Mail className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (header.type === 'telephone') {
      const phoneValue = `${value ?? ''}`.trim();
      const canUsePhoneActions = isValidPhone(phoneValue);
      const telHref = canUsePhoneActions ? `tel:${normalizePhoneValue(phoneValue)}` : null;
      const whatsappHref = canUsePhoneActions ? `https://wa.me/${normalizeWhatsAppPhoneValue(phoneValue)}` : null;

      return (
        <div className="group relative">
          <input
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
            placeholder={header.label}
            readOnly={readOnly}
            inputMode={inputMode}
            pattern="[0-9+()\\-\\s]*"
            className={`w-full rounded-md border py-2 pl-2.5 pr-16 text-sm outline-none transition ${fieldAlignmentClass} ${
              readOnly
                ? 'border-transparent bg-transparent text-slate-500'
                : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
            }`}
            style={fieldTextStyle}
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <a
              href={telHref || undefined}
              onClick={(event) => {
                if (!canUsePhoneActions) event.preventDefault();
              }}
              title={canUsePhoneActions ? 'Start call' : 'Enter a valid phone number'}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                canUsePhoneActions
                  ? 'text-[#008080] hover:bg-[#008080]/10 hover:text-[#008080]'
                  : 'cursor-not-allowed text-slate-300'
              }`}
            >
              <Phone className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => handleOpenSmsComposer(row, header)}
              disabled={!canUsePhoneActions}
              title={canUsePhoneActions ? 'Send sheet SMS' : 'Enter a valid phone number'}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                canUsePhoneActions
                  ? 'text-[#008080] hover:bg-[#008080]/10 hover:text-[#008080]'
                  : 'cursor-not-allowed text-slate-300'
              }`}
            >
              <Send className="h-4 w-4" />
            </button>
            <a
              href={whatsappHref || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                if (!canUsePhoneActions) event.preventDefault();
              }}
              title={canUsePhoneActions ? 'Send WhatsApp message' : 'Enter a valid phone number'}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                canUsePhoneActions
                  ? 'text-[#008080] hover:bg-[#008080]/10 hover:text-[#008080]'
                  : 'cursor-not-allowed text-slate-300'
              }`}
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>
      );
    }

    return (
      <input
        type={inputType}
        value={value ?? ''}
        onChange={(e) => handleCellChange(row.id, header.id, e.target.value)}
        placeholder={header.label}
        readOnly={readOnly}
        inputMode={inputMode}
        step={step}
        pattern={header.type === 'telephone' ? '[0-9+()\\-\\s]*' : undefined}
        className={`w-full rounded-md border px-2.5 py-2 text-sm outline-none transition ${fieldAlignmentClass} ${
          readOnly
            ? 'border-transparent bg-transparent text-slate-500'
            : 'border-transparent bg-transparent text-slate-700 focus:border-slate-200 focus:bg-white'
        }`}
        style={fieldTextStyle}
      />
    );
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Loading sheet...
      </div>
    );
  }

  if (error || !sheet) {
    const shouldOfferLogin = !user?.uid && `${error || ''}`.toLowerCase().includes('sign in');
    return (
      <div className="mx-auto max-w-3xl rounded-3xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm">
        <p>{error || 'Sheet not found.'}</p>
        {shouldOfferLogin && (
          <button
            type="button"
            onClick={() => setIsLoginModalOpen(true)}
            className="mt-4 inline-flex items-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-100"
          >
            Sign in to continue
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`sheet-viewer-themed flex min-h-0 ${isEmbedded ? 'h-full gap-0 overflow-hidden bg-[#f4f7fb]' : 'h-full gap-6 overflow-y-auto'}`}
      style={{ ...sheetThemeStyle, fontFamily: sheetRenderFontFamily }}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-visible">
        {!isEmbedded && (
          <header className="border-b border-slate-200 bg-white px-2 py-2 lg:px-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                {canShowBackButton && (
                  <button
                    type="button"
                    onClick={handleBackNavigation}
                    aria-label="Back to sheets"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                )}

                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <img src={sheetLogo} alt="ParrotSheets" className="h-12 w-auto shrink-0 object-contain" />
                    <div className="min-w-0">
                      <h1 className="truncate text-xl font-bold tracking-tight text-slate-900 lg:text-2xl">
                        {sheet.title}
                      </h1>

                      {sheet.description && (
                        <p className="mt-0 max-w-3xl text-sm text-slate-600 lg:text-base">
                          {sheet.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="relative shrink-0" ref={userMenuRef}>
                    <div className="lg:hidden">
                      {user ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setIsUserMenuOpen((prev) => !prev)}
                            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition hover:bg-slate-50"
                            aria-label="Open user menu"
                          >
                            {userProfile?.photoURL || user?.photoURL ? (
                              <img
                                src={userProfile?.photoURL || user?.photoURL}
                                alt="Profile"
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-purple-600">
                                <span className="text-xs font-medium text-white">{getUserInitials(user, userProfile)}</span>
                              </div>
                            )}
                          </button>

                          {isUserMenuOpen && (
                            <div className="absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl">
                              <div className="border-b border-slate-100 px-4 py-3">
                                <p className="truncate text-sm font-medium text-slate-900">{getUserDisplayName()}</p>
                                <p className="truncate text-xs text-slate-500">{user?.email}</p>
                              </div>

                              <button
                                type="button"
                                onClick={openProfileModal}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                              >
                                <User className="h-4 w-4" />
                                Profile
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  navigate('/settings');
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                              >
                                <Settings className="h-4 w-4" />
                                Settings
                              </button>

                              <button
                                type="button"
                                onClick={handleSignOut}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 transition hover:bg-rose-50"
                              >
                                <LogOut className="h-4 w-4" />
                                Sign out
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setIsLoginModalOpen(true)}
                          className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                        >
                          Sign in
                        </button>
                      )}
                    </div>

                    <div className="hidden lg:flex">
                      {user ? (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setIsUserMenuOpen((prev) => !prev)}
                            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                          >
                            {userProfile?.photoURL || user?.photoURL ? (
                              <img
                                src={userProfile?.photoURL || user?.photoURL}
                                alt="Profile"
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-purple-600">
                                <span className="text-xs font-medium text-white">{getUserInitials(user, userProfile)}</span>
                              </div>
                            )}
                            <div className="min-w-0 text-left">
                              <p className="max-w-[140px] truncate text-sm font-semibold text-slate-900">{getUserDisplayName()}</p>
                              <p className="max-w-[140px] truncate text-xs text-slate-500">{user?.email || 'Signed in'}</p>
                            </div>
                          </button>

                          {isUserMenuOpen && (
                            <div className="absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl">
                              <div className="border-b border-slate-100 px-4 py-3">
                                <p className="truncate text-sm font-medium text-slate-900">{getUserDisplayName()}</p>
                                <p className="truncate text-xs text-slate-500">{user?.email}</p>
                              </div>

                              <button
                                type="button"
                                onClick={openProfileModal}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                              >
                                <User className="h-4 w-4" />
                                Profile
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  navigate('/settings');
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                              >
                                <Settings className="h-4 w-4" />
                                Settings
                              </button>

                              <button
                                type="button"
                                onClick={handleSignOut}
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 transition hover:bg-rose-50"
                              >
                                <LogOut className="h-4 w-4" />
                                Sign out
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setIsLoginModalOpen(true)}
                          className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                        >
                          Sign in
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </header>
        )}

        {isAdminReadOnlyView && !isEmbedded && (
          <div className="border-b border-teal-200 bg-teal-50 px-4 py-2 text-sm font-medium text-teal-800 lg:px-10">
            Admin view is read-only. You can inspect this sheet because its owner enabled admin access.
          </div>
        )}

        <section className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${isEmbedded ? 'p-3 lg:p-4' : 'px-1 pb-0 pt-2 lg:px-10'}`}>
          <div className={`flex flex-col gap-3 ${isEmbedded ? 'mb-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-[0_16px_38px_-32px_rgba(15,23,42,0.55)]' : 'sticky top-0 z-20 -mx-2 mb-2 bg-[linear-gradient(180deg,rgba(248,250,252,0.98)_0%,rgba(248,250,252,0.95)_82%,rgba(248,250,252,0)_100%)] px-2 pb-3 backdrop-blur-sm lg:static lg:mx-0 lg:bg-none lg:px-0 lg:pb-0 lg:backdrop-blur-0'}`}>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className={`${isEmbedded ? '' : 'ml-2 '}flex flex-wrap items-center gap-2`}>
                    <h2 className="inline-flex items-center gap-2 rounded-lg border border-[#008080]/20 bg-[#008080]/10 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#008080]">
                      <TableProperties className="h-3.5 w-3.5" />
                      Smart Sheet
                    </h2>
                    {!isAdminReadOnlyView && (hasPublishedSheetForm || isOwner) && (
                      <button
                        type="button"
                        onClick={handleOpenSheetForm}
                        className="inline-flex items-center gap-2 rounded-lg border border-[#008080]/20 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#008080] transition hover:bg-[#008080]/5"
                      >
                        {hasPublishedSheetForm ? <ExternalLink className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        {hasPublishedSheetForm ? 'Open Form' : 'Add Form'}
                      </button>
                    )}
                  </div>
                </div>
                {(isOwner || canCreateRows || sheetActionForms.length > 0 || filterHeaders.length > 0 || hasAnalytics || realtimeFailed || csvColumns.length > 0 || (user?.uid && !isAdminReadOnlyView)) && (
                  <div className={`flex flex-wrap items-center justify-end gap-2 lg:w-auto lg:shrink-0 lg:flex-nowrap ${isEmbedded ? '' : 'rounded-md bg-white/80 px-2'}`}>
                    <input
                      ref={csvImportInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleImportCsv}
                      className="hidden"
                    />
                    {filterHeaders.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowFilters((prev) => !prev)}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border text-sm font-semibold shadow-lg transition-colors sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2 sm:whitespace-nowrap ${
                          showFilters || hasActiveFilters
                            ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080] hover:bg-[#008080]/15'
                            : 'border-white bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        aria-label={showFilters ? 'Hide filters' : 'Filters'}
                      >
                        <Filter className="h-4 w-4" />
                        <span className="hidden sm:inline">{showFilters ? 'Hide filters' : 'Filters'}</span>
                      </button>
                    )}
                    {!isEmbedded && hasAnalytics && user?.uid && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowChartsSidebar((prev) => !prev);
                          if (!showChartsSidebar) setComposerState(null);
                        }}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border text-sm font-semibold shadow-lg transition-colors sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2 sm:whitespace-nowrap ${
                          showChartsSidebar
                            ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080] hover:bg-[#008080]/15'
                            : 'border-white bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        aria-label={showChartsSidebar ? 'Hide analytics' : 'Analytics'}
                      >
                        <BarChart3 className="h-4 w-4" />
                        <span className="hidden sm:inline">{showChartsSidebar ? 'Hide analytics' : 'Analytics'}</span>
                      </button>
                    )}
                    {user?.uid && !isAdminReadOnlyView && (
                      <button
                        type="button"
                        onClick={handleOpenSheetAi}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#008080]/30 bg-[#008080]/10 text-sm font-semibold text-[#008080] shadow-lg transition-colors hover:bg-[#008080]/15 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Create AI report"
                      >
                        <Sparkles className="h-4 w-4" />
                        <span className="hidden sm:inline">AI Report</span>
                      </button>
                    )}
                    {realtimeFailed && (
                      <button
                        type="button"
                        onClick={handleRefreshSheet}
                        disabled={refreshing}
                        title="Refresh sheet and retry live updates"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-700 shadow-lg transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-70 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2"
                        aria-label={refreshing ? 'Refreshing' : 'Refresh'}
                      >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                      </button>
                    )}
                    {sheetActionForms.length > 0 && (
                      <button
                        type="button"
                        onClick={handleOpenActionSidebar}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white bg-white text-sm font-semibold text-slate-700 shadow-lg transition-colors hover:bg-slate-50 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Open action forms"
                      >
                        <FileText className="h-4 w-4" />
                        <span className="hidden sm:inline">Actions</span>
                      </button>
                    )}
                    {csvColumns.length > 0 && (
                      <button
                        type="button"
                        onClick={handleDownloadCsvTemplate}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white bg-white text-sm font-semibold text-slate-700 shadow-lg transition-colors hover:bg-slate-50 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Download CSV template"
                      >
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">CSV template</span>
                      </button>
                    )}
                    {canCreateRows && csvColumns.length > 0 && (
                      <button
                        type="button"
                        onClick={handleOpenCsvImport}
                        disabled={importingCsv}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white bg-white text-sm font-semibold text-slate-700 shadow-lg transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label={importingCsv ? 'Importing CSV' : 'Import CSV'}
                      >
                        <Upload className="h-4 w-4" />
                        <span className="hidden sm:inline">{importingCsv ? 'Importing...' : 'Import CSV'}</span>
                      </button>
                    )}
                    {canCreateRows && (
                      <button
                        type="button"
                        onClick={handleAddRow}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white bg-white text-sm font-semibold text-slate-700 shadow-lg transition-colors hover:bg-slate-50 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Add row"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add row</span>
                      </button>
                    )}
                    {canEditAnyRow && (
                      <button
                        type="button"
                        onClick={() => setSheetEditMode((prev) => !prev)}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-lg border text-sm font-semibold shadow-lg transition-colors sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap ${
                          sheetEditMode
                            ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080] hover:bg-[#008080]/15'
                            : 'border-white bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        aria-label={sheetEditMode ? 'Exit edit mode' : 'Switch to edit mode'}
                      >
                        {sheetEditMode ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        <span className="hidden sm:inline">{sheetEditMode ? 'Rendered view' : 'Edit mode'}</span>
                      </button>
                    )}
                    {activeRow && sidebarView === 'details' && rowDetailMode === 'edit' && (
                      <button
                        type="button"
                        onClick={() => setRowDetailMode('view')}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#008080]/30 bg-[#008080]/10 text-[#008080] text-sm font-semibold shadow-sm transition-colors hover:bg-[#008080]/15 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Switch to rendered view"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline">Rendered view</span>
                      </button>
                    )}
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => navigate(`/sheets/${sheet.id}/edit`, {
                          state: {
                            returnTo: location.pathname,
                            returnToState: location.state
                          }
                        })}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-lg transition-colors hover:bg-slate-50 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label="Edit Sheet"
                      >
                        <TableProperties className="h-4 w-4" />
                        <span className="hidden sm:inline">Edit Sheet</span>
                      </button>
                    )}
                    {hasPendingRowChanges && (
                      <button
                        type="button"
                        onClick={handleSaveRows}
                        disabled={saving}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#008080] text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70 sm:h-auto sm:w-auto sm:gap-2 py-1 sm:px-4 sm:py-2 sm:whitespace-nowrap"
                        aria-label={saving ? 'Saving changes' : 'Save changes'}
                      >
                        <Save className="h-4 w-4" />
                        <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save changes'}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {csvImportSummary && (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#008080]">CSV Import</p>
                      <p className="mt-1 text-sm text-slate-700">
                        Imported {csvImportSummary.importedCount} row{csvImportSummary.importedCount === 1 ? '' : 's'} from {csvImportSummary.fileName}.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCsvImportSummary(null)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                      aria-label="Dismiss CSV import summary"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                    {csvImportSummary.skippedRowCount > 0 && (
                      <span className="rounded-full bg-slate-100 px-3 py-1">
                        Skipped empty rows: {csvImportSummary.skippedRowCount}
                      </span>
                    )}
                    {csvImportSummary.ignoredColumns.length > 0 && (
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">
                        Ignored columns: {csvImportSummary.ignoredColumns.join(', ')}
                      </span>
                    )}
                    {csvImportSummary.missingColumns.length > 0 && (
                      <span className="rounded-full bg-slate-100 px-3 py-1">
                        Left blank: {csvImportSummary.missingColumns.join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(showFilters || (!isEmbedded && hasActiveFilters)) && filterHeaders.length > 0 && (
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Row Filters</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Filter the visible rows using the columns chosen in the sheet builder.
                      </p>
                    </div>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="inline-flex items-center gap-2 self-start rounded-xl border border-[#008080]0 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                      >
                        <X className="h-4 w-4" />
                        Clear filters
                      </button>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {filterHeaders.map((header) => {
                      const serverFilter = isEmbedded ? analyticsFilterOptionsByHeaderId[header.id] : null;
                      const fallbackOptions = getFilterOptionsForHeader(header, resolvedRows, headerOptionRowsBySheetId, resolvedAllHeaders);
                      const filterOptions = Array.isArray(serverFilter?.options) ? serverFilter.options : fallbackOptions;

                      return (
                        <div key={`${header.id}_filter_control`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {header.label}
                          </label>
                          <select
                            value={activeFilters[header.id] || ''}
                            onChange={(e) => handleFilterChange(header.id, e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="">All values</option>
                            {filterOptions.map((option) => (
                              <option key={`${header.id}_${option.value}`} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {serverFilter?.truncated && <p className="mt-1.5 text-[11px] text-amber-700">Showing the first 250 matching values.</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {isEmbedded && hasAnalytics && user?.uid && (
                <section className="mt-3 overflow-hidden rounded-2xl border border-[#008080]/20 bg-white shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <button
                      type="button"
                      onClick={toggleEmbeddedAnalytics}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      aria-expanded={embeddedAnalyticsExpanded}
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#008080]/10 text-[#008080]"><BarChart3 className="h-4 w-4" /></span>
                      <span className="min-w-0"><span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#008080]">Sheet analytics</span><span className="block truncate text-sm text-slate-500">Filter this sheet and its published insights together.</span></span>
                    </button>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={toggleEmbeddedAnalytics} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label={embeddedAnalyticsExpanded ? 'Collapse analytics' : 'Expand analytics'}>{embeddedAnalyticsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                    </div>
                  </div>
                  {embeddedAnalyticsExpanded && (
                    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-4 py-4">
                      {analyticsLoading && analyticsResults.length > 0 && <div className="flex justify-end"><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#008080]"><RefreshCw className="h-3.5 w-3.5 animate-spin" />Updating...</span></div>}
                      {analyticsLoading && analyticsResults.length === 0 && <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-500">Calculating exact insights...</div>}
                      {analyticsError && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{analyticsError}</div>}
                      {analyticsResults.length > 0 && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{analyticsResults.map((result) => <div key={result.id} className={result.visual?.size === 'wide' ? 'md:col-span-2' : ''}><SheetAnalyticsResult result={result} primaryColor={sheetPrimaryColor} /></div>)}</div>}
                    </div>
                  )}
                </section>
              )}
              {selectedRowIds.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-[#008080]/20 bg-[#008080]/10 px-4 py-3 text-sm text-[#0f4f4f]">
                  <span className="font-semibold">
                    {selectedRowIds.length} row{selectedRowIds.length === 1 ? '' : 's'} selected
                  </span>
                  {hasEmailHeaders && (
                    <>
                      <span className="text-[#008080]">
                        {selectedEmailCount} valid email{selectedEmailCount === 1 ? '' : 's'} available
                      </span>
                      <button
                        type="button"
                        onClick={handleOpenBatchEmailComposer}
                        disabled={!selectedEmailCount}
                        className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 font-semibold text-[#008080] transition hover:bg-[#008080]/15 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Mail className="h-4 w-4" />
                        Email selected
                      </button>
                    </>
                  )}
                  {hasPhoneHeaders && (
                    <>
                      <span className="text-[#008080]">
                        {selectedPhoneCount} valid phone{selectedPhoneCount === 1 ? '' : 's'} available
                      </span>
                      <button
                        type="button"
                        onClick={handleOpenBatchSmsComposer}
                        disabled={!selectedPhoneCount}
                        className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 font-semibold text-[#008080] transition hover:bg-[#008080]/15 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" />
                        SMS selected
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedRowIds([])}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 font-semibold text-slate-600 transition hover:bg-white"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_60px_-36px_rgba(15,23,42,0.4)] lg:min-h-[420px]">
          <div ref={gridScrollRef} className="min-h-0 flex-1 p-1 overflow-auto">
            {usesPresetRenderLayout && !sheetEditMode ? (
              usesKanbanPresetLayout ? (
                <div className={`grid h-full gap-4 overflow-y-auto bg-slate-50 p-1 md:p-5 ${
                  sheetRenderPresetConfig.uiColumns <= 1
                    ? 'grid-cols-1'
                    : sheetRenderPresetConfig.uiColumns === 2
                      ? 'grid-cols-1 md:grid-cols-2'
                      : sheetRenderPresetConfig.uiColumns === 3
                        ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                        : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'
                }`} style={embeddedPresetGridStyle}>
                  {kanbanColumns.length === 0 ? (
                    <div className="flex min-h-[240px] w-full items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
                      No status columns or matching rows available for this board.
                    </div>
                  ) : (
                    kanbanColumns.map((column) => (
                      <section key={column.id} className="flex min-h-[320px] min-w-0 flex-col rounded-3xl border border-slate-200 bg-white/90 shadow-sm">
                        <div className="border-b border-slate-200 px-4 py-4">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-700">{column.label}</h3>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                              {column.rows.length}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 space-y-3 overflow-y-auto p-3">
                          {column.rows.length ? column.rows.map((row, rowIndex) => {
                            const fallbackLabel = getRowReferenceLabel(row, rowIndex + 1);
                            const presetData = getKanbanPresetData(row, fallbackLabel);
                            return (
                              <div key={row.id} className="relative">
                                <label className="absolute left-3 top-3 z-10 inline-flex items-center gap-2 rounded-full bg-white/95 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm">
                                  <input
                                    type="checkbox"
                                    checked={selectedRowIds.includes(row.id)}
                                    onChange={() => handleToggleRowSelection(row.id)}
                                    aria-label={`Select ${fallbackLabel}`}
                                    className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                  />
                                  Pick
                                </label>
                                <div className="pt-8">
                                  <SheetKanbanPresetCard
                                    row={row}
                                    title={presetData.title}
                                    body={presetData.body}
                                    meta={presetData.meta}
                                    primaryColor={sheetPrimaryColor}
                                    secondaryColor={sheet?.secondaryColor || ''}
                                    actionLabel={getRowPrimaryActionConfig(row.id)?.label || ''}
                                    actionNotice={getRowActiveActionSummary(row.id)?.primaryLabel || ''}
                                    onActionClick={() => handleRunConfiguredRowAction(row.id, getRowActiveActionSummary(row.id)?.entries?.[0]?.formId || '')}
                                    actionItems={getRowActionMenuItems(row.id)}
                                    isActionMenuOpen={openRowActionMenuRowId === row.id}
                                    onToggleActionMenu={() => setOpenRowActionMenuRowId((current) => current === row.id ? '' : row.id)}
                                    onSelectActionItem={(actionItem) => handleSelectRowActionItem(row.id, actionItem)}
                                    showOpenButton
                                    onOpen={() => handleOpenRowDetail(row.id)}
                                  />
                                </div>
                              </div>
                            );
                          }) : (
                            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                              No rows in this column.
                            </div>
                          )}
                        </div>
                      </section>
                    ))
                  )}
                </div>
              ) : usesCalendarPresetLayout ? (
                <div className="bg-slate-50 p-3 md:p-5">
                  <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <button
                      type="button"
                      onClick={() => {
                        if (calendarPresetView === 'day') {
                          const nextDate = addDays(calendarFocusDate, -1);
                          setCalendarFocusDate(nextDate);
                          setCalendarViewMonth(getMonthStart(nextDate));
                          return;
                        }
                        if (calendarPresetView === 'week') {
                          const nextDate = addDays(calendarFocusDate, -7);
                          setCalendarFocusDate(nextDate);
                          setCalendarViewMonth(getMonthStart(nextDate));
                          return;
                        }
                        setCalendarViewMonth((prev) => addMonths(prev, -1));
                      }}
                      className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Prev
                    </button>
                    <div className="text-center">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Calendar View</p>
                      <h3 className="text-lg font-bold text-slate-900">
                        {calendarPresetView === 'day'
                          ? calendarFocusDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                          : calendarPresetView === 'week'
                            ? `${calendarSelectedWeekDays[0]?.date?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) || ''} - ${calendarSelectedWeekDays[6]?.date?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) || ''}`
                            : calendarViewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (calendarPresetView === 'day') {
                          const nextDate = addDays(calendarFocusDate, 1);
                          setCalendarFocusDate(nextDate);
                          setCalendarViewMonth(getMonthStart(nextDate));
                          return;
                        }
                        if (calendarPresetView === 'week') {
                          const nextDate = addDays(calendarFocusDate, 7);
                          setCalendarFocusDate(nextDate);
                          setCalendarViewMonth(getMonthStart(nextDate));
                          return;
                        }
                        setCalendarViewMonth((prev) => addMonths(prev, 1));
                      }}
                      className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    {[
                      { key: 'month', label: 'Month', enabled: true },
                      { key: 'week', label: 'Week', enabled: selectedWeekHasRows },
                      { key: 'day', label: 'Day', enabled: calendarSelectedDayRows.length > 0 }
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => option.enabled && setCalendarPresetView(option.key)}
                        disabled={!option.enabled}
                        className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                          calendarPresetView === option.key
                            ? 'bg-slate-900 text-white'
                            : option.enabled
                              ? 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                              : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {calendarPresetView === 'day' ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Day Agenda</p>
                          <h4 className="text-base font-bold text-slate-900">
                            {calendarFocusDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                          </h4>
                        </div>
                        <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          {calendarSelectedDayRows.length} item{calendarSelectedDayRows.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {calendarSelectedDayRows.map((item) => (
                          <SheetCalendarPresetCard
                            key={item.row.id}
                            title={item.title}
                            meta={getRowActiveActionSummary(item.row.id)?.primaryLabel || item.meta}
                            dateLabel={item.date ? item.date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                            imageUrl={item.imageUrl}
                            tone={item.row.isDirty || item.row.isNew ? 'accent' : 'default'}
                                    primaryColor={sheetPrimaryColor}
                            secondaryColor={sheet?.secondaryColor || ''}
                            onOpen={() => handleOpenRowDetail(item.row.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ) : calendarPresetView === 'week' ? (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
                      {calendarSelectedWeekDays.map((day) => (
                        <div
                          key={day.key}
                          className={`rounded-3xl border p-3 shadow-sm ${day.isFocused ? 'border-[#008080]/30 bg-[#008080]/5' : 'border-slate-200 bg-white'}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setCalendarFocusDate(day.date);
                              setCalendarViewMonth(getMonthStart(day.date));
                              if (day.rows.length) {
                                setCalendarPresetView('day');
                              }
                            }}
                            className="mb-3 block w-full text-left"
                          >
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                              {day.date.toLocaleDateString(undefined, { weekday: 'short' })}
                            </p>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <h4 className="text-base font-bold text-slate-900">{day.date.getDate()}</h4>
                              <span className="text-[11px] font-semibold text-slate-400">{day.rows.length || ''}</span>
                            </div>
                          </button>
                          <div className="space-y-2">
                            {day.rows.length ? day.rows.slice(0, 4).map((item) => (
                              <SheetCalendarPresetCard
                                key={item.row.id}
                                title={item.title}
                                meta={getRowActiveActionSummary(item.row.id)?.primaryLabel || item.meta}
                                dateLabel={item.date ? item.date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                imageUrl={item.imageUrl}
                                tone={item.row.isDirty || item.row.isNew ? 'accent' : 'default'}
                                    primaryColor={sheetPrimaryColor}
                                secondaryColor={sheet?.secondaryColor || ''}
                                onOpen={() => handleOpenRowDetail(item.row.id)}
                              />
                            )) : (
                              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-400">
                                No rows
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-7 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} className="border-b border-slate-200 bg-slate-50 px-2 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {day}
                        </div>
                      ))}
                      {calendarDays.map((day) => (
                        <button
                          type="button"
                          key={day.key}
                          onClick={() => {
                            setCalendarFocusDate(day.date);
                            setCalendarViewMonth(getMonthStart(day.date));
                            if (day.rows.length) {
                              setCalendarPresetView('day');
                              return;
                            }
                            const weekStart = getWeekStart(day.date);
                            const weekHasRows = Array.from({ length: 7 }, (_, index) => (
                              (calendarRowsByDateKey[getDateKey(addDays(weekStart, index))] || []).length > 0
                            )).some(Boolean);
                            if (weekHasRows) {
                              setCalendarPresetView('week');
                            }
                          }}
                          className={`min-h-[132px] border-b border-r border-slate-100 p-2 text-left align-top ${day.inMonth ? 'bg-white' : 'bg-slate-50/80'}`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                              day.inMonth ? 'bg-slate-100 text-slate-700' : 'bg-transparent text-slate-400'
                            }`}>
                              {day.date.getDate()}
                            </span>
                            {day.rows.length ? (
                              <span className="text-[11px] font-semibold text-slate-400">{day.rows.length}</span>
                            ) : null}
                          </div>
                          <div className="space-y-2">
                            {day.rows.slice(0, 3).map((item) => (
                              <SheetCalendarEventChip
                                key={item.row.id}
                                title={item.title}
                                meta={getRowActiveActionSummary(item.row.id)?.primaryLabel || item.meta || (item.date ? item.date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '')}
                                imageUrl={item.imageUrl}
                                tone={item.row.isDirty || item.row.isNew ? 'accent' : 'default'}
                                    primaryColor={sheetPrimaryColor}
                                secondaryColor={sheet?.secondaryColor || ''}
                                titleColor={item.titleColor}
                                metaColor={item.metaColor}
                                onOpen={() => handleOpenRowDetail(item.row.id)}
                              />
                            ))}
                            {day.rows.length > 3 ? (
                              <div className="text-xs font-semibold text-[#008080]">
                                +{day.rows.length - 3} more
                              </div>
                            ) : null}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
              <div className={`grid gap-3 bg-slate-50 p-0 md:gap-5 md:p-5 ${
                sheetRenderPresetConfig.uiColumns <= 1
                  ? 'grid-cols-1'
                  : sheetRenderPresetConfig.uiColumns === 2
                    ? 'grid-cols-1 md:grid-cols-2'
                    : sheetRenderPresetConfig.uiColumns === 3
                      ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                      : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'
              }`} style={embeddedPresetGridStyle}>
                {filteredRows.length === 0 ? (
                  <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
                    No rows match the current filters.
                  </div>
                ) : (
                  filteredRows.map((row, rowIndex) => (
                    (() => {
                      const rowLabel = getRowReferenceLabel(row, rowIndex + 1);
                      const profilePresetData = getProfilePresetData(row, rowLabel);
                      const rowActionSummary = getRowActiveActionSummary(row.id);
                      const rowPrimaryAction = getRowPrimaryActionConfig(row.id);
                      return (
                        <div key={row.id} className="relative">
                          <label className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
                            <input
                              type="checkbox"
                              checked={selectedRowIds.includes(row.id)}
                              onChange={() => handleToggleRowSelection(row.id)}
                              aria-label={`Select row ${rowIndex + 1}`}
                              className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                            />
                            Pick
                          </label>
                          {usesProfilePresetLayout ? (
                            <SheetProfilePresetCard
                              row={row}
                              profileHeader={presetProfileHeader}
                              profile={getProfileDisplayData(presetProfileHeader, row.resolvedValues || row.values || {})}
                              cardLabel={rowLabel}
                              title={profilePresetData.title}
                              body={profilePresetData.body}
                                    primaryColor={sheetPrimaryColor}
                              secondaryColor={sheet?.secondaryColor || ''}
                              actionContent={profilePresetData.actionHeader ? renderCellView(row, profilePresetData.actionHeader) : null}
                              actionNotice={getRowActiveActionSummary(row.id)?.primaryLabel || ''}
                              onActionClick={() => handleRunConfiguredRowAction(row.id, getRowActiveActionSummary(row.id)?.entries?.[0]?.formId || '')}
                              actionItems={getRowActionMenuItems(row.id)}
                              isActionMenuOpen={openRowActionMenuRowId === row.id}
                              onToggleActionMenu={() => setOpenRowActionMenuRowId((current) => current === row.id ? '' : row.id)}
                              onSelectActionItem={(actionItem) => handleSelectRowActionItem(row.id, actionItem)}
                              showOpenButton
                              onOpen={() => handleOpenRowDetail(row.id)}
                            />
                          ) : (
                            <SheetListCardPreset
                              row={row}
                              {...getListCardPresetData(row, rowLabel)}
                                    primaryColor={sheetPrimaryColor}
                              secondaryColor={sheet?.secondaryColor || ''}
                              actionLabel={rowPrimaryAction?.label || ''}
                              actionNotice={rowActionSummary?.primaryLabel || ''}
                              onActionClick={() => handleRunConfiguredRowAction(row.id, rowPrimaryAction?.formId || '')}
                              actionItems={getRowActionMenuItems(row.id)}
                              isActionMenuOpen={openRowActionMenuRowId === row.id}
                              onToggleActionMenu={() => setOpenRowActionMenuRowId((current) => current === row.id ? '' : row.id)}
                              onSelectActionItem={(actionItem) => handleSelectRowActionItem(row.id, actionItem)}
                              showOpenButton
                              onOpen={() => handleOpenRowDetail(row.id)}
                            />
                          )}
                        </div>
                      );
                    })()
                  ))
                )}
              </div>
              )
            ) : (
            <table
              className="w-full border-collapse"
              style={{ minWidth: `${tableMinWidth}px` }}
            >
            <thead>
              <tr>
                <th
                  className="sticky left-0 top-0 z-30 border-r border-b border-slate-200 bg-slate-50 px-4 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                  style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                >
                  <input
                    type="checkbox"
                    checked={allRowsSelected}
                    onChange={handleToggleAllRows}
                    aria-label="Select all rows"
                    className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                  />
                </th>
                <th
                  className="sticky left-[56px] top-0 z-30 border-r border-b border-slate-200 bg-slate-50 px-4 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                  style={{ minWidth: `${META_COLUMN_WIDTH}px`, width: `${META_COLUMN_WIDTH}px` }}
                />
                {sheetGridHeaders.map((header, index) => (
                  <th
                    key={`${header.id}-letter`}
                    className="sticky top-0 z-20 border-r border-b border-slate-200 bg-slate-50 px-6 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                    style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                  >
                    {getColumnLetter(index)}
                  </th>
                ))}
                <th
                  className="sticky top-0 z-20 border-r border-b border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                  style={{ minWidth: `${META_COLUMN_WIDTH}px` }}
                />
                <th
                  className="sticky top-0 z-20 border-r border-b border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                  style={{ minWidth: `${META_COLUMN_WIDTH * 2}px` }}
                />
                {isOwner && isOwnRowsMode && (
                  <th
                    className="sticky top-0 z-20 border-r border-b border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                    style={{ minWidth: `${META_COLUMN_WIDTH * 2.5}px` }}
                  />
                )}
                <th
                  className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400"
                  style={{ minWidth: `${META_COLUMN_WIDTH * 2}px` }}
                />
              </tr>
              <tr>
                <th
                  className="sticky left-0 top-[40px] z-20 border-r border-b border-slate-200 bg-white px-4 py-2 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500"
                  style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                >
                  Pick
                </th>
                <th
                  className="sticky left-[56px] top-[40px] z-20 border-r border-b border-slate-200 bg-white px-4 py-2 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500"
                  style={{ minWidth: `${META_COLUMN_WIDTH}px`, width: `${META_COLUMN_WIDTH}px` }}
                >
                  Row
                </th>
                {sheetGridHeaders.map((header) => (
                  <th
                    key={header.id}
                    className="group sticky top-[40px] z-10 border-r border-b border-slate-200 bg-white px-6 py-2 text-left text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700 relative"
                    style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                  >
                    {header.label}
                    {isOwner && (
                      <button
                        type="button"
                        aria-label={`Resize ${header.label} column`}
                        onMouseDown={(event) => handleResizeStart(event, header.id)}
                        className="absolute inset-y-0 right-0 z-20 cursor-col-resize opacity-0 transition group-hover:opacity-100"
                        style={{ width: `${RESIZE_HANDLE_WIDTH}px` }}
                      >
                        <span className="absolute inset-y-1 right-1 w-px bg-[#008080]" />
                      </button>
                    )}
                  </th>
                ))}
                <th
                  className="sticky top-[40px] z-10 border-r border-b border-slate-200 bg-white px-4 py-1 text-left text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700"
                  style={{ minWidth: `${META_COLUMN_WIDTH}px` }}
                >
                  State
                </th>
                <th
                  className="sticky top-[40px] z-10 border-r border-b border-slate-200 bg-white px-4 py-1 text-left text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700"
                  style={{ minWidth: `${META_COLUMN_WIDTH * 2}px` }}
                >
                  Updated
                </th>
                {isOwner && isOwnRowsMode && (
                  <th
                    className="sticky top-[40px] z-10 border-r border-b border-slate-200 bg-white px-4 py-1 text-left text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700"
                    style={{ minWidth: `${META_COLUMN_WIDTH * 2.5}px` }}
                  >
                    By
                  </th>
                )}
                <th
                  className="sticky top-[40px] z-10 border-b border-slate-200 bg-white px-4 py-1 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700"
                  style={{ minWidth: `${META_COLUMN_WIDTH * 2}px` }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredRows.length === 0 && (
                <tr className="bg-white">
                  <td
                    colSpan={tableColSpan}
                    className="px-6 py-8 text-center text-sm text-slate-500"
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              )}
              {filteredRows.map((row, rowIndex) => {
                const columnHeaders = getGridHeadersForRow(row);

                return (
                  <tr
                    key={row.id}
                    className={`group ${row.isNew ? 'bg-[#008080]/10' : 'bg-white'} transition-colors hover:bg-slate-50/50`}
                  >
                    <td
                      className="sticky left-0 z-10 border-r border-slate-100 bg-inherit px-4 py-1 text-center"
                      style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRowIds.includes(row.id)}
                        onChange={() => handleToggleRowSelection(row.id)}
                        aria-label={`Select row ${rowIndex + 1}`}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                    </td>
                    <td
                      className="sticky left-[56px] z-10 bg-inherit px-4 py-1 text-center text-[15px] font-medium text-slate-400"
                      style={{ minWidth: `${META_COLUMN_WIDTH}px`, width: `${META_COLUMN_WIDTH}px` }}
                    >
                      <button
                        type="button"
                        onClick={() => handleOpenRowDetail(row.id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[15px] font-semibold text-slate-500 transition hover:bg-slate-200/70 hover:text-slate-900"
                        title={`Open row ${rowIndex + 1}`}
                      >
                        {rowIndex + 1}
                      </button>
                    </td>

                    {columnHeaders.map((header) => (
                      <td
                        key={header.id}
                        className="px-4 py-0 align-top"
                        style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                      >
                        {sheetEditMode && (row.isNew ? canCreateRows : canEditSavedRow(row))
                          ? renderCellInput(row, header)
                          : renderCellView(row, header, { compact: true })}
                      </td>
                    ))}

                    <td className="px-4 py-1 text-xs">
                      <span className={`inline-flex rounded-full px-2 py-0 font-semibold ${
                        row.isNew
                          ? 'bg-[#008080]/15 text-[#008080]'
                          : row.isDirty
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {row.isNew ? 'Draft' : row.isDirty ? 'Edited' : 'Saved'}
                      </span>
                    </td>

                    <td className="px-4 py-1 text-xs text-slate-500">
                      {row.isNew ? 'Not saved yet' : formatDate(row.updatedAt || row.createdAt)}
                    </td>

                    {isOwner && isOwnRowsMode && (
                      <td className="px-4 py-1 text-xs text-slate-500">
                        {row.isNew ? 'Current user' : (row.createdByEmail || row.createdBy || 'Unknown')}
                      </td>
                    )}

                    <td className="px-4 py-1 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleOpenRowDetail(row.id)}
                          className="inline-flex items-center justify-center rounded-md px-2 py-2 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                          title="Open row details"
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicateRow(row)}
                          disabled={!canCreateRows}
                          className="inline-flex items-center justify-center gap-1 rounded-md px-2 py-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          title="Duplicate row"
                        >
                          <Copy className="h-4 w-4" />
                          <span className="text-xs font-semibold sm:hidden">Copy</span>
                        </button>
                        {(row.isNew || canEditSavedRow(row)) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row)}
                            className="inline-flex items-center justify-center gap-1 rounded-md px-2 py-2 text-rose-400 transition hover:bg-rose-50 hover:text-rose-600"
                            title={row.isNew ? 'Remove draft row' : 'Delete row'}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="text-xs font-semibold sm:hidden">{row.isNew ? 'Remove' : 'Delete'}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {hasSummaryRow && (
              <tfoot>
                <tr className="bg-slate-50">
                  <td
                    className="sticky bottom-0 left-0 z-10 border-r border-t border-slate-200 bg-slate-50 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500"
                    style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                  >
                    Sum
                  </td>
                  <td
                    className="sticky bottom-0 left-[56px] z-10 border-r border-t border-slate-200 bg-slate-50 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500"
                    style={{ minWidth: `${META_COLUMN_WIDTH}px`, width: `${META_COLUMN_WIDTH}px` }}
                  >
                    Row
                  </td>
                  {sheetGridHeaders.map((header) => {
                    const summaryType = `${header.summaryType || 'none'}`.trim();
                    const summaryValue = summaryRow[header.id];
                    return (
                      <td
                        key={`${header.id}_summary`}
                        className="border-r border-t border-slate-200 bg-slate-50 px-4 py-2 align-top text-xs last:border-r-0"
                        style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                      >
                        {summaryType !== 'none' && summaryValue !== null && summaryValue !== undefined ? (
                          <div className="space-y-1">
                            <p className="font-semibold uppercase tracking-[0.18em] text-slate-400">{SUMMARY_LABELS[summaryType] || 'Value'}</p>
                            <p className="text-sm font-semibold text-slate-700">{getSummaryDisplayValue(header, summaryValue, summaryType)}</p>
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="border-r border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-400">
                    Visible
                  </td>
                  <td className="border-r border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-400">
                    {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}
                  </td>
                  {isOwner && isOwnRowsMode && (
                    <td className="border-r border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-400">
                      Filtered set
                    </td>
                  )}
                  <td className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-400">
                    Summary
                  </td>
                </tr>
              </tfoot>
            )}
            </table>
            )}
          </div>
          </div>
        </section>
        {!isEmbedded && (
          <footer className="px-6 pb-[5px] text-center text-xs text-slate-400 lg:px-10">
            &copy; ParrotSheets 2026
          </footer>
        )}
      </div>

      {!isEmbedded && showChartsSidebar && hasAnalytics && (
        <div className="fixed inset-0 z-50 flex bg-slate-950/35">
          <button
            type="button"
            aria-label="Close analytics"
            onClick={() => setShowChartsSidebar(false)}
            className="hidden h-full flex-1 cursor-default lg:block"
          />
          <aside className="ml-auto flex h-full w-full max-w-md flex-col overflow-hidden bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#008080]">Data insights</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">Analytics</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowChartsSidebar(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close analytics"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <p className="text-xs text-slate-500">
                {selectedRows.length > 0
                  ? `Showing ${selectedRows.length} selected row${selectedRows.length === 1 ? '' : 's'}`
                  : `Showing ${filteredRows.length} filtered row${filteredRows.length === 1 ? '' : 's'}`}
              </p>
              {analyticsLoading && <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">Calculating exact insights…</div>}
              {analyticsError && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{analyticsError}</div>}
              {!analyticsLoading && !analyticsError && analyticsResults.map((result) => (
                <SheetAnalyticsResult key={result.id} result={result} primaryColor={sheetPrimaryColor} />
              ))}
            </div>
          </aside>
        </div>
      )}

      {instantActionPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6">
          <button
            type="button"
            aria-label="Close action prompt"
            onClick={() => setInstantActionPrompt(null)}
            className="absolute inset-0"
          />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#008080]">Quick Action</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  {instantActionPrompt.actionConfig?.buttonLabel || instantActionPrompt.actionConfig?.name || 'Add row'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Choose the remaining values, then save directly to {instantActionPrompt.linkedSheet?.title || 'the target sheet'}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInstantActionPrompt(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close action prompt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-5">
              {instantActionPromptHeaders.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No additional values are needed for this action.
                </div>
              ) : (
                <div className="grid gap-4">
                  {instantActionPromptHeaders.map((header) => (
                    <div key={`instant_action_field_${header.id}`}>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {header.label}
                      </label>
                      {renderInstantActionPromptFieldInput(header)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-5 py-4">
              <button
                type="button"
                onClick={() => setInstantActionPrompt(null)}
                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitInstantActionPrompt}
                disabled={instantActionPrompt.saving === true}
                className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <Save className="h-4 w-4" />
                {instantActionPrompt.saving === true
                  ? 'Saving...'
                  : (instantActionPrompt.actionConfig?.submitLabel || `Save ${instantActionPrompt.actionConfig?.buttonLabel || instantActionPrompt.actionConfig?.name || 'action'}`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {composerState && (
        <aside className="flex h-full w-full max-w-md flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#008080]">
                {composerState.channel === 'sms'
                  ? composerState.mode === 'batch' ? 'Batch SMS Composer' : 'SMS Composer'
                  : composerState.mode === 'batch' ? 'Batch Email Composer' : 'Email Composer'}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{composerState.recipientLabel || composerState.to}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {composerState.channel === 'sms'
                  ? composerState.mode === 'batch'
                    ? 'Compose manually or generate a draft, then send via BulkSMS.'
                    : 'Compose manually or generate a draft from the current row, then send via BulkSMS.'
                  : composerState.mode === 'batch'
                    ? 'Compose manually or generate a draft using the selected rows.'
                    : 'Compose manually or generate a draft from the current row.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setComposerState(null)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close email composer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {composerState.channel === 'sms' ? 'Recipients' : 'To'}
                </label>
                <input
                  type="text"
                  value={composerState.to}
                  onChange={(e) => setComposerState((prev) => ({ ...prev, to: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                  readOnly={composerState.channel === 'sms'}
                />
              </div>
              {composerState.channel !== 'sms' && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</label>
                  <input
                    type="text"
                    value={composerState.subject}
                    onChange={(e) => setComposerState((prev) => ({ ...prev, subject: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                  />
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {composerState.channel === 'sms' ? 'SMS Message' : 'Message'}
                </label>
                <textarea
                  rows={12}
                  value={composerState.body}
                  onChange={(e) => setComposerState((prev) => ({ ...prev, body: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-[#008080]/20 bg-[#008080]/10/60 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#008080]">
                <Sparkles className="h-4 w-4" />
                AI Assist
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Add drafting instructions and generate a row-aware {composerState.channel === 'sms' ? 'SMS' : 'email'} draft.
              </p>
              <textarea
                rows={4}
                value={composerState.aiPrompt}
                onChange={(e) => setComposerState((prev) => ({ ...prev, aiPrompt: e.target.value }))}
                placeholder="Example: Invite them to confirm attendance for next Tuesday and keep the tone professional."
                className="mt-3 w-full rounded-xl border border-[#008080]/30 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
              />
              <button
                type="button"
                onClick={handleGenerateComposerDraft}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2 text-sm font-semibold text-white hover:bg-[#006d6d]"
              >
                <Sparkles className="h-4 w-4" />
                Generate draft
              </button>
              <button
                type="button"
                onClick={composerState.channel === 'sms' ? handleSendSmsComposer : handleSendEmailComposer}
                disabled={sendingComposer}
                className="mt-3 ml-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sendingComposer ? 'Sending...' : composerState.channel === 'sms' ? 'Send SMS' : 'Send Email'}
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {composerState.mode === 'batch' ? 'Selected Row Context' : 'Row Context'}
              </p>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                {composerState.mode === 'batch' ? (
                  selectedRows
                    .filter((row) => composerState.rowIds?.includes(row.id))
                    .slice(0, 5)
                    .map((row, index) => {
                      const preview = resolvedHeaders
                        .map((header) => {
                          const cellValue = getHeaderDisplayValueForRow(header, row.resolvedValues || row.values || {});
                          return cellValue ? `${header.label}: ${cellValue}` : null;
                        })
                        .filter(Boolean)
                        .slice(0, 4)
                        .join(' | ');

                      return (
                        <div key={row.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{getRowReferenceLabel(row, index + 1)}</p>
                          <p className="mt-1 text-sm text-slate-700">{preview || 'No row details available.'}</p>
                        </div>
                      );
                    })
                ) : (
                  resolvedHeaders.map((header) => {
                    const activeRow = rows.find((row) => row.id === composerState.rowId);
                    const resolvedRow = activeRow ? {
                      ...activeRow,
                      resolvedValues: resolveRowFormulas(resolvedAllHeaders, activeRow.values || {}, headerOptionRowsBySheetId)
                    } : null;
                    const cellValue = getHeaderDisplayValueForRow(header, resolvedRow?.resolvedValues || resolvedRow?.values || {});
                    if (!cellValue) return null;
                    return (
                      <div key={header.id} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 font-medium text-slate-500">{header.label}</span>
                        <span className="text-right text-slate-700">{cellValue}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </aside>
      )}

      {isProfileModalOpen && <Profile isModal onClose={() => setIsProfileModalOpen(false)} />}
      {isLoginModalOpen && <LoginModal onClose={() => setIsLoginModalOpen(false)} onSuccess={handleLoginSuccess} logo="sheets" />}

      {activeActionFormConfig && (
        <div className="fixed inset-0 z-50 flex bg-slate-950/35">
          <button
            type="button"
            aria-label="Close action form"
            onClick={handleCloseActionSidebar}
            className="hidden h-full flex-1 cursor-default lg:block"
          />
          <aside className="ml-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl">
            <div className="border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#008080]">
                    Sheet Action
                  </p>
                  <h2 className="truncate text-lg font-bold tracking-tight text-slate-900">
                    {activeActionFormConfig.name || activeActionFormConfig.buttonLabel || 'Action form'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Save a dedicated action entry without changing the current row detail sidebar.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseActionSidebar}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Close action form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {sheetActionForms.length > 1 && (
                <div className="mt-4 rounded-2xl bg-slate-100 p-1">
                  <div className="grid grid-cols-2 gap-1">
                    {sheetActionForms.map((actionForm) => (
                      <button
                        key={actionForm.id}
                        type="button"
                        onClick={() => setActiveActionFormId(actionForm.id)}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          activeActionFormId === actionForm.id
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {actionForm.buttonLabel || actionForm.name || 'Action'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-50 px-4 py-4 sm:px-6 sm:py-5">
              <div className="space-y-4">
                <SheetSidebarActionFormCard
                  type={activeActionFormConfig.type}
                  title={actionSheetForm?.title || activeActionFormConfig.name || activeActionFormConfig.buttonLabel || 'Action form'}
                  description={actionSheetForm?.description || activeActionFormConfig.description}
                  targetSheetTitle={actionFormSheet?.title || 'Linked sheet'}
                  fieldsCount={visibleActionFormUserHeaders.length}
                />

                {activeRowActionEntries.length > 0 && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Active Actions</p>
                        <h3 className="mt-1 text-sm font-semibold text-slate-900">
                          {activeRowActionEntries.length} linked action{activeRowActionEntries.length === 1 ? '' : 's'} for this item
                        </h3>
                      </div>
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                        {getActionFormNotificationLabel(activeActionFormConfig.type, activeRowActionEntries.length)}
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {activeRowActionEntries.map((entry) => (
                        <div key={`${entry.formId}_${entry.row.id}`} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                            {entry.sheetTitle || entry.formName}
                          </p>
                          <p className="mt-1 text-sm text-slate-700">
                            {formatDate(entry.row.updatedAt || entry.row.createdAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {!actionFormSheet && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                    Loading action form...
                  </div>
                )}

                {actionFormSheet && !visibleActionFormUserHeaders.length && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                    The linked action sheet does not have any user fields available for this form.
                  </div>
                )}

                {actionFormSheet && visibleActionFormUserHeaders.length > 0 && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    {usesCustomActionSheetForm ? (
                      <div className="grid gap-5 md:grid-cols-6">
                        {visibleActionFormLayoutItems.map((item) => {
                          if (item.type === 'sectionDivider') {
                            return (
                              <div key={item.id} className="md:col-span-6">
                                <div className="mb-3 inline-flex rounded-full bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm [white-space:break-spaces]">
                                  {item.label}
                                </div>
                                <div className="border-t border-slate-200" />
                              </div>
                            );
                          }

                          if (item.type === 'textBlock') {
                            return (
                              <div key={item.id} className="md:col-span-6">
                                <ActionFormRichTextBlock item={item} />
                              </div>
                            );
                          }

                          if (item.type === 'imageSlide') {
                            return (
                              <div key={item.id} className="md:col-span-6">
                                <ActionFormImageSlide item={item} />
                              </div>
                            );
                          }

                          const field = item.field;
                          return (
                            <div key={item.id} className={getActionSheetFormFieldWidthClass(item.width)}>
                              <div className="mb-2 flex items-center gap-2">
                                <label className="text-sm font-semibold text-slate-800">{field.label}</label>
                                {field.required && (
                                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-600">
                                    Required
                                  </span>
                                )}
                              </div>
                              {renderActionFormFieldInput(field)}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {visibleActionFormUserHeaders.map((header) => (
                          <div key={`action_form_field_${header.id}`}>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                              {header.label}
                            </label>
                            {renderActionFormFieldInput(header)}
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleCloseActionSidebar}
                  className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSubmitActionForm}
                  disabled={actionFormSaving || !actionFormSheet || !visibleActionFormUserHeaders.length}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <Save className="h-4 w-4" />
                  {actionFormSaving
                    ? 'Saving...'
                    : (activeActionFormConfig.submitLabel || `Save ${activeActionFormConfig.buttonLabel || activeActionFormConfig.name || 'action'}`)}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {(activeRow || sheetAiOpen) && !activeActionFormConfig && (
        <div className={`fixed inset-0 z-50 flex ${isEmbedded ? 'pointer-events-none bg-transparent' : 'bg-slate-950/35'}`}>
          <button
            type="button"
            aria-label="Close row details"
            onClick={handleCloseRowDetail}
            className={`hidden h-full flex-1 cursor-default lg:block ${isEmbedded ? 'pointer-events-auto' : ''}`}
          />
          <aside className={`ml-auto flex w-full flex-col overflow-hidden bg-white ${
            isEmbedded
              ? 'pointer-events-auto m-3 h-[calc(100%-1.5rem)] max-w-[430px] rounded-2xl border border-slate-200 shadow-[0_28px_80px_-28px_rgba(15,23,42,0.5)] lg:m-4 lg:h-[calc(100%-2rem)]'
              : 'h-full max-w-xl shadow-2xl'
          }`}>
            <div className={`border-b border-slate-200 bg-white ${isEmbedded ? 'px-5 py-3.5' : 'px-5 py-4 sm:px-6'}`}>
              <div className="flex items-start gap-3">
                <img
                  src={sheetLogo}
                    alt=""
                    aria-hidden="true"
                    className="h-10 w-auto shrink-0 object-contain sm:hidden"
                  />
                 <div className="grid min-w-0 flex-1 grid-cols-2 items-start gap-2">
                  <div className="min-w-0 items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#008080]">
                    {sheetAiOpen ? 'Sheet AI' : 'Row Detail'}
                  </p>
                          <h2 className="text-lg font-bold tracking-tight text-slate-900">
                            {sheetAiOpen ? sheet.title : activeRowLabel}
                          </h2>
                </div>
                  {!sheetAiOpen && canCreateRows && (
                    <button
                      type="button"
                      onClick={handleAddRow}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                      aria-label="Add row"
                      title="Add row"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  )}
                  </div>
                {!isAdminReadOnlyView && !sheetAiOpen && (
                <div className="hidden min-w-0 flex-1 lg:block">
                  <div className="rounded-2xl bg-slate-100 p-1">
                    <div className="grid grid-cols-4 gap-1">
                      <button
                        type="button"
                        onClick={() => setSidebarView('details')}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm transition ${
                          sidebarView === 'details'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <TableProperties className="h-4 w-4" />
                        Details
                      </button>
                      <button
                        type="button"
                        onClick={() => setSidebarView('logs')}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm transition ${
                          sidebarView === 'logs'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Save className="h-4 w-4" />
                        Logs
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSidebarView('ai');
                          setAiMode('report');
                        }}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm transition ${
                          sidebarView === 'ai'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Sparkles className="h-4 w-4" />
                        AI
                      </button>
                      <button
                        type="button"
                        onClick={() => setSidebarView('automations')}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm transition ${
                          sidebarView === 'automations'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Settings className="h-4 w-4" />
                        Auto
                      </button>
                    </div>
                  </div>
                </div>
                )}
                <button
                  type="button"
                  onClick={handleCloseRowDetail}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Close row details"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {!isAdminReadOnlyView && !sheetAiOpen && (
              <div className="mt-3 lg:hidden">
                <div className="grid grid-cols-4 gap-2 rounded-2xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSidebarView('details')}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-sm transition ${
                      sidebarView === 'details'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <TableProperties className="h-4 w-4" />
                    Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setSidebarView('logs')}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-sm transition ${
                      sidebarView === 'logs'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Save className="h-4 w-4" />
                    Logs
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSidebarView('ai');
                      setAiMode('report');
                    }}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-sm transition ${
                      sidebarView === 'ai'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Sparkles className="h-4 w-4" />
                    AI
                  </button>
                  <button
                    type="button"
                    onClick={() => setSidebarView('automations')}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-sm transition ${
                      sidebarView === 'automations'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Auto
                  </button>
                </div>
              </div>
              )}
            </div>

            <div className={`flex-1 overflow-y-auto ${isEmbedded ? 'bg-[#f7f9fc] px-4 py-4' : 'bg-slate-50 px-4 py-4 sm:px-6 sm:py-5'}`}>
              {sidebarView === 'details' && (
                <div className="space-y-3">
                  {!isAdminReadOnlyView && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                    <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => setRowDetailMode('view')}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                          rowDetailMode === 'view'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Eye className="h-4 w-4" />
                        Rendered view
                      </button>
                      <button
                        type="button"
                        onClick={() => setRowDetailMode('edit')}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                          rowDetailMode === 'edit'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit mode
                      </button>
                    </div>
                  </section>
                  )}
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-[#008080]/5 p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {rowDetailMode === 'edit' || !usesPresetRenderLayout ? (
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#008080]">
                            {rowDetailMode === 'edit' ? 'Editing Row' : 'Row Snapshot'}
                          </p>
                        ) : null}
                          <h3 className="truncate text-lg font-bold tracking-tight text-slate-900">
                            {activeRowLabel}
                          </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          {rowDetailMode === 'edit'
                            ? 'Adjust the source fields, then save to return to the rendered layout.'
                            : usesPresetRenderLayout
                              ? 'Rendered using the selected sheet preset. Switch to edit mode to return to the raw form fields.'
                              : 'A polished read view of this row while keeping the spreadsheet structure intact.'}
                        </p>
                      </div>
                      <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                        activeRow.isNew
                          ? 'bg-[#008080]/15 text-[#008080]'
                          : activeRow.isDirty
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {activeRow.isNew ? 'Draft' : activeRow.isDirty ? 'Edited' : 'Saved'}
                      </span>
                    </div>
                  </div>
                  {rowDetailMode !== 'edit' && usesCardPresetLayout ? (
                    <>
                      {usesProfilePresetLayout ? (
                        (() => {
                          const profilePresetData = getProfilePresetData(activeRow, activeRowLabel);
                          return (
                        <SheetProfilePresetCard
                          row={activeRow}
                          profileHeader={presetProfileHeader}
                          profile={getProfileDisplayData(presetProfileHeader, activeRow.resolvedValues || activeRow.values || {})}
                          cardLabel={activeRowLabel}
                          title={profilePresetData.title}
                          body={profilePresetData.body}
                                    primaryColor={sheetPrimaryColor}
                          secondaryColor={sheet?.secondaryColor || ''}
                          actionContent={profilePresetData.actionHeader ? renderCellView(activeRow, profilePresetData.actionHeader) : null}
                          actionNotice={getRowActiveActionSummary(activeRow.id)?.primaryLabel || ''}
                          onActionClick={() => handleRunConfiguredRowAction(activeRow.id, getRowActiveActionSummary(activeRow.id)?.entries?.[0]?.formId || '')}
                          actionItems={getRowActionMenuItems(activeRow.id)}
                          isActionMenuOpen={openRowActionMenuRowId === activeRow.id}
                          onToggleActionMenu={() => setOpenRowActionMenuRowId((current) => current === activeRow.id ? '' : activeRow.id)}
                          onSelectActionItem={(actionItem) => handleSelectRowActionItem(activeRow.id, actionItem)}
                            />
                          );
                        })()
                      ) : (
                        (() => {
                          const activeRowActionSummary = getRowActiveActionSummary(activeRow.id);
                          const activeRowPrimaryAction = getRowPrimaryActionConfig(activeRow.id);
                          return (
                        <SheetListCardPreset
                          row={activeRow}
                          {...getListCardPresetData(activeRow, activeRowLabel)}
                                    primaryColor={sheetPrimaryColor}
                          secondaryColor={sheet?.secondaryColor || ''}
                          actionLabel={activeRowPrimaryAction?.label || ''}
                          actionNotice={activeRowActionSummary?.primaryLabel || ''}
                          onActionClick={() => handleRunConfiguredRowAction(activeRow.id, activeRowPrimaryAction?.formId || '')}
                          actionItems={getRowActionMenuItems(activeRow.id)}
                          isActionMenuOpen={openRowActionMenuRowId === activeRow.id}
                          onToggleActionMenu={() => setOpenRowActionMenuRowId((current) => current === activeRow.id ? '' : activeRow.id)}
                          onSelectActionItem={(actionItem) => handleSelectRowActionItem(activeRow.id, actionItem)}
                        />
                          );
                        })()
                      )}
                      {(usesProfilePresetLayout ? profilePresetSidebarHeaders : listCardPresetSidebarHeaders).map((header) => (
                        <section
                          key={`${activeRow.id}_${header.id}`}
                          className="rounded-2xl border border-slate-200/80 bg-white/95 p-4 shadow-sm"
                        >
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                {getColumnLetter(Math.max(0, resolvedHeaders.findIndex((item) => item.id === header.id)))}
                              </p>
                              <h3 className="mt-1 text-sm font-semibold text-slate-900">{header.label}</h3>
                            </div>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              {header.type || 'text'}
                            </span>
                          </div>
                          {renderCellView(activeRow, header)}
                        </section>
                      ))}
                    </>
                  ) : (
                    visibleDetailHeaders.map((header) => (
                      <section
                        key={`${activeRow.id}_${header.id}`}
                        className={`rounded-2xl border p-4 shadow-sm ${
                          rowDetailMode === 'edit'
                            ? 'border-slate-200 bg-white'
                            : 'border-slate-200/80 bg-white/95'
                        }`}
                      >
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                              {getColumnLetter(Math.max(0, resolvedHeaders.findIndex((item) => item.id === header.id)))}
                            </p>
                            <h3 className="mt-1 text-sm font-semibold text-slate-900">{header.label}</h3>
                          </div>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            {header.type || 'text'}
                          </span>
                        </div>
                        {rowDetailMode === 'edit' ? renderCellInput(activeRow, header) : renderCellView(activeRow, header)}
                      </section>
                    ))
                  )}
                </div>
              )}

              {sidebarView === 'logs' && (
                <div className="space-y-4">
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Mode</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                          <button
                            type="button"
                            onClick={() => setLogMode('saved')}
                            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                              logMode === 'saved'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            Saved logs
                          </button>
                          <button
                            type="button"
                            onClick={() => setLogMode('form')}
                            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                              logMode === 'form'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            New log
                          </button>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Target</p>
                        <div className="mt-2 grid grid-cols-3 gap-2 rounded-2xl bg-slate-100 p-1">
                          {['sheet', 'column', 'row'].map((scope) => (
                            <button
                              key={scope}
                              type="button"
                              onClick={() => setLogScopeType(scope)}
                              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                                logScopeType === scope
                                  ? 'bg-white text-slate-900 shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              {getLogScopeLabel(scope)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {logMode === 'form' && logScopeType === 'column' && (
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Column
                          </label>
                          <select
                            value={selectedLogColumnId}
                            onChange={(e) => setSelectedLogColumnId(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                          >
                            {resolvedHeaders.map((header) => (
                              <option key={header.id} value={header.id}>
                                {header.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {customLogSheet?.id && !hasRequiredCustomLogSystemFields && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                          The linked log sheet is missing required system field mappings for `logScope`, `sourceSheetId`, `sourceRowId`, or `sourceColumnId`.
                        </div>
                      )}
                    </div>
                  </section>

                  {logMode === 'saved' ? (
                    displayedLogs.length ? (
                      <div className="space-y-3">
                        {displayedLogs.map((log) => {
                          const customSummaryItems = customLogSheet?.id ? getCustomLogSummaryItems(log) : [];
                          const scopeValue = customLogSheet?.id
                            ? log.values?.[customLogSystemFieldMap.logScope] || logScopeType
                            : log.scopeType;
                          const logDate = customLogSheet?.id
                            ? log.values?.[customLogSystemFieldMap.loggedAt] || log.createdAt
                            : log.date;
                          const targetValue = customLogSheet?.id
                            ? (
                                log.values?.[customLogSystemFieldMap.sourceColumnLabel]
                                || log.values?.[customLogSystemFieldMap.sourceRowLabel]
                                || sheet?.title
                              )
                            : log.targetLabel;

                          return (
                            <article key={log.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">
                                  {getLogScopeLabel(scopeValue)}
                                </span>
                                <span>{targetValue || 'Log entry'}</span>
                                <span>{formatDate(logDate) || 'No date'}</span>
                              </div>
                              {customLogSheet?.id ? (
                                <div className="mt-3 space-y-2 text-sm text-slate-700">
                                  {customSummaryItems.length ? customSummaryItems.map((item) => (
                                    <p key={`${log.id}_${item.label}`}>
                                      <span className="font-semibold text-slate-900">{item.label}:</span> {item.value}
                                    </p>
                                  )) : (
                                    <p>No custom log fields were filled.</p>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <p className="mt-3 text-sm text-slate-700">{log.note}</p>
                                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                    <span className={`rounded-full px-2.5 py-1 font-semibold ${
                                      log.status === 'G'
                                        ? 'bg-[#008080]/15 text-[#008080]'
                                        : log.status === 'O'
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-rose-100 text-rose-700'
                                    }`}>
                                      {log.status || 'O'}
                                    </span>
                                    <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                      {log.action || 'Call'}
                                    </span>
                                    <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                      {log.progress === '' || log.progress === null || log.progress === undefined ? 'No progress' : `${log.progress}%`}
                                    </span>
                                  </div>
                                </>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                        No saved logs for this target yet.
                      </div>
                    )
                  ) : (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="grid gap-4">
                        {customLogSheet?.id ? (
                          visibleCustomLogUserHeaders.length ? (
                            visibleCustomLogUserHeaders.map((header) => (
                              <div key={`custom_log_field_${header.id}`}>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                  {header.label}
                                </label>
                                {renderCustomLogFieldInput(header)}
                              </div>
                            ))
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                              The selected custom log sheet only has system fields. Add at least one user field to use it as a form.
                            </div>
                          )
                        ) : (
                          <>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                Date
                              </label>
                              <input
                                type="date"
                                value={logDraft.date}
                                onChange={(e) => setLogDraft((prev) => ({ ...prev, date: e.target.value }))}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                  Progress
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={logDraft.progress}
                                  onChange={(e) => setLogDraft((prev) => ({ ...prev, progress: e.target.value }))}
                                  placeholder="0-100"
                                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                  Action
                                </label>
                                <select
                                  value={logDraft.action}
                                  onChange={(e) => setLogDraft((prev) => ({ ...prev, action: e.target.value }))}
                                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                                >
                                  {['Call', 'Message', 'Email'].map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                Status
                              </label>
                              <div className="grid grid-cols-3 gap-2 rounded-2xl bg-slate-100 p-1">
                                {['R', 'O', 'G'].map((status) => (
                                  <button
                                    key={status}
                                    type="button"
                                    onClick={() => setLogDraft((prev) => ({ ...prev, status }))}
                                    className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                                      logDraft.status === status
                                        ? status === 'G'
                                          ? 'bg-[#008080]/15 text-[#008080]'
                                          : status === 'O'
                                            ? 'bg-amber-100 text-amber-700'
                                            : 'bg-rose-100 text-rose-700'
                                        : 'bg-white text-slate-500'
                                    }`}
                                  >
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                Note / Update
                              </label>
                              <textarea
                                rows={6}
                                value={logDraft.note}
                                onChange={(e) => setLogDraft((prev) => ({ ...prev, note: e.target.value }))}
                                placeholder="Add context, next steps, or an update."
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                              />
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setLogDraft(createInitialLogDraft());
                              setCustomLogDraft(createCustomLogDraft(customLogHeaders));
                              setLogMode('saved');
                            }}
                            className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveLog}
                            disabled={logSaving || Boolean(customLogSheet?.id && !hasRequiredCustomLogSystemFields)}
                            className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
                          >
                            <Save className="h-4 w-4" />
                            {logSaving ? 'Saving...' : 'Save log'}
                          </button>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              )}

              {sidebarView === 'ai' && (
                <div className="space-y-4">
                  <section className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                    <div className="grid grid-cols-1 gap-2 rounded-xl bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => setAiMode('report')}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          aiMode === 'report'
                            ? 'bg-[#008080] text-[#f6f5f5] shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        AI Report
                      </button>
                    </div>

                    {aiMode === 'chat' && (
                      <div className="mt-3">
                        <div className={`grid gap-2 rounded-xl bg-slate-100 p-1 ${sheetAiOpen ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          <button
                            type="button"
                            onClick={() => setChatMode('current')}
                            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                              chatMode === 'current'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            {sheetAiOpen ? 'Sheet conversation' : 'Current thread'}
                          </button>
                          {!sheetAiOpen && (
                            <button
                              type="button"
                              onClick={() => setChatMode('saved')}
                              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                                chatMode === 'saved'
                                  ? 'bg-white text-slate-900 shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              Saved chats
                            </button>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {chatMode === 'current' && currentThreadMessages.length > 0 && (
                            <>
                              {!sheetAiOpen && (
                                <button
                                  type="button"
                                  onClick={handleSaveCurrentThread}
                                  disabled={savingChatThread}
                                  className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                  <Save className="h-4 w-4" />
                                  {savingChatThread ? 'Saving...' : 'Save chat'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setCurrentThreadMessages([])}
                                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                              >
                                Clear
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </section>

                  {aiMode === 'chat' && chatMode === 'saved' && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Saved Threads</p>
                      <div className="mt-3 space-y-2">
                        {savedThreads.length ? savedThreads.map((thread) => (
                          <button
                            key={thread.id}
                            type="button"
                            onClick={() => setSelectedSavedThreadId(thread.id)}
                            className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                              selectedSavedThreadId === thread.id
                                ? 'border-[#008080]/10 bg-[#008080]/10'
                                : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                          >
                            <p className="text-sm font-semibold text-slate-900">{thread.title || 'Saved chat'}</p>
                            <p className="mt-1 text-xs text-slate-500">{thread.lastMessagePreview || 'No preview available.'}</p>
                          </button>
                        )) : (
                          <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                            No saved chats for this row yet.
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {aiMode === 'chat' && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            {chatMode === 'saved' ? 'Saved conversation' : 'Current conversation'}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            Ask about the entire accessible sheet. Mention rows only when you want the answer to focus on them.
                          </p>
                        </div>
                        {chatMode === 'saved' && selectedSavedThread && (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            {selectedSavedThread.rowLabel || activeRowLabel}
                          </span>
                        )}
                      </div>

                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Row mentions</p>
                            <p className="mt-1 text-xs text-slate-500">Optional focus; the full sheet remains available to the assistant.</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedRows.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setChatMentionedRowIds((prev) => Array.from(new Set([...prev, ...selectedRows.map((row) => row.id)])))}
                                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]"
                              >
                                Mention selected ({selectedRows.length})
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setShowChatRowMentions((prev) => !prev)}
                              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]"
                            >
                              @ Mention rows
                            </button>
                          </div>
                        </div>

                        {chatMentionedRows.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {chatMentionedRows.map((row) => (
                              <button
                                key={`chat_mention_${row.id}`}
                                type="button"
                                onClick={() => setChatMentionedRowIds((prev) => prev.filter((rowId) => rowId !== row.id))}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#008080]/10 px-3 py-1.5 text-xs font-semibold text-[#008080]"
                                title="Remove row mention"
                              >
                                @{getRowReferenceLabel(row, resolvedRows.findIndex((item) => item.id === row.id) + 1)}
                                <X className="h-3 w-3" />
                              </button>
                            ))}
                          </div>
                        )}

                        {showChatRowMentions && (
                          <div className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
                            {resolvedRows.filter((row) => !row.isNew).map((row, index) => {
                              const isMentioned = chatMentionedRowIds.includes(row.id);
                              return (
                                <label key={`chat_row_picker_${row.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={isMentioned}
                                    onChange={() => setChatMentionedRowIds((prev) => (
                                      isMentioned ? prev.filter((rowId) => rowId !== row.id) : [...prev, row.id]
                                    ))}
                                    className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                  />
                                  <span className="truncate">{getRowReferenceLabel(row, index + 1)}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 space-y-3">
                        {activeChatMessages.length ? activeChatMessages.map((message, index) => (
                          <div
                            key={message.id || `${message.role}_${index}`}
                            className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                          >
                            <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm ${
                              message.role === 'assistant'
                                ? 'bg-slate-100 text-slate-700'
                                : 'bg-[#008080]/20 text-slate-700'
                            }`}>
                              {message.role === 'assistant' ? (
                                <div
                                  className="markdown-body [&_a]:text-[#008080] [&_code]:text-[0.92em] [&_ol]:my-0 [&_p:last-child]:mb-0 [&_ul]:my-0"
                                  dangerouslySetInnerHTML={{ __html: markdownToHtml(message.content) }}
                                />
                              ) : (
                                <p>{message.content}</p>
                              )}
                            </div>
                          </div>
                        )) : (
                          <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                            {chatMode === 'saved'
                              ? 'Select a saved chat or switch to Current to start a new thread.'
                              : 'Start a conversation about trends, comparisons, gaps, or anything else in this sheet.'}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {aiMode === 'report' && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">AI Report</p>
                        <p className="mt-1 text-sm text-slate-500">
                          Generate a report for a row, selected rows, a specific column, or the whole visible sheet.
                        </p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        reportSkill.enabled
                          ? 'bg-[#008080]/10 text-[#0f4f4f]'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {reportSkill.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    {(reportSkill.title || reportSkill.objective) && (
                      <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        <p className="font-semibold text-slate-900">{reportSkill.title || 'Generated report'}</p>
                        {reportSkill.objective && (
                          <p className="mt-1">{reportSkill.objective}</p>
                        )}
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => setReportTargetType('active-row')}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          reportTargetType === 'active-row'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Active row
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportTargetType('selected-rows')}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          reportTargetType === 'selected-rows'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Selected rows
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportTargetType('column')}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          reportTargetType === 'column'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Column
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportTargetType('sheet')}
                        className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                          reportTargetType === 'sheet'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Whole sheet
                      </button>
                    </div>

                    {reportTargetType === 'column' && (
                      <div className="mt-4">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Column
                        </label>
                        <select
                          value={selectedReportColumnId}
                          onChange={(e) => setSelectedReportColumnId(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#008080]"
                        >
                          {resolvedHeaders.map((header) => (
                            <option key={`report_column_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      {reportTargetSummary}
                    </div>

                    {!reportSkill.enabled && (
                      <div className="mt-3 rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                        Enable the report AI skill in the sheet builder to generate reports here.
                      </div>
                    )}
                  </section>
                  )}

                  {aiMode === 'report' && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Generated Output</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {generatedReportTitle || 'No report generated yet.'}
                        </p>
                      </div>
                      {reportSkill.enabled && (
                        <button
                          type="button"
                          onClick={handleGenerateReport}
                          disabled={reportLoading}
                          className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Sparkles className={`h-4 w-4 ${reportLoading ? 'animate-pulse' : ''}`} />
                          {reportLoading ? 'Generating...' : 'Generate'}
                        </button>
                      )}
                    </div>

                    <div className="mt-4">
                      {generatedReport ? (
                        <div
                          className="markdown-body rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700 [&_a]:text-[#008080] [&_code]:text-[0.92em] [&_ol]:my-0 [&_p:last-child]:mb-0 [&_ul]:my-0"
                          dangerouslySetInnerHTML={{ __html: markdownToHtml(generatedReport) }}
                        />
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                          Generate a report to summarize this row, recent activity, and suggested next actions.
                        </div>
                      )}
                    </div>
                    </section>
                  )}
                </div>
              )}

              {sidebarView === 'automations' && (
                <div className="space-y-4">
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Configured Automations</p>
                    <p className="mt-1 text-sm text-slate-500">
                      Manual execution works now for report and email digest automations. Scheduled execution is the next step.
                    </p>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="space-y-3">
                      {sheetAutomations.length ? sheetAutomations.map((automation) => (
                        <article key={automation.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{automation.name}</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {automation.type === 'email_digest' ? 'Email digest' : 'Report'} â€¢ {automation.triggerType === 'schedule'
                                  ? `${automation.scheduleCadence}${automation.scheduleCadence === 'weekly' ? ` â€¢ ${automation.scheduleDay}` : ''} â€¢ ${automation.scheduleTime} â€¢ ${automation.timezone}`
                                  : 'Manual'}
                              </p>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                              automation.enabled
                                ? 'bg-[#008080]/10 text-[#0f4f4f]'
                                : 'bg-slate-200 text-slate-600'
                            }`}>
                              {automation.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          {automation.instructions && (
                            <p className="mt-3 text-sm text-slate-600">{automation.instructions}</p>
                          )}
                          {automation.type === 'email_digest' && automation.recipients.length > 0 && (
                            <p className="mt-2 text-xs text-slate-500">
                              Recipients: {automation.recipients.join(', ')}
                            </p>
                          )}
                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handleRunAutomation(automation)}
                              disabled={!automation.enabled || automationRunningId === automation.id}
                              className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {automationRunningId === automation.id ? <Sparkles className="h-4 w-4 animate-pulse" /> : <Play className="h-4 w-4" />}
                              {automation.type === 'email_digest'
                                ? (automationRunningId === automation.id ? 'Sending...' : 'Run and send')
                                : (automationRunningId === automation.id ? 'Running...' : 'Run now')}
                            </button>
                            {automation.type === 'report' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setReportInput(automation.instructions || automation.name);
                                  setSidebarView('ai');
                                  setAiMode('report');
                                }}
                                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                              >
                                <FileText className="h-4 w-4" />
                                Open in report tab
                              </button>
                            )}
                          </div>
                        </article>
                      )) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                          No automations configured for this sheet yet.
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              )}
            </div>

            <div className={`border-t border-slate-200 bg-white px-4 py-4 sm:px-6 ${sidebarView === 'ai' ? 'sticky bottom-0' : ''}`}>
              {sidebarView === 'details' && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleDuplicateRow(activeRow)}
                      disabled={!canCreateRows}
                      title="Duplicate row"
                      aria-label="Duplicate row"
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Copy className="h-4 w-4" />
                      <span>Duplicate</span>
                    </button>
                    {rowDetailMode === 'edit' && (
                      <button
                        type="button"
                        onClick={handleSaveAndReturnToView}
                        disabled={saving}
                        title={saving ? 'Saving changes' : 'Save changes'}
                        aria-label={saving ? 'Saving changes' : 'Save changes'}
                        className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#008080] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <Save className="h-4 w-4" />
                        <span>{saving ? 'Saving...' : 'Save'}</span>
                      </button>
                    )}
                    {(activeRow.isNew || canEditSavedRow(activeRow)) && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(activeRow)}
                        title={activeRow.isNew ? 'Remove draft' : 'Delete row'}
                        aria-label={activeRow.isNew ? 'Remove draft' : 'Delete row'}
                        className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-100"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>{activeRow.isNew ? 'Remove' : 'Delete'}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {sidebarView === 'logs' && (
                <p className="text-sm text-slate-500">
                  Save dated notes against the sheet, a column, or this row so updates stay attached to the data.
                </p>
              )}

              {sidebarView === 'ai' && aiMode === 'chat' && (
                <div>
                  <div className="relative">
                    <textarea
                      rows={3}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Ask about the whole sheet, or @ mention rows above to focus the answer."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 pr-14 text-sm text-slate-900 outline-none focus:border-[#008080]"
                    />
                    <button
                      type="button"
                      onClick={handleSendSidebarChat}
                      disabled={chatLoading || !chatInput.trim() || (chatMode === 'saved' && !selectedSavedThread)}
                      className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#008080] text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={chatLoading ? 'Thinking' : 'Send message'}
                    >
                      {chatLoading ? <Sparkles className="h-4 w-4 animate-pulse" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {sidebarView === 'ai' && aiMode === 'report' && (
                <div>
                  <div className="relative">
                    <textarea
                      rows={3}
                      value={reportInput}
                      onChange={(e) => setReportInput(e.target.value)}
                      placeholder="Optional: ask for risks, blockers, executive summary, handover notes, or next actions."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 pr-14 text-sm text-slate-900 outline-none focus:border-[#008080]"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateReport}
                      disabled={reportLoading || !reportSkill.enabled}
                      className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#008080] text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={reportLoading ? 'Generating report' : 'Generate report'}
                    >
                      {reportLoading ? <Sparkles className="h-4 w-4 animate-pulse" /> : <FileText className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {activeImageUrl && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 px-4"
          onClick={() => setActiveImageUrl('')}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-4xl rounded-lg p-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveImageUrl('')}
              className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close image"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex max-h-[calc(90vh-1.5rem)] items-center justify-center overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={activeImageUrl}
                alt="Expanded sheet"
                className="max-h-[calc(90vh-2rem)] w-auto max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {activeYoutubeEmbedUrl && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 px-4"
          onClick={() => setActiveYoutubeEmbedUrl('')}
        >
          <div
            className="relative w-full max-w-4xl rounded-3xl bg-white p-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveYoutubeEmbedUrl('')}
              className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close video"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="relative w-full pt-[56.25%]">
                <iframe
                  src={activeYoutubeEmbedUrl}
                  title="YouTube video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheetViewer;
